import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useSearchParams, Link } from "react-router-dom";
import {
  Wrench, Package, FileText, CheckCircle2, AlertTriangle,
  Plus, Search, X, Loader2, Building2, ClipboardList, Receipt,
  BadgeCheck, Clock, DollarSign, TrendingUp, Banknote,
  ChevronDown, ChevronRight, Eye, BarChart2, Filter,
  Check, Calendar, AlertCircle, MessageSquare, Home, RotateCcw,
  Download, History, FileCheck, Pencil, ShoppingCart, Award, Brain, Upload, Trash2, Cloud, Link2, ShieldCheck
} from "lucide-react";
import { parseDateOnly } from "../utils/format";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { API, useAuth } from "../contexts/AuthContext";
import { formatPYG, formatPYGCompact, formatDate, todayAsuncion } from "../utils/format";
import { Skeleton } from "../components/ui/skeleton";
import { AutocompleteInput } from "../components/AutocompleteInput";
import ListasTab from "./ListasTab";
import { toast } from "sonner";
import { roleAllowed } from "../utils/roles";
import { SelectItemsForPdfModal, AprobacionParcial, mensajeAprobacion } from "./compras/SharedComponents";
import { useAprobadorPedidos, RangosAprobacionTab, AprobacionAP, ChipAprobacionAP } from "./compras/RangosAprobacionTab";   // Fix EX · Fix HB
import { ImportPMItemsModal } from "./compras/ImportPMItemsModal";
import { filtrarPresupuestos } from "./compras/FiltroClienteObraPresupuesto";
import { useFiltrosServicios, BarraFiltrosServicios } from "./compras/FiltrosServicios";   // Fix FV (2026-10-13)
import { sortPendingFirst } from "./compras/BusquedaFilters";   // Fix FV
import SelectBuscable from "../components/SelectBuscable";   // Fix FU (2026-10-12)
import { budgetItemLabel, SelectItemPto, VariosItemsPanel, expandirFilas } from "./compras/PedidosMaterialesTab";
import { guardarPDF } from "./compras/helpers";
import { ServiciosFacturasTab, crtInvoices, crtCoverage } from "./compras/ServiciosFacturasTab";   // Fix FL · Fix FV

// ==================== CONSTANTS ====================

const TABS = [
  { id: "listas",       label: "Listas",           icon: ClipboardList },
  { id: "pedidos",      label: "Pedidos de Serv.", icon: ClipboardList },
  { id: "aprobaciones", label: "Aprobaciones",     icon: CheckCircle2  },
  { id: "ped_prov",     label: "Ped. Proveedores", icon: Package       },
  { id: "analisis",     label: "Análisis Ppto.",   icon: BarChart2     },
  { id: "ptos_finales", label: "Ptos. Finales",    icon: FileCheck     },
  { id: "ordenes",      label: "Órd. de Servicio", icon: ShoppingCart  },
  { id: "certificacion",label: "Certificación",    icon: Award         },
  { id: "facturas",     label: "Facturas",         icon: Receipt       },   // Fix FL
  { id: "proveedores",  label: "Proveedores",      icon: Building2     },
  { id: "rangos",       label: "Rango p/ Aprobaciones", icon: ShieldCheck },   // Fix HB (2026-10-20)
];

// BLOQUE 5 PARTE A — cinturón extra contra el off-by-one de la IA (espejo de AnalisisPresupuestoTab.jsx)
const _normName = (s) => (s || "").toString().trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const _namesMatch = (a, b) => {
  const na = _normName(a), nb = _normName(b);
  if (!na || !nb) return false;
  return na === nb || na.includes(nb) || nb.includes(na);
};
const _resolveAiTargetIdx = (extracted, items) => {
  const idx = extracted.item_index;
  if (typeof idx !== "number" || idx < 0 || idx >= items.length) return -1;
  if (_namesMatch(extracted.item_name, items[idx]?.item_name)) return idx;
  const found = items.findIndex(it => _namesMatch(extracted.item_name, it.item_name));
  return found >= 0 ? found : idx;
};

// BLOQUE 60 — días efectivos de certificación (espejo exacto del cálculo
// backend en routers/alquileres.py::dias_efectivos_certificacion, usado para
// prefill/preview en vivo en la ventana de certificación). Convención
// UNIFICADA e INCLUSIVA en ambos extremos (idéntica a la de Inventario) — el
// día de retiro Y el día de devolución cuentan como días de uso. "Sábado" y
// "Domingo" son casillas independientes entre sí.
const _countWeekdayBetweenInclusive = (fi, ffr, jsWeekday) => {
  if (!fi || !ffr || ffr < fi) return 0;
  let cnt = 0;
  const d = new Date(fi.getTime());
  while (d <= ffr) {
    if (d.getDay() === jsWeekday) cnt++;
    d.setDate(d.getDate() + 1);
  }
  return cnt;
};
const diasEfectivosClient = (fiStr, ffrStr, sabadoIncluido = true, domingoIncluido = true) => {
  const fi = parseDateOnly(fiStr), ffr = parseDateOnly(ffrStr);
  if (!fi || !ffr || ffr < fi) return 0;
  let total = Math.round((ffr - fi) / 86400000) + 1;
  if (!sabadoIncluido) total -= _countWeekdayBetweenInclusive(fi, ffr, 6); // 6 = sábado
  if (!domingoIncluido) total -= _countWeekdayBetweenInclusive(fi, ffr, 0); // 0 = domingo
  return Math.max(0, total);
};
// BLOQUE 60 — propone Desde–Hasta por defecto: el día siguiente al último
// período ya certificado (o el inicio del alquiler si es la primera vez),
// hasta hoy (o la fecha real de fin si ya finalizó).
const proponerPeriodo = (alq) => {
  const periodos = (alq?.certified_periods || []).slice().sort((a, b) => (a.to > b.to ? 1 : -1));
  const ultimo = periodos.length ? periodos[periodos.length - 1] : null;
  let from;
  if (ultimo) {
    const d = parseDateOnly(ultimo.to);
    d.setDate(d.getDate() + 1);
    from = d.toISOString().slice(0, 10);
  } else {
    from = alq?.fecha_inicio || "";
  }
  const finalizado = alq?.estado_base === "finalizado";
  let to = finalizado ? (alq?.fecha_fin_real || "") : todayAsuncion();
  // Fix FX (2026-10-14): si ya está certificado hasta la fecha real de fin, propone el primer día adicional.
  if (finalizado && from && to && from > to) to = from;
  return { from, to };
};

const STATUS_COLORS = {
  pendiente:            "bg-amber-100 text-amber-700 border-amber-200",
  aprobada:             "bg-emerald-100 text-emerald-700 border-emerald-200",
  rechazada:            "bg-red-100 text-red-700 border-red-200",
  modificar:            "bg-orange-100 text-orange-700 border-orange-200",
  pedido_proveedor:     "bg-blue-100 text-blue-700 border-blue-200",
  analisis_presupuesto: "bg-purple-100 text-purple-700 border-purple-200",
  presupuesto_aprobado: "bg-cyan-100 text-cyan-700 border-cyan-200",
  en_analisis:          "bg-violet-100 text-violet-700 border-violet-200",
  abierta:              "bg-amber-100 text-amber-700 border-amber-200",
  cerrada:              "bg-emerald-100 text-emerald-700 border-emerald-200",
  borrador:             "bg-gray-100 text-gray-600 border-gray-200",
  ots_generada:         "bg-[#2E6DA4]/10 text-[#2E6DA4] border-[#2E6DA4]/20",
  pendiente_aprobacion: "bg-orange-100 text-orange-700 border-orange-200",
  cancelada:            "bg-gray-100 text-gray-500 border-gray-200",
  certificada:          "bg-teal-100 text-teal-700 border-teal-200",
  facturada:            "bg-orange-100 text-orange-700 border-orange-200",
};

const STATUS_LABELS = {
  pendiente:            "Pendiente",
  aprobada:             "Aprobado",
  rechazada:            "Rechazado",
  modificar:            "A Modificar",
  pedido_proveedor:     "Ped. Proveedor",
  presupuesto_aprobado: "Ppto. Aprobado",
  en_analisis:          "En Análisis",
  abierta:              "Abierta",
  cerrada:              "Cerrada",
  borrador:             "Borrador",
  ots_generada:         "OTS Generada",
  pendiente_aprobacion: "Pend. Aprobación",
  cancelada:            "Cancelada",
  certificada:          "Certificada",
  facturada:            "Facturada",
};

const URGENCY_COLORS = {
  normal:  "bg-gray-100 text-gray-600",
  alta:    "bg-amber-100 text-amber-700",
  critica: "bg-red-100 text-red-700",
};

const SEAV_DESCRIPTIONS = [
  "Montaje de artefactos de iluminación",
  "Montaje de mecanismos de encendido",
  "Montaje de tableros eléctricos",
  "Tendido y conexionado de cables",
  "Instalación de bandejas portacables",
  "Colocación de electroductos y cajas",
  "Instalación de puesta a tierra",
  "Instalación de alimentación de circuitos",
  "Instalación de transformador / generador",
  "Instalación de protecciones eléctricas",
  "Trabajos de media tensión",
  "Instalación de sistemas de señales débiles",
  "Mantenimiento eléctrico preventivo",
  "Mantenimiento eléctrico correctivo",
  "Instalación de soportería",
  "Instalación de equipos de medición",
  "Colocación de artefactos y equipos",
  "Limpieza y terminaciones eléctricas",
];

const SEAV_FRONTS = [
  "1. Media tensión",
  "2. Transformador / Generador",
  "3. Alimentación de tableros",
  "4. Tableros metálicos",
  "4. Tableros plásticos",
  "4. Protecciones",
  "4. Accesorios tableros",
  "5. Alimentación de circuitos",
  "5. Cajas y electroductos",
  "5. Soportería cajas y electroductos",
  "6. Bandeja portacable",
  "6. Soportería bandejas",
  "7. Artefactos de iluminación",
  "8. Mecanismos de encendido",
  "9. Sistema de puesta a tierra",
  "10. Señales débiles",
  "11. Herramientas y equipos de medición",
  "12. Equipamiento de seguridad",
  "13. Otros",
];

const CONDICION_PAGO_OPTS = [
  { value: "contado", label: "Contado" },
  { value: "credito_certificacion", label: "Crédito sobre Certificación" },
  { value: "30_dias", label: "30 días" },
  { value: "60_dias", label: "60 días" },
  { value: "30_60_dias", label: "30/60 días" },
  { value: "a_convenir", label: "A convenir" },
];

// ==================== HELPER COMPONENTS ====================

function StatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || "bg-gray-100 text-gray-500 border-gray-200";
  return (
    <span className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-full font-bold border ${cls}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function Modal({ open, onClose, title, children, wide, extraWide }) {
  if (!open) return null;
  const w = extraWide ? "max-w-4xl" : wide ? "max-w-2xl" : "max-w-lg";
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${w} max-h-[90vh] flex flex-col`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>,
    document.body
  );
}

function FlowStep({ steps, currentStatus }) {
  const idx = steps.findIndex(s => s.key === currentStatus);
  return (
    <div className="flex items-center gap-0.5 overflow-x-auto">
      {steps.map((step, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <div key={step.key} className="flex items-center">
            <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold whitespace-nowrap transition-all ${active ? "text-white" : done ? "text-emerald-700 bg-emerald-50" : "text-gray-400 bg-gray-50"}`}
              style={active ? { backgroundColor: step.color } : {}}>
              {done && <Check size={8} />}
              {step.short}
            </div>
            {i < steps.length - 1 && <div className={`w-3 h-px ${i < idx ? "bg-emerald-300" : "bg-gray-200"}`} />}
          </div>
        );
      })}
    </div>
  );
}

const SVC_FLOW_STEPS = [
  { key: "pendiente",            label: "Pendiente",      short: "PEND",    color: "#F59E0B" },
  { key: "aprobada",             label: "Aprobado",       short: "APROB",   color: "#10B981" },
  { key: "pedido_proveedor",     label: "Ped. Proveedor", short: "PPS",     color: "#3B82F6" },
  { key: "presupuesto_aprobado", label: "Ppto. Final",    short: "PFS",     color: "#06B6D4" },
  { key: "ots_generada",         label: "OTS",            short: "OTS",     color: "#2E6DA4" },
  { key: "certificada",          label: "Certificado",    short: "CRT",     color: "#14B8A6" },
  { key: "facturada",            label: "Facturado",      short: "FACT",    color: "#F97316" },
];

// ==================== PDF GENERATORS ====================

function generatePSPDF(ps) {
  const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();
  const CEN_BLUE = [46, 109, 164];
  doc.setFillColor(...CEN_BLUE);
  doc.rect(0, 0, pw, 22, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13); doc.setFont(undefined, "bold");
  doc.text("CEN INGENIERIA E.A.S.", 14, 10);
  doc.setFontSize(8); doc.setFont(undefined, "normal");
  doc.text("Pedido de Servicio", pw - 14, 10, { align: "right" });
  doc.text(ps.number || "", pw - 14, 15, { align: "right" });
  doc.setTextColor(40, 40, 40);
  let y = 30;
  doc.setFontSize(9);
  const info = [
    ["Número PS", ps.number || ""], ["Proyecto", ps.project_name || ""],
    ["Categoría", ps.categoria === "alquiler" ? "Alquiler" : "Servicio General"],
    ["Urgencia", (ps.urgency || "").toUpperCase()], ["Solicitante", ps.requested_by_name || ""],
    ["F. Inicio", ps.start_date || "—"], ["F. Final", ps.end_date || "—"],
    ["Estado", STATUS_LABELS[ps.status] || ps.status],
  ];
  if (ps.categoria === "alquiler") {
    info.push(["F. Límite Alquiler", ps.alquiler_fecha_limite || "—"]);
  }
  info.forEach(([k, v], i) => {
    const col = i % 2 === 0 ? 14 : 110;
    if (i % 2 === 0 && i > 0) y += 6;
    doc.setFont(undefined, "bold"); doc.text(k + ":", col, y);
    doc.setFont(undefined, "normal"); doc.text(String(v), col + 32, y);
  });
  y += 10;
  doc.setFont(undefined, "bold"); doc.setFontSize(9);
  doc.text("ÍTEMS SOLICITADOS", 14, y); y += 4;
  autoTable(doc, {
    startY: y,
    head: [["#", "Descripción del Servicio", "UM", "Cantidad", "Sector", "Frente"]],
    body: (ps.items || []).map((item, i) => [i + 1, item.description || item.item_name || "", item.unit || "", item.quantity || 1, item.sector || "—", item.front || "—"]),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: CEN_BLUE, textColor: 255, fontStyle: "bold" },
    columnStyles: { 0: { cellWidth: 8 }, 2: { cellWidth: 14 }, 3: { cellWidth: 18 }, 4: { cellWidth: 22 }, 5: { cellWidth: 22 } },
    alternateRowStyles: { fillColor: [248, 249, 250] },
  });
  if (ps.history && ps.history.length) {
    y = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(9); doc.setFont(undefined, "bold"); doc.text("HISTORIAL", 14, y); y += 4;
    autoTable(doc, {
      startY: y,
      head: [["Acción", "Estado", "Por", "Fecha", "Comentario"]],
      body: ps.history.map(h => [h.action || "", STATUS_LABELS[h.status] || h.status, h.by || "", formatDate(h.at), h.comment || ""]),
      styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: [80, 80, 80], textColor: 255 },
    });
  }
  guardarPDF(doc, `${ps.number || "PS"}.pdf`);
}

function generatePPSPDF(pps, selectedIndexes) {
  const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();
  const CEN_BLUE = [46, 109, 164];
  doc.setFillColor(...CEN_BLUE);
  doc.rect(0, 0, pw, 22, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13); doc.setFont(undefined, "bold");
  doc.text("CEN INGENIERIA E.A.S.", 14, 10);
  doc.setFontSize(8); doc.setFont(undefined, "normal");
  doc.text("Pedido de Presupuesto de Servicio", pw - 14, 10, { align: "right" });
  doc.text(pps.number || "", pw - 14, 15, { align: "right" });
  doc.setTextColor(40, 40, 40);
  let y = 30;
  doc.setFontSize(9);
  [["Número PPS", pps.number || ""], ["Proyecto", pps.project_name || ""], ["PS Origen", pps.ps_number || ""], ["Fecha", formatDate(pps.created_at)]].forEach(([k, v], i) => {
    const col = i % 2 === 0 ? 14 : 110;
    if (i % 2 === 0 && i > 0) y += 6;
    doc.setFont(undefined, "bold"); doc.text(k + ":", col, y);
    doc.setFont(undefined, "normal"); doc.text(String(v), col + 32, y);
  });
  y += 10;
  doc.setFont(undefined, "bold"); doc.setFontSize(9);
  doc.text("ÍTEMS SOLICITADOS", 14, y); y += 4;
  // BLOQUE 18 PARTE A — filtro de exportación (mismo patrón que el PDF del PP)
  const allItems = pps.items || [];
  const exportItems = Array.isArray(selectedIndexes) ? selectedIndexes.map(i => allItems[i]).filter(Boolean) : allItems;
  autoTable(doc, {
    startY: y,
    head: [["#", "Descripción del Servicio", "UM", "Cantidad", "Sector", "Frente"]],
    body: exportItems.map((item, i) => [i + 1, item.description || item.item_name || "", item.unit || "", item.quantity || 1, item.sector || "—", item.front || "—"]),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: CEN_BLUE, textColor: 255, fontStyle: "bold" },
    columnStyles: { 0: { cellWidth: 8 }, 2: { cellWidth: 14 }, 3: { cellWidth: 18 }, 4: { cellWidth: 22 }, 5: { cellWidth: 22 } },
    alternateRowStyles: { fillColor: [248, 249, 250] },
  });
  guardarPDF(doc, `${pps.number || "PPS"}.pdf`);
}

function generateOTSPDF(ots) {
  const doc = new jsPDF({ orientation: "l", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();
  const CEN_BLUE = [46, 109, 164];
  doc.setFillColor(...CEN_BLUE); doc.rect(0, 0, pw, 22, "F");
  doc.setTextColor(255, 255, 255); doc.setFontSize(14); doc.setFont(undefined, "bold");
  doc.text("CEN INGENIERIA E.A.S.", 14, 10);
  doc.setFontSize(9); doc.setFont(undefined, "normal");
  doc.text("Orden de Trabajo de Servicio (OTS)", pw - 14, 9, { align: "right" });
  doc.text(ots.number || "", pw - 14, 15, { align: "right" });
  doc.setTextColor(40, 40, 40);
  let y = 30;
  doc.setFontSize(9);
  const info = [["Proyecto", ots.project_name || ""], ["Proveedor / Contratista", ots.provider_name || ""], ["Condición de Pago", ots.condicion_pago || "contado"], ["Fecha", formatDate(ots.created_at)]];
  info.forEach(([k, v], i) => {
    const col = i % 2 === 0 ? 14 : 155;
    if (i % 2 === 0 && i > 0) y += 6;
    doc.setFont(undefined, "bold"); doc.text(k + ":", col, y);
    doc.setFont(undefined, "normal"); doc.text(String(v), col + 42, y);
  });
  y += 10;
  autoTable(doc, {
    startY: y,
    head: [["#", "Descripción del Servicio", "UM", "Cantidad", "P.U. c/IVA (Gs)", "Subtotal c/IVA (Gs)", "Sector", "Frente"]],
    body: agruparServicios(ots.items).map((item, i) => [   // Fix FQ (2026-10-12)
      i + 1, item.item_name || "", item.unit || "un", item.quantity || 1,
      formatPYG(item.unit_price || 0), formatPYG(item.subtotal || 0),
      item.sector || "—", item.front || "—"
    ]),
    styles: { fontSize: 8, cellPadding: 2.5 },
    headStyles: { fillColor: CEN_BLUE, textColor: 255, fontStyle: "bold" },
    columnStyles: { 0: { cellWidth: 8 }, 4: { halign: "right" }, 5: { halign: "right", fontStyle: "bold" } },
    alternateRowStyles: { fillColor: [248, 249, 250] },
  });
  const finalY = doc.lastAutoTable.finalY + 6;
  // Fix FK (2026-10-09): en Compras los precios van con IVA incluido, sin separar el IVA.
  doc.setFont(undefined, "bold"); doc.setFontSize(11);
  doc.text(`TOTAL (IVA incluido): ${formatPYG(ots.total || 0)}`, pw - 14, finalY + 2, { align: "right" });
  guardarPDF(doc, `${ots.number || "OTS"}.pdf`);
}

function generateCRTPDF(crt) {
  const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();
  const CEN_TEAL = [20, 184, 166];
  doc.setFillColor(...CEN_TEAL); doc.rect(0, 0, pw, 22, "F");
  doc.setTextColor(255, 255, 255); doc.setFontSize(13); doc.setFont(undefined, "bold");
  doc.text("CEN INGENIERIA E.A.S.", 14, 10);
  doc.setFontSize(8); doc.setFont(undefined, "normal");
  doc.text("Certificación de Servicio", pw - 14, 10, { align: "right" });
  doc.text(crt.number || "", pw - 14, 15, { align: "right" });
  doc.setTextColor(40, 40, 40);
  let y = 30;
  doc.setFontSize(9);
  [["N° Certificación", crt.number || ""], ["OTS Referencia", crt.ots_number || ""], ["Proyecto", crt.project_name || ""], ["Proveedor", crt.provider_name || ""]].forEach(([k, v], i) => {
    const col = i % 2 === 0 ? 14 : 110;
    if (i % 2 === 0 && i > 0) y += 6;
    doc.setFont(undefined, "bold"); doc.text(k + ":", col, y);
    doc.setFont(undefined, "normal"); doc.text(String(v), col + 40, y);
  });
  y += 12;
  // Fix FK (2026-10-09): monto certificado con IVA incluido, sin separar el IVA.
  doc.setFont(undefined, "bold"); doc.setFontSize(11);
  doc.text("TOTAL CERTIFICADO (IVA incluido):", 14, y); doc.text(formatPYG(crt.total || 0), pw - 14, y, { align: "right" });
  y += 7;
  // Fix FQ (2026-10-12): anticipo de alquiler y descuento de anticipos
  // Fix FV (2026-10-13): el anticipo también existe en servicios.
  if (crt.anticipo) { doc.setFont(undefined, "normal"); doc.setFontSize(9); doc.text(crt.categoria === "alquiler" ? "Anticipo de alquiler (se descuenta de las próximas certificaciones por días)" : "Anticipo (se descuenta de la certificación del servicio)", 14, y); y += 5; }
  if (crt.anticipo_descontado > 0) { doc.setFont(undefined, "normal"); doc.setFontSize(9); doc.text(`${crt.categoria === "alquiler" ? "Total del período" : "Total certificado"}: ${formatPYG(crt.monto_periodo || 0)} · Anticipo descontado: ${formatPYG(crt.anticipo_descontado)}`, 14, y); y += 5; }
  if (crt.invoice_number) { doc.setFont(undefined, "normal"); doc.setFontSize(9); y += 4; doc.text(`Factura N°: ${crt.invoice_number}`, 14, y); if (crt.invoice_total) doc.text(`Total Factura: ${formatPYG(crt.invoice_total)}`, 110, y); }
  guardarPDF(doc, `${crt.number || "CRT"}.pdf`);
}

// Fix FK (2026-10-09): monto certificado de la OTS con IVA incluido. Las OTS certificadas
// antes guardaban en certified_amount el monto sin IVA.
const montoCertificadoOTS = (ots) => ots.certified_total || (ots.certified_amount ? ots.certified_amount + Math.round(ots.certified_amount * 0.10) : 0);

// ==================== TAB: PEDIDOS DE SERVICIO ====================

// Fix CI (2026-09-11): "que en Unidad de medida se detalle, si es por día, por mes, o por el
// periodo definido con el proveedor" (Sergio, 11/09). En un alquiler la UM es la unidad de
// tiempo del precio: por día y por mes se multiplican por los días / meses; por período es
// un precio cerrado (tiempo = 1).
const UM_TIEMPO = [["día", "Por día"], ["mes", "Por mes"], ["período", "Por período"]];
const esPorPeriodo = (u) => _normName(u) === "periodo";
const tiempoUnidad = (u) => (_normName(u) === "mes" ? "meses" : esPorPeriodo(u) ? "período" : "días");
const tiempoCertificable = (unit, desde, hasta, sabado, domingo) => {
  if (esPorPeriodo(unit)) return 1;
  if (_normName(unit) === "mes") return Math.round((diasEfectivosClient(desde, hasta, true, true) / 30) * 100) / 100;
  return diasEfectivosClient(desde, hasta, sabado, domingo);
};
// Fix CI (2026-09-11): imputación de un ítem de servicio (línea, nivel y tarea del presupuesto).
// Fix FH (2026-10-08): "al momento de pedir un servicio/alquiler debe ser así también como en
// materiales" (CEN): categoría por ítem. "Servicio (del Pto.)" se imputa a un ítem del
// presupuesto; las demás no lo exigen (como Material Menor o las (Adm) en materiales).
const PS_CATEGORIAS = [
  { value: "listado", label: "Servicio (del Pto.)" },
  { value: "imprevistos", label: "Imprevisto" },
  { value: "equipamientos", label: "Equipamiento (Adm)" },
  { value: "herramientas", label: "Herramienta (Adm)" },
  { value: "epis", label: "EPI (Adm)" },
  { value: "alquileres", label: "Alquiler (Adm)" },
];
const nuevoItemPS = () => ({ categoria: "listado", description: "", unit: "", quantity: 1, sector: "", front: "" });
const categoriaPS = (it) => (it.categoria && it.categoria !== "listado" ? (PS_CATEGORIAS.find(c => c.value === it.categoria) || {}).label || it.categoria : "");
// Fix FQ (2026-10-12): la línea que junta varios ítems del presupuesto lo dice (como en materiales).
const imputacionTexto = (it) => [categoriaPS(it), (it.lineas_ps || []).length > 1 ? `Total de ${it.lineas_ps.length} ítems del presupuesto` : it.item_budget_label, it.nivel_label, it.tarea_numero && `Tarea ${it.tarea_numero}`].filter(Boolean).join(" · ");

// Fix FQ (2026-10-12): "En servicios aún divide los ítems globales" (Sergio, 12/10). La OTS y la
// certificación muestran una línea por servicio con la cantidad total; por debajo siguen siendo
// una línea por ítem del presupuesto (la certificación y el consumo las leen así).
const _normSvc = v => String(v ?? "").trim().replace(/\s+/g, " ").toLowerCase();
const agruparServicios = (items) => {
  const grupos = new Map();
  (items || []).forEach(it => {
    if (!it) return;
    const k = [_normSvc(it.item_name), _normSvc(it.unit), String(Number(it.unit_price) || 0), String(it.dias ?? "")].join("|");
    const g = grupos.get(k);
    if (!g) { grupos.set(k, { ...it, quantity: Number(it.quantity) || 0, subtotal: Number(it.subtotal) || 0, __lineas: 1 }); return; }
    g.quantity = Math.round((g.quantity + (Number(it.quantity) || 0)) * 10000) / 10000;
    g.subtotal += Number(it.subtotal) || 0;
    g.__lineas += 1;
    if ((it.sector || "") !== (g.sector || "")) g.sector = "";
    if ((it.front || "") !== (g.front || "")) g.front = "";
  });
  return [...grupos.values()];
};

// Fix CI (2026-09-11): detalle del Pedido de Servicio, disponible desde todas las instancias.
// Fix EK (2026-09-27): "En Servicios/Alquileres necesitamos poder visualizar el análisis.
// Que esto sea bastante similar a la parte de compras de materiales" (CEN). Mismo formato que
// el PDF del análisis de materiales: P.U. (con su % contra el costo de M.O. del presupuesto),
// tiempo en alquiler, subtotal por proveedor y el proveedor elegido de cada ítem.
function generateAPSPDF(act) {
  const doc = new jsPDF({ orientation: "l", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();
  const VIOLETA = [109, 40, 217];
  const cols = act.provider_columns || [];
  const items = act.analysis_items || [];
  const alq = act.categoria === "alquiler";
  const porProv = alq ? 3 : 2;
  doc.setFillColor(...VIOLETA);
  doc.rect(0, 0, pw, 30, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13); doc.setFont("helvetica", "bold");
  doc.text("CEN INGENIERIA E.A.S.", 14, 11);
  doc.setFontSize(8); doc.setFont("helvetica", "normal");
  doc.text("Instalaciones Eléctricas — Asunción, Paraguay", 14, 18);
  doc.setFontSize(9.5); doc.setFont("helvetica", "bold");
  doc.text("ANÁLISIS DE PRESUPUESTO DE SERVICIO", pw - 14, 11, { align: "right" });
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  doc.text(act.number || "", pw - 14, 18, { align: "right" });
  doc.setTextColor(...VIOLETA);
  doc.setFontSize(7.5);
  doc.text(`PPS: ${act.pps_number || "—"}   PS: ${act.ps_number || "—"}   Obra: ${act.project_name || act.obra_rapida_nombre || "—"}${alq ? "   ·   ALQUILER" : ""}`, 14, 27);
  doc.text(act.status === "cerrada" ? `CERRADO · ${formatDate(act.closed_at)}` : "ABIERTO", pw - 14, 27, { align: "right" });
  const sub = (it, ci) => {
    const p = (it.prices || [])[ci] || {};
    return (Number(p.unit_price) || 0) * (Number(it.quantity) || 0) * (alq ? (Number(p.dias) || 1) : 1);
  };
  const pct = (it, ci) => {
    const pu = Number(((it.prices || [])[ci] || {}).unit_price) || 0;
    return it.ref_precio_mo > 0 && pu > 0 ? ((pu / it.ref_precio_mo) - 1) * 100 : null;
  };
  const head = [[
    { content: "Servicio", rowSpan: 2 }, { content: "UM", rowSpan: 2 }, { content: "Cant.", rowSpan: 2 },
    ...cols.map(c => ({ content: c, colSpan: porProv, styles: { halign: "center" } })),
    { content: "Elegido", rowSpan: 2 },
  ], cols.flatMap(() => (alq ? ["P.U.", "Tiempo", "Subtotal"] : ["P.U.", "Subtotal"]))];
  const body = items.map(it => [
    `${it.item_name || "—"}${it.sub_de_nombre ? `\nSub-ítem de ${it.sub_de_nombre}` : ""}${imputacionTexto(it) ? `\n${imputacionTexto(it)}` : ""}${it.ref_precio_mo > 0 ? `\nRef. M.O.: ${formatPYG(it.ref_precio_mo)}` : ""}`,
    it.unit || "—", String(it.quantity ?? "—"),
    ...cols.flatMap((_, ci) => {
      const p = (it.prices || [])[ci] || {};
      const d = pct(it, ci);
      const pu = Number(p.unit_price) > 0 ? `${formatPYG(p.unit_price)}${d !== null ? `\n${d > 0 ? "+" : ""}${d.toFixed(1)}%` : ""}` : "—";
      const st = sub(it, ci) ? formatPYG(sub(it, ci)) : "—";
      return alq ? [pu, esPorPeriodo(it.unit) ? "Período" : String(p.dias || 1), st] : [pu, st];
    }),
    it.selected_idx >= 0 ? (cols[it.selected_idx] || "—") : "Sin selec.",
  ]);
  const elegido = items.reduce((s, it) => s + (it.selected_idx >= 0 ? sub(it, it.selected_idx) : 0), 0);
  body.push([
    { content: "TOTAL", colSpan: 3, styles: { halign: "right", fontStyle: "bold" } },
    ...cols.flatMap((_, ci) => {
      const t = items.reduce((s, it) => s + sub(it, ci), 0);
      return alq ? ["", "", t ? formatPYG(t) : "—"] : ["", t ? formatPYG(t) : "—"];
    }),
    { content: formatPYG(elegido), styles: { fontStyle: "bold" } },
  ]);
  autoTable(doc, {
    startY: 34, head, body, margin: { left: 6, right: 6 },
    headStyles: { fillColor: VIOLETA, textColor: [255, 255, 255], fontSize: 6.5, fontStyle: "bold", halign: "center", cellPadding: 1 },
    bodyStyles: { fontSize: 6.5, textColor: [30, 30, 30], overflow: "linebreak", cellPadding: 1 },
    columnStyles: { 0: { cellWidth: 55 }, 1: { cellWidth: 12, halign: "center" }, 2: { cellWidth: 12, halign: "center" } },
    didParseCell: (data) => {
      if (data.section !== "body" || data.row.index >= items.length) return;
      const it = items[data.row.index];
      const k = data.column.index - 3;
      if (k < 0 || k >= cols.length * porProv) return;
      const ci = Math.floor(k / porProv);
      if (ci === it.selected_idx) data.cell.styles.fillColor = [236, 253, 245];
      const d = pct(it, ci);
      if (k % porProv === 0 && d !== null) data.cell.styles.textColor = d > 1 ? [185, 28, 28] : d < -1 ? [21, 128, 61] : [180, 83, 9];
    },
  });
  const footerY = doc.internal.pageSize.getHeight() - 8;
  doc.setFontSize(7); doc.setFont("helvetica", "normal"); doc.setTextColor(150, 150, 150);
  doc.text(`Generado por Sistema ERP CEN · ${new Date().toLocaleString("es-PY")}`, pw / 2, footerY, { align: "center" });
  guardarPDF(doc, `${act.number || "aps"}.pdf`);
}

function PSDetalleModal({ ps, onClose }) {
  if (!ps) return null;
  return (
    <Modal open={!!ps} onClose={onClose} title={`Detalle: ${ps.number}`} wide>
      <div className="space-y-4" data-testid="ps-detalle-modal">
        <div className="grid grid-cols-2 gap-3">
          {[["N° Pedido", ps.number], ["Proyecto", ps.project_name], ["Estado", <StatusBadge key="s" status={ps.status} />], ["Categoría", ps.categoria === "alquiler" ? "Alquiler" : "Servicio General"], ["Urgencia", (ps.urgency || "").toUpperCase()], ["F. Inicio", ps.start_date ? formatDate(ps.start_date) : "—"], ["F. Final", ps.end_date ? formatDate(ps.end_date) : "—"], ["Solicitante", ps.requested_by_name || "—"]].map(([k, v]) => (
            <div key={k} className="bg-gray-50 rounded-xl p-3"><p className="text-[10px] text-gray-400 uppercase font-semibold tracking-wide">{k}</p><div className="text-sm font-medium text-gray-900 mt-0.5">{v}</div></div>
          ))}
        </div>
        {ps.categoria === "alquiler" && (
          <div className={`rounded-xl p-3 border ${ps.alquiler_flujo_cerrado ? "bg-gray-50 border-gray-200" : ps.alquiler_vencido ? "bg-red-50 border-red-200" : "bg-orange-50 border-orange-200"}`} data-testid="ps-detail-alquiler-info">
            <p className="text-[10px] uppercase font-semibold tracking-wide text-gray-500">Fecha límite de alquiler</p>
            <p className="text-sm font-bold text-gray-900 mt-0.5">{ps.alquiler_fecha_limite ? formatDate(ps.alquiler_fecha_limite) : "—"}</p>
            {ps.alquiler_flujo_cerrado ? (
              <p className="text-xs text-gray-500 mt-1">Flujo certificado — alerta finalizada</p>
            ) : ps.alquiler_dias_restantes != null && (
              <p className={`text-xs font-semibold mt-1 ${ps.alquiler_vencido ? "text-red-600" : "text-orange-600"}`}>
                {ps.alquiler_vencido ? `VENCIDO hace ${Math.abs(ps.alquiler_dias_restantes)} día(s)` : `Vence en ${ps.alquiler_dias_restantes} día(s)`}
              </p>
            )}
          </div>
        )}
        <div className="border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full text-xs"><thead className="bg-gray-50 border-b border-gray-200"><tr>{["#", "Descripción del Servicio", "UM", "Cant.", "Sector", "Frente", "Imputación"].map(h => <th key={h} className="text-left px-3 py-2.5 text-gray-600 font-semibold">{h}</th>)}</tr></thead>
            <tbody>{(ps.items || []).map((item, i) => (<tr key={i} className="border-b border-gray-100 last:border-0"><td className="px-3 py-2.5 text-gray-500">{i + 1}</td><td className="px-3 py-2.5 text-gray-700">{item.description || item.item_name}</td><td className="px-3 py-2.5 text-gray-500">{item.unit || "un"}</td><td className="px-3 py-2.5 text-gray-700">{item.quantity}</td><td className="px-3 py-2.5 text-gray-500">{item.sector || "—"}</td><td className="px-3 py-2.5 text-gray-500">{item.front || "—"}</td><td className="px-3 py-2.5 text-gray-500" data-testid={`ps-detalle-imputacion-${i}`}>{imputacionTexto(item) || "—"}</td></tr>))}</tbody>
          </table>
        </div>
        {ps.history?.length > 0 && (
          <div><p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Historial</p>
            {ps.history.map((h, i) => (<div key={i} className="flex items-start gap-2 text-xs mb-1"><span className="w-2 h-2 rounded-full bg-[#2E6DA4] mt-1.5 flex-shrink-0" /><div><span className="font-semibold text-gray-700">{h.action}</span><span className="text-gray-400 ml-2">{formatDate(h.at)}</span>{h.by && <span className="text-gray-400 ml-1">· {h.by}</span>}{h.comment && <p className="text-gray-500 mt-0.5">{h.comment}</p>}</div></div>))}
          </div>
        )}
        <div className="flex gap-3 pt-2 border-t border-gray-100">
          <button onClick={() => generatePSPDF(ps)} className="flex items-center gap-2 bg-[#2E6DA4] text-white px-4 py-2.5 rounded-xl text-xs font-semibold"><Download size={13} /> PDF</button>
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-xs font-medium">Cerrar</button>
        </div>
      </div>
    </Modal>
  );
}

// Fix CI (2026-09-11): "en todas las instancias se puedan visualizar los pedidos así como en
// materiales. Y en las líneas debe verse los datos de fecha objetivo y quien hizo el pedido"
// (Sergio, 11/09). Fecha objetivo = período estimado del PS (inicio → fin).
function PSLinea({ ps }) {
  const [ver, setVer] = useState(false);
  if (!ps) return null;
  const fecha = ps.start_date ? `${formatDate(ps.start_date)}${ps.end_date ? ` → ${formatDate(ps.end_date)}` : ""}` : (ps.end_date ? `hasta ${formatDate(ps.end_date)}` : "—");
  return (
    <div className="flex items-center gap-x-2 gap-y-0.5 flex-wrap text-[11px] text-gray-500 mt-1" data-testid={`ps-linea-${ps._id}`}>
      <span className="inline-flex items-center gap-1"><Calendar size={10} /> Fecha objetivo: <strong className="text-gray-700">{fecha}</strong></span>
      {ps.categoria === "alquiler" && ps.alquiler_fecha_limite && <span>· Límite alquiler: <strong className="text-gray-700">{formatDate(ps.alquiler_fecha_limite)}</strong></span>}
      <span>· Pedido por: <strong className="text-gray-700">{ps.requested_by_name || "—"}</strong></span>
      <button type="button" onClick={() => setVer(true)} className="inline-flex items-center gap-1 text-[#2E6DA4] font-semibold hover:underline" data-testid={`ver-ps-${ps._id}`}>
        <Eye size={11} /> Ver pedido {ps.number}
      </button>
      {ver && <PSDetalleModal ps={ps} onClose={() => setVer(false)} />}
    </div>
  );
}

function PedidosTab({
  requests, projects, loading, onRefresh, userRole, openNumber,
  listasClientes, listasObras, allBudgets, refreshBudgets, filtros,
}) {
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [viewPS, setViewPS] = useState(null);
  const [vincularTarget, setVincularTarget] = useState(null);
  const fl = useFiltrosServicios(filtros);   // Fix FV (2026-10-13): los filtros de Materiales
  const [form, setForm] = useState({
    urgency: "normal", start_date: "", end_date: "", notes: "", categoria: "servicio_general", alquiler_fecha_limite: "",
    lista_cliente_id: "", lista_obra_id: "", pto_objetivo: "", presupuesto_id: "", presupuesto_codigo: "",
    obra_rapida: false, obra_rapida_nombre: "",
    items: [nuevoItemPS()],
  });
  const [saving, setSaving] = useState(false);
  const [listaFrentes, setListaFrentes] = useState([]);
  const [loadingBudgets, setLoadingBudgets] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editFechaPS, setEditFechaPS] = useState(null);
  const [editFechaValue, setEditFechaValue] = useState("");
  const [savingFecha, setSavingFecha] = useState(false);
  // Fix O (2026-08-31): punto 7 de la carta — para que el semáforo de precios de
  // mano de obra pueda pintar, la línea del pedido de servicios necesita poder
  // vincularse a un ítem del presupuesto (mismo patrón del Fix G para materiales).
  // Es opcional: una línea sin vínculo se guarda igual que hoy.
  const [budgetItemsForLink, setBudgetItemsForLink] = useState([]);
  const [budgetNiveles, setBudgetNiveles] = useState([]); // Fix CI (2026-09-11)
  const canCreate = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra", "Ingeniero Residente", "Compras"]);
  const canDelete = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Técnica", "Compras", "Jefe de Compras"]);
  const canEditFecha = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra", "Compras"]);
  // BLOQUE 35 PARTE A — vinculación retroactiva de PS de obra rápida (mismo patrón Bloque 16 para PM).
  const canLink = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Técnica", "Jefe de Compras", "Coordinador de Obra"]);

  // BLOQUE 34 — deep-link desde el Dashboard (cuadro "Alquileres"): abre
  // directamente el detalle del pedido de servicio clickeado (?open=<numero>).
  const openedRef = useRef(false);
  useEffect(() => {
    if (!openNumber || openedRef.current || !requests.length) return;
    const target = requests.find(r => r.number === openNumber);
    if (target) {
      setViewPS(target);
      openedRef.current = true;
    }
  }, [openNumber, requests]);

  // Load budgets when form opens (refresca el catálogo compartido del padre)
  useEffect(() => {
    if (!showNew) return;
    setLoadingBudgets(true);
    refreshBudgets();
    setLoadingBudgets(false);
  }, [showNew, refreshBudgets]);

  // BLOQUE 35 PARTE A — Vinculación con Presupuesto (mismo patrón del PM, Bloque 1/16).
  const obrasFiltradas = form.lista_cliente_id
    ? listasObras.filter(o => o.cliente_id === form.lista_cliente_id || o._id === form.lista_obra_id)
    : listasObras;
  const selectedObra = listasObras.find(o => o._id === form.lista_obra_id);
  // Fix FU (2026-10-12): con cliente elegido, solo sus presupuestos (mismo criterio que el Pedido de Materiales, Fix DY).
  const presupuestosFiltrados = filtrarPresupuestos(allBudgets, { clienteId: form.lista_cliente_id, obraSel: selectedObra, listasClientes, listasObras });


  useEffect(() => {
    axios.get(`${API}/listas/frentes`).then(r => setListaFrentes(r.data || [])).catch(() => {});
  }, []);

  // Fix O (2026-08-31): listado del presupuesto (sin precios) para el buscador
  // opcional de vinculación por línea, mismo endpoint que ya usa el Fix G del PM.
  useEffect(() => {
    // Fix CI (2026-09-11): al cambiar de presupuesto, la imputación anterior deja de valer.
    setForm(f => (f.items.some(it => it.item_uid || it.nivel_id)
      ? { ...f, items: f.items.map(it => ({ ...it, item_uid: null, item_budget_label: "", nivel_id: "", nivel_label: "" })) }
      : f));
    if (!form.presupuesto_id) {
      setBudgetItemsForLink([]);
      setBudgetNiveles([]);
      return;
    }
    axios.get(`${API}/budgets/${form.presupuesto_id}/items-para-pedido`, { withCredentials: true })
      .then(({ data }) => {
        setBudgetItemsForLink(data.items || []);
        setBudgetNiveles(data.niveles || []);
      })
      .catch(() => {});
  }, [form.presupuesto_id]);
  // Fix CI (2026-09-11): catálogo de tareas + las tareas propias de cada ítem del presupuesto
  // (mismo desplegable Tarea del pedido de materiales).
  const [catalogoTareas, setCatalogoTareas] = useState([]);
  useEffect(() => {
    axios.get(`${API}/catalogo-tareas`, { withCredentials: true })
      .then(({ data }) => setCatalogoTareas(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);
  const tareaOptions = useMemo(() => {
    const nombres = {};
    catalogoTareas.forEach(t => { nombres[t.numero] = t.nombre; });
    const porItem = {};
    budgetItemsForLink.forEach(bi => {
      const nums = (bi.tareas || []).map(t => t.tarea_numero).filter(n => n != null);
      if (nums.length > 0) porItem[bi.item_uid] = nums;
    });
    return { todas: catalogoTareas, nombres, porItem };
  }, [catalogoTareas, budgetItemsForLink]);

  // V3.3 — flatten catalog the same way as PedidosMaterialesTab to keep parity
  const frenteOptions = listaFrentes.flatMap(f => (f.frentes || []).map(fr => `${f.cod}. ${fr}`));

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, nuevoItemPS()] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, field, val) => setForm(f => { const items = [...f.items]; items[i] = { ...items[i], [field]: val }; return { ...f, items }; });
  // Fix CI (2026-09-11): imputa el ítem a una línea del presupuesto (o lo desimputa), como el
  // material. Sigue siendo opcional; item_uid alimenta además el semáforo de M.O. del APS.
  const imputarItem = (i, uid) => {
    const bi = budgetItemsForLink.find(x => x.item_uid === uid);
    setForm(f => {
      const items = [...f.items];
      items[i] = { ...items[i], item_uids: null, item_uid: bi ? bi.item_uid : null, item_budget_label: bi ? budgetItemLabel(bi) : "" };
      return { ...f, items };
    });
  };
  // Fix FH (2026-10-08): "estos últimos 2 comentarios, son en compras de materiales, pero deben
  // funcionar de igual manera con servicios" (CEN): varios ítems del Pto (alcanza con uno) y
  // copiar/pegar ítems y tarea, como en el pedido de materiales. La fila sigue siendo una; al
  // guardar se abre en una línea por ítem, con la cantidad en partes iguales.
  const [variosIdx, setVariosIdx] = useState(null);
  const [psItemsCopiados, setPsItemsCopiados] = useState(null);
  const [psTareaCopiada, setPsTareaCopiada] = useState("");
  useEffect(() => { setPsItemsCopiados(null); }, [form.presupuesto_id]);
  const setItemsVarios = (i, uids) => {
    const limpios = (uids || []).filter(Boolean);
    if (limpios.length < 2) { imputarItem(i, limpios[0] || ""); return; }
    setForm(f => {
      const items = [...f.items];
      items[i] = { ...items[i], item_uids: limpios, item_uid: null, item_budget_label: "", nivel_id: "", nivel_label: "" };
      return { ...f, items };
    });
  };
  const uidsDeFila = (it) => (Array.isArray(it.item_uids) && it.item_uids.length > 1 ? it.item_uids : (it.item_uid ? [it.item_uid] : []));
  const copiarItemsPS = (it) => {
    const uids = uidsDeFila(it);
    if (!uids.length) return;
    setPsItemsCopiados(uids);
    toast.success(`${uids.length === 1 ? "Ítem copiado" : `${uids.length} ítems copiados`}. Usá "Pegar ítems" en otra fila.`);
  };
  const pegarItemsPS = (i) => setItemsVarios(i, (psItemsCopiados || []).filter(u => budgetItemsForLink.some(bi => bi.item_uid === u)));
  const copiarTareaPS = (num) => {
    if (!num) return;
    setPsTareaCopiada(String(num));
    toast.success(`Tarea ${num} copiada. Usá "Pegar tarea" en otra fila.`);
  };
  const updateItemNivel = (i, nivelId) => {
    const n = budgetNiveles.find(x => x.id === nivelId);
    setForm(f => {
      const items = [...f.items];
      items[i] = { ...items[i], nivel_id: nivelId, nivel_label: n ? n.label : "" };
      return { ...f, items };
    });
  };

  // BLOQUE 43 PARTE B — los ítems importados desde Excel/PDF se AGREGAN a los ya
  // cargados manualmente (solo se descartan las filas vacías del formulario).
  const handleImportItems = (imported) => {
    setForm(f => {
      const manuales = f.items.filter(it => String(it.description || "").trim());
      const nuevos = imported.map(it => ({
        categoria: "listado",   // Fix FH
        description: it.description || "",
        unit: it.unit || "un",
        quantity: Number(it.quantity) || 1,
        sector: it.sector || "",
        front: it.front || "",
      }));
      return { ...f, items: [...manuales, ...nuevos] };
    });
    setShowImport(false);
    toast.success(`${imported.length} ítem(s) agregados al pedido`);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (form.items.some(it => !it.description)) { toast.error("Complete todos los campos obligatorios"); return; }
    if (form.categoria === "alquiler" && !form.alquiler_fecha_limite) { toast.error("Los pedidos de categoría Alquiler requieren una fecha límite"); return; }
    if (form.obra_rapida) {
      if (!form.obra_rapida_nombre.trim()) { toast.error("El nombre de la obra es obligatorio para un pedido de obra rápida"); return; }
    } else if (!form.presupuesto_id) {
      toast.error("Debés vincular el pedido a un presupuesto aprobado");
      return;
    }
    // Fix EK (2026-09-27): mismo criterio de imputación que el pedido de materiales.
    // Fix FH (2026-10-08): solo las líneas "Servicio (del Pto.)"; vale también imputar a varios.
    if (!form.obra_rapida && budgetItemsForLink.length > 0
        && form.items.some(it => (it.categoria || "listado") === "listado" && uidsDeFila(it).length === 0)) {
      toast.error("Imputá cada línea a un ítem del presupuesto");
      return;
    }
    // Fix FH: una fila imputada a varios ítems se abre en una línea por ítem.
    const items = expandirFilas(form.items, null, budgetItemsForLink).map(({ __uid, ...it }) => {
      if (!__uid) return it;
      const bi = budgetItemsForLink.find(x => x.item_uid === it.item_uid);
      const niv = budgetNiveles.find(n => n.id === it.nivel_id);
      return { ...it, item_budget_label: bi ? budgetItemLabel(bi) : "", nivel_label: niv ? niv.label : "" };
    });
    setSaving(true);
    try {
      await axios.post(`${API}/purchases/service-requests`, { ...form, items }, { withCredentials: true });
      toast.success("Pedido de servicio creado");
      setShowNew(false);
      setForm({
        urgency: "normal", start_date: "", end_date: "", notes: "", categoria: "servicio_general", alquiler_fecha_limite: "",
        lista_cliente_id: "", lista_obra_id: "", pto_objetivo: "", presupuesto_id: "", presupuesto_codigo: "",
        obra_rapida: false, obra_rapida_nombre: "",
        items: [nuevoItemPS()],
      });
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al crear el pedido"); }
    finally { setSaving(false); }
  };

  const handleDeletePS = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/purchases/service-requests/${deleteTarget._id}`, { withCredentials: true });
      toast.success(`Pedido ${deleteTarget.number} eliminado`);
      setDeleteTarget(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar el pedido"); }
    finally { setDeleting(false); }
  };

  const openEditFecha = (ps) => { setEditFechaPS(ps); setEditFechaValue(ps.alquiler_fecha_limite || ""); };
  const handleSaveFecha = async () => {
    if (!editFechaPS || !editFechaValue) { toast.error("Ingrese una fecha válida"); return; }
    setSavingFecha(true);
    try {
      await axios.put(`${API}/purchases/service-requests/${editFechaPS._id}/alquiler-fecha`, { alquiler_fecha_limite: editFechaValue }, { withCredentials: true });
      toast.success("Fecha límite de alquiler actualizada");
      setEditFechaPS(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al actualizar la fecha"); }
    finally { setSavingFecha(false); }
  };

  // Fix FV (2026-10-13): como en Materiales, los pendientes arriba y lo más nuevo primero.
  const filtered = sortPendingFirst(fl.filtrar(requests), r => ["pendiente", "modificar"].includes(r.status));

  // BLOQUE 6 PARTE A — alquileres que requieren atención (por vencer ≤5 días o vencidos)
  const alquileresAlerta = requests.filter(r => r.alquiler_requiere_atencion);

  return (
    <div className="space-y-5">
      {/* Fix FV (2026-10-13): la barra de filtros de Materiales (Cliente → Obra → Presupuesto, Buscar, Estado y Urgencia). */}
      <BarraFiltrosServicios fl={fl} filtros={filtros} testIdPrefix="ps" searchPlaceholder="Buscar pedidos..."
        statusOptions={fl.opciones(requests, { etiquetas: STATUS_LABELS, fijos: ["pendiente", "modificar", "aprobada", "rechazada"] })}>
        {canCreate && (
          <button onClick={() => setShowNew(true)} className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2 rounded-xl text-xs font-semibold transition-all shadow-sm hover:shadow flex-shrink-0" data-testid="new-ps-btn">
            <Plus size={14} /> Nuevo Pedido de Servicio
          </button>
        )}
      </BarraFiltrosServicios>

      {/* Fix FV (2026-10-13): "Flujo del Pedido", como en Materiales. */}
      <div className="bg-gradient-to-r from-[#1E3A5F]/5 to-[#2E6DA4]/5 rounded-xl border border-[#2E6DA4]/10 p-4" data-testid="svc-flujo-pedido">
        <p className="text-xs font-semibold text-[#2E6DA4] mb-3">Flujo del Pedido de Servicios/Alquileres</p>
        <div className="flex items-center gap-1 overflow-x-auto">
          {SVC_FLOW_STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center flex-shrink-0">
              <div className="flex flex-col items-center min-w-[52px]">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: s.color }}>
                  {i + 1}
                </div>
                <span className="text-[8px] text-center mt-1 text-gray-500 leading-tight max-w-[50px]">{s.short}</span>
              </div>
              {i < SVC_FLOW_STEPS.length - 1 && <div className="h-px w-4 bg-gray-300 mb-3 flex-shrink-0" />}
            </div>
          ))}
        </div>
      </div>

      {/* BLOQUE 6 PARTE A — Banner "Alquileres por vencer" */}
      {alquileresAlerta.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4" data-testid="alquileres-banner">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} className="text-orange-500" />
            <h4 className="text-sm font-bold text-orange-800">Alquileres por vencer</h4>
            <span className="bg-orange-200 text-orange-800 text-xs font-bold px-2 py-0.5 rounded-full">{alquileresAlerta.length}</span>
          </div>
          <div className="space-y-1.5">
            {alquileresAlerta.map(ps => (
              <div key={ps._id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-orange-100 text-xs" data-testid={`alquiler-alerta-item-${ps._id}`}>
                <span className="text-gray-700"><span className="font-mono font-bold">{ps.number}</span> · {ps.project_name}</span>
                <span className={`font-bold ${ps.alquiler_vencido ? "text-red-600" : "text-orange-600"}`}>
                  {ps.alquiler_vencido ? `VENCIDO hace ${Math.abs(ps.alquiler_dias_restantes)} día${Math.abs(ps.alquiler_dias_restantes) !== 1 ? "s" : ""}` : `Vence en ${ps.alquiler_dias_restantes} día${ps.alquiler_dias_restantes !== 1 ? "s" : ""}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-28" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200/60 p-16 text-center shadow-sm">
          <Wrench size={44} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-500 font-medium">No hay pedidos de servicio</p>
          <p className="text-xs text-gray-400 mt-1">Crea el primer pedido de servicio para iniciar el flujo</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(ps => (
            <div key={ps._id} className="bg-white rounded-2xl border border-gray-200/60 p-4 shadow-sm hover:shadow-md transition-all" data-testid="ps-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-bold text-gray-900 text-sm font-mono">{ps.number}</span>
                    <StatusBadge status={ps.status} />
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${URGENCY_COLORS[ps.urgency] || "bg-gray-100 text-gray-500"}`}>{(ps.urgency || "normal").toUpperCase()}</span>
                  </div>
                  <p className="text-xs text-gray-600 truncate">{ps.project_name} · <span className="text-gray-400">por {ps.requested_by_name}</span></p>
                  {ps.obra_rapida && (
                    <button
                      type="button"
                      onClick={() => canLink && setVincularTarget(ps)}
                      disabled={!canLink}
                      className={`mt-1.5 inline-flex items-center gap-2 bg-sky-100 text-sky-800 border border-sky-300 rounded-full pl-3 pr-2 py-1 text-[11px] font-bold shadow-sm ${canLink ? "hover:bg-sky-200 cursor-pointer" : "cursor-default"} transition-colors`}
                      title={canLink ? "Vincular a un presupuesto aprobado" : "Pedido de obra rápida sin vincular"}
                      data-testid={`ps-obra-rapida-badge-${ps._id}`}>
                      <Cloud size={13} className="text-sky-600" />
                      <span>Pedido para obra rápida / A vincular con presupuesto</span>
                      {canLink && (
                        <span className="ml-0.5 bg-sky-600 text-white rounded-full w-5 h-5 flex items-center justify-center">
                          <Link2 size={12} />
                        </span>
                      )}
                    </button>
                  )}
                  <p className="text-xs text-gray-400 mt-0.5">{ps.items?.length || 0} ítem(s) · {ps.start_date ? `Inicio: ${ps.start_date}` : ""}{ps.end_date ? ` · Fin: ${ps.end_date}` : ""}</p>
                  {ps.categoria === "alquiler" && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      {ps.alquiler_flujo_cerrado ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200" data-testid={`alquiler-chip-${ps._id}`}>
                          <CheckCircle2 size={10} /> Alquiler finalizado
                        </span>
                      ) : (
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${ps.alquiler_vencido ? "bg-red-100 text-red-700 border-red-200" : ps.alquiler_requiere_atencion ? "bg-orange-100 text-orange-700 border-orange-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}
                          data-testid={`alquiler-chip-${ps._id}`}>
                          <Clock size={10} />
                          {ps.alquiler_vencido ? `VENCIDO hace ${Math.abs(ps.alquiler_dias_restantes)} día${Math.abs(ps.alquiler_dias_restantes) !== 1 ? "s" : ""}` : `Vence en ${ps.alquiler_dias_restantes} día${ps.alquiler_dias_restantes !== 1 ? "s" : ""}`}
                        </span>
                      )}
                      {canEditFecha && !ps.alquiler_flujo_cerrado && (
                        <button onClick={() => openEditFecha(ps)} title="Editar fecha límite de alquiler" className="text-gray-400 hover:text-[#2E6DA4]" data-testid={`edit-alquiler-fecha-btn-${ps._id}`}>
                          <Pencil size={11} />
                        </button>
                      )}
                    </div>
                  )}
                  <div className="mt-2">
                    <FlowStep steps={SVC_FLOW_STEPS} currentStatus={ps.status} />
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => setViewPS(ps)} className="flex items-center gap-1.5 border border-gray-200 text-gray-600 hover:bg-gray-50 px-3 py-1.5 rounded-xl text-xs font-medium transition-all" data-testid="view-ps-btn"><Eye size={13} /> VER</button>
                  <button onClick={() => generatePSPDF(ps)} className="flex items-center gap-1.5 border border-[#2E6DA4]/30 text-[#2E6DA4] hover:bg-[#2E6DA4]/5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all"><Download size={13} /> PDF</button>
                  {canDelete && (
                    <button onClick={() => setDeleteTarget(ps)} title="Eliminar pedido" className="flex items-center justify-center border border-red-200 text-red-500 hover:bg-red-50 px-2.5 py-1.5 rounded-xl transition-all" data-testid={`delete-ps-btn-${ps._id}`}><Trash2 size={13} /></button>
                  )}
                </div>
              </div>
              {ps.modification_comment && (
                <div className="mt-2 bg-orange-50 rounded-lg p-2 border border-orange-100 text-xs text-orange-700">
                  <MessageSquare size={11} className="inline mr-1" /><strong>Observación:</strong> {ps.modification_comment}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal: Detalle PS — Fix CI (2026-09-11): modal compartido con las demás instancias */}
      {viewPS && <PSDetalleModal ps={viewPS} onClose={() => setViewPS(null)} />}

      {/* Modal: Nuevo PS */}
      {/* Fix FH (2026-10-08): más ancho, por la categoría de cada ítem. */}
      <Modal open={showNew} onClose={() => setShowNew(false)} title="Nuevo Pedido de Servicio" extraWide>
        <form onSubmit={handleSave} className="space-y-4">
          {/* BLOQUE 35 PARTE A — Checkbox obra rápida (excepción a la validación de presupuesto, mismo patrón Bloque 16 para PM) */}
          <label className="flex items-start gap-2.5 bg-sky-50 border border-sky-200 rounded-xl p-3 cursor-pointer" data-testid="ps-obra-rapida-checkbox-label">
            <input
              type="checkbox"
              checked={form.obra_rapida}
              onChange={e => setForm(f => ({ ...f, obra_rapida: e.target.checked, ...(e.target.checked ? { lista_cliente_id: "", lista_obra_id: "", presupuesto_id: "", presupuesto_codigo: "", pto_objetivo: "" } : { obra_rapida_nombre: "" }) }))}
              className="mt-0.5 w-4 h-4 accent-sky-600"
              data-testid="ps-obra-rapida-checkbox"
            />
            <span className="flex items-center gap-1.5 text-xs font-semibold text-sky-800">
              <Cloud size={14} className="text-sky-600" />
              Pedido para obra rápida / a vincular posteriormente
            </span>
          </label>

          {form.obra_rapida ? (
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 space-y-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Nombre de la obra <span className="text-red-600">*</span></label>
              <input
                type="text"
                value={form.obra_rapida_nombre}
                onChange={e => setForm(f => ({ ...f, obra_rapida_nombre: e.target.value }))}
                placeholder="Ej: Reforma local Sergio Doria"
                className={`w-full border rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 ${!form.obra_rapida_nombre.trim() ? "border-red-300" : "border-gray-300"}`}
                data-testid="ps-obra-rapida-nombre-input"
              />
              <p className="text-[10px] text-sky-700">Este pedido avanzará el flujo completo sin presupuesto. Podrás vincularlo retroactivamente cuando exista el presupuesto aprobado.</p>
            </div>
          ) : (
          /* Vinculación con Presupuesto (OBLIGATORIA — reemplaza al campo Proyecto) */
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold text-blue-800 flex items-center gap-1.5">
              <span>Vinculación con Presupuesto <span className="text-red-600">*</span></span>
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Cliente (Catálogo)</label>
                {/* Fix FU (2026-10-12): "acá nos falta el buscador/filtro en el apartado Servicios/Alquileres" (Edson, 12/10). Con buscador, mismo valor y mismas cascadas. */}
                <SelectBuscable
                  value={form.lista_cliente_id}
                  onChange={v => setForm(f => ({ ...f, lista_cliente_id: v, lista_obra_id: "" }))}
                  vacio="— Sin vincular —"
                  options={listasClientes.map(c => ({ value: c._id, label: `${c.codigo} · ${c.cliente}` }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white"
                  testId="ps-lista-cliente-select" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Obra (Catálogo)</label>
                <SelectBuscable
                  value={form.lista_obra_id}
                  onChange={v => setForm(f => ({ ...f, lista_obra_id: v, presupuesto_id: "", presupuesto_codigo: "" }))}
                  vacio="— Sin vincular —"
                  options={obrasFiltradas.map(o => ({ value: o._id, label: `${o.codigo} · ${o.obra}` }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white"
                  testId="ps-lista-obra-select" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Presupuesto Objetivo <span className="text-red-600">*</span></label>
              <SelectBuscable
                value={form.presupuesto_id}
                onChange={v => {
                  const sel = presupuestosFiltrados.find(b => (b._id || b.id) === v);
                  setForm(f => ({ ...f, presupuesto_id: v, presupuesto_codigo: sel?.number || "", pto_objetivo: sel?.number || "" }));
                }}
                vacio={loadingBudgets ? "Cargando..." : "— Seleccionar presupuesto aprobado (obligatorio) —"}
                etiquetaActual={form.presupuesto_codigo}
                options={presupuestosFiltrados.map(b => ({ value: b._id || b.id, label: `${b.number} · ${b.nombre || b.obra}` }))}
                className={`w-full border rounded-xl px-3 py-2.5 text-sm bg-white ${!form.presupuesto_id ? "border-red-300" : "border-gray-300"}`}
                testId="ps-presupuesto-select"
                disabled={loadingBudgets} />
              {selectedObra && presupuestosFiltrados.length === 0 && (
                <p className="text-[10px] text-red-600 mt-1" data-testid="ps-sin-aprobados-warning">
                  La obra "{selectedObra.obra}" no tiene presupuestos aprobados. No se puede crear el pedido hasta que exista uno.
                </p>
              )}
              {!selectedObra && !form.presupuesto_id && (
                <p className="text-[10px] text-red-600 mt-1">Debés vincular el pedido a un presupuesto antes de guardar.</p>
              )}
            </div>
          </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Categoría</label>
              <select value={form.categoria} onChange={e => {
                const categoria = e.target.value;
                setForm(f => ({ ...f, categoria, alquiler_fecha_limite: categoria === "alquiler" ? (f.alquiler_fecha_limite || f.end_date || "") : "" }));
              }} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white" data-testid="ps-categoria-select">
                <option value="servicio_general">Servicio General</option>
                <option value="alquiler">Alquiler</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Urgencia</label>
              <select value={form.urgency} onChange={e => setForm(f => ({...f, urgency: e.target.value}))} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white" data-testid="ps-urgency-select">
                <option value="normal">Normal</option><option value="alta">Alta</option><option value="critica">Crítica</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">F. Inicio Estimada</label>

              <input type="date" value={form.start_date} onChange={e => setForm(f => ({...f, start_date: e.target.value}))} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">F. Finalización Estimada</label>
              <input type="date" value={form.end_date} onChange={e => setForm(f => ({...f, end_date: e.target.value}))} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" />
            </div>
            {form.categoria === "alquiler" && (
              <div className="col-span-2 bg-orange-50 border border-orange-200 rounded-xl p-3">
                <label className="block text-xs font-semibold text-orange-800 mb-1.5">Fecha límite de alquiler *</label>
                <input type="date" value={form.alquiler_fecha_limite} onChange={e => setForm(f => ({...f, alquiler_fecha_limite: e.target.value}))} className="w-full border border-orange-300 rounded-xl px-3 py-2.5 text-sm" required data-testid="ps-alquiler-fecha-input" />
                <p className="text-[10px] text-orange-600 mt-1">Se generará una alerta 5 días antes de esta fecha y hasta que se certifique el flujo.</p>
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-700">Ítems de Servicio *</label>
              <div className="flex items-center gap-4">
                <button type="button" onClick={() => setShowImport(true)}
                  className="flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-800 font-semibold"
                  data-testid="import-svc-items-btn">
                  <Upload size={12} /> Importar desde Excel/PDF (IA)
                </button>
                <button type="button" onClick={addItem} className="text-xs text-[#2E6DA4] hover:underline font-medium" data-testid="add-svc-item-btn">+ Agregar ítem</button>
              </div>
            </div>
            <div className="space-y-2">
              {form.items.map((item, i) => (
                <div key={i} className="bg-gray-50/50 rounded-xl p-2 border border-gray-100 space-y-1.5">
                  <div className="grid grid-cols-12 gap-2 items-center">
                    {/* Fix FH (2026-10-08): categoría del ítem, como en el pedido de materiales. */}
                    <select value={item.categoria || "listado"} onChange={e => updateItem(i, "categoria", e.target.value)}
                      className="col-span-2 border border-gray-200 rounded-lg px-1 py-1.5 text-[11px] bg-white" data-testid={`ps-item-categoria-${i}`}>
                      {PS_CATEGORIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                    <AutocompleteInput
                      placeholder="Descripción del servicio *" value={item.description}
                      onChange={v => updateItem(i, "description", v)}
                      suggestions={SEAV_DESCRIPTIONS}
                      className="col-span-4"
                      multiline   /* Fix IU (2026-11-02): la descripción se ve completa y se sigue editando */
                      inputClassName="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                      data-testid={`ps-item-desc-${i}`} required
                    />
                    <input placeholder="UM" value={item.unit} onChange={e => updateItem(i, "unit", e.target.value)} className="col-span-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-center" />
                    <input type="number" min="1" value={item.quantity} onChange={e => updateItem(i, "quantity", Number(e.target.value))} className="col-span-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-center" />
                    <input placeholder="Sector" value={item.sector} onChange={e => updateItem(i, "sector", e.target.value)} className="col-span-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                    <div className="col-span-2 min-w-0">
                      <SelectBuscable value={item.front} onChange={v => updateItem(i, "front", v)} vacio="— Frente —" placeholderBuscar="Buscar frente..."
                        options={(frenteOptions.length > 0 ? frenteOptions : SEAV_FRONTS).map(opt => ({ value: opt, label: opt }))}
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" envolver testId={`ps-item-frente-${i}`} />
                    </div>
                    {form.items.length > 1 && <button type="button" onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600 col-span-1 flex justify-center"><X size={14} /></button>}
                  </div>
                  {/* Fix CI (2026-09-11): "que el ítem del servicio pueda imputarse de la misma
                      manera que lo hace el material" (Sergio, 11/09): Ítem del Pto, Nivel y Tarea,
                      como en el pedido de materiales. Opcional; alimenta el semáforo de M.O. del APS. */}
                  {form.presupuesto_id && !form.obra_rapida && (
                    <div className="grid grid-cols-12 gap-2 items-start pl-1" data-testid={`ps-item-imputacion-${i}`}>
                      <div className="col-span-6 flex flex-col gap-0.5">
                        {Array.isArray(item.item_uids) && item.item_uids.length > 1 ? (
                          /* Fix FH (2026-10-08): imputada a varios ítems del Pto. */
                          <div className="border border-[#2E6DA4]/40 bg-[#EBF3FB]/60 rounded-lg px-1.5 py-1 space-y-0.5" data-testid={`ps-item-multi-${i}`}>
                            {item.item_uids.map(uid => {
                              const bi = budgetItemsForLink.find(x => x.item_uid === uid);
                              return <p key={uid} className="text-[10px] leading-tight text-gray-700">{bi ? `${bi.numero ? bi.numero + " " : ""}${bi.descripcion}` : uid}</p>;
                            })}
                            <button type="button" onClick={() => setVariosIdx(i)} className="text-[9px] font-semibold text-[#2E6DA4] hover:underline" data-testid={`ps-item-varios-items-${i}`}>Cambiar ítems…</button>
                          </div>
                        ) : (
                          <SelectItemPto
                            value={item.item_uid || ""}
                            itemsDeEsteMaterial={[]}
                            itemsRestantes={budgetItemsForLink}
                            disabled={budgetItemsForLink.length === 0}
                            placeholder={budgetItemsForLink.length ? ((item.categoria || "listado") === "listado" ? "— Imputar a ítem del Pto (obligatorio) —" : "— Imputar a ítem del Pto (opcional) —") : "El presupuesto no tiene ítems para imputar"}
                            testId={`ps-item-ptoitem-${i}`}
                            onChange={v => imputarItem(i, v)}
                          />
                        )}
                        <div className="flex gap-2">
                          {budgetItemsForLink.length > 1 && !(Array.isArray(item.item_uids) && item.item_uids.length > 1) && (
                            <button type="button" onClick={() => setVariosIdx(i)} className="text-[9px] font-semibold text-[#2E6DA4] hover:underline" data-testid={`ps-item-varios-items-${i}`}>Varios ítems…</button>
                          )}
                          {uidsDeFila(item).length > 0 && (
                            <button type="button" onClick={() => copiarItemsPS(item)} className="text-[9px] font-semibold text-gray-500 hover:text-[#2E6DA4] hover:underline" data-testid={`ps-item-copiar-items-${i}`}>Copiar ítems</button>
                          )}
                          {(psItemsCopiados || []).length > 0 && (
                            <button type="button" onClick={() => pegarItemsPS(i)} className="text-[9px] font-semibold text-emerald-700 hover:underline" data-testid={`ps-item-pegar-items-${i}`}>Pegar ítems ({psItemsCopiados.length})</button>
                          )}
                        </div>
                        {variosIdx === i && (
                          <VariosItemsPanel
                            servicio
                            itemsDeEsteMaterial={[]}
                            itemsRestantes={budgetItemsForLink}
                            nivelMap={null}
                            pesos={null}
                            cantidad={item.quantity}
                            unidad={item.unit}
                            seleccion={uidsDeFila(item)}
                            onCancel={() => setVariosIdx(null)}
                            onConfirm={(uids) => { setVariosIdx(null); setItemsVarios(i, uids); }}
                          />
                        )}
                      </div>
                      {Array.isArray(item.item_uids) && item.item_uids.length > 1 ? (
                        <p className="col-span-3 text-[11px] text-gray-400 px-2 py-1.5" data-testid={`ps-item-nivel-${i}`}>Según cada ítem</p>
                      ) : (
                      <div className="col-span-3 min-w-0">
                        <SelectBuscable value={item.nivel_id || ""} onChange={v => updateItemNivel(i, v)} vacio={budgetNiveles.length > 0 ? "— Nivel —" : "Sin niveles"} placeholderBuscar="Buscar nivel..."
                          options={budgetNiveles.map(n => ({ value: n.id, label: n.label }))}
                          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" testId={`ps-item-nivel-${i}`} />
                      </div>
                      )}
                      <div className="col-span-3 flex flex-col gap-0.5">
                      <SelectBuscable value={item.tarea_numero || ""} onChange={v => updateItem(i, "tarea_numero", v)} vacio="— Tarea —" placeholderBuscar="Buscar tarea..."
                        options={[
                          ...(tareaOptions.porItem[item.item_uid] || []).map(n => ({ value: String(n), label: `${n}. ${tareaOptions.nombres[n] || ""}`, grupo: "Del ítem" })),
                          ...tareaOptions.todas.map(t => ({ value: String(t.numero), label: `${t.numero}. ${t.nombre}`, grupo: (tareaOptions.porItem[item.item_uid] || []).length > 0 ? "Todas" : "Catálogo" })),
                        ]}
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" testId={`ps-item-tarea-${i}`} />
                      {/* Fix FH (2026-10-08): copiar y pegar la tarea, como en materiales. */}
                      <div className="flex gap-2">
                        {item.tarea_numero && (
                          <button type="button" onClick={() => copiarTareaPS(item.tarea_numero)} className="text-[9px] font-semibold text-gray-500 hover:text-[#2E6DA4] hover:underline" data-testid={`ps-item-copiar-tarea-${i}`}>Copiar tarea</button>
                        )}
                        {psTareaCopiada && psTareaCopiada !== String(item.tarea_numero || "") && (
                          <button type="button" onClick={() => updateItem(i, "tarea_numero", psTareaCopiada)} className="text-[9px] font-semibold text-emerald-700 hover:underline" data-testid={`ps-item-pegar-tarea-${i}`}>Pegar tarea ({psTareaCopiada})</button>
                        )}
                      </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={2} placeholder="Instrucciones adicionales..." className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowNew(false)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all" data-testid="save-ps-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}{saving ? "Creando..." : "Crear Pedido"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal Importar ítems de servicio desde Excel/PDF (BLOQUE 43 PARTE B) */}
      <ImportPMItemsModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onConfirm={handleImportItems}
        mode="servicios"
        frenteOptions={frenteOptions.length > 0 ? frenteOptions : SEAV_FRONTS}
      />

      {/* Modal confirmar eliminación de PS (BLOQUE 6 PARTE C.2) */}
      <Modal open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} title="Eliminar Pedido de Servicio">
        {deleteTarget && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
              <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                ¿Confirmás eliminar el pedido <span className="font-mono font-bold">{deleteTarget.number}</span>? Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleting}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium disabled:opacity-60">Cancelar</button>
              <button type="button" onClick={handleDeletePS} disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="confirm-delete-ps-btn">
                {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                {deleting ? "Eliminando..." : "Eliminar"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal editar fecha límite de alquiler (BLOQUE 6 PARTE A) */}
      <Modal open={!!editFechaPS} onClose={() => !savingFecha && setEditFechaPS(null)} title="Editar Fecha Límite de Alquiler">
        {editFechaPS && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Pedido <span className="font-mono font-bold">{editFechaPS.number}</span></p>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Nueva fecha límite *</label>
              <input type="date" value={editFechaValue} onChange={e => setEditFechaValue(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="edit-alquiler-fecha-input" />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setEditFechaPS(null)} disabled={savingFecha}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium disabled:opacity-60">Cancelar</button>
              <button type="button" onClick={handleSaveFecha} disabled={savingFecha}
                className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="save-alquiler-fecha-btn">
                {savingFecha ? <Loader2 size={15} className="animate-spin" /> : <Pencil size={15} />}
                {savingFecha ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal Vinculación Retroactiva (BLOQUE 35 PARTE A) */}
      <VincularPSRetroactivoModal
        target={vincularTarget}
        onClose={() => setVincularTarget(null)}
        listasClientes={listasClientes}
        listasObras={listasObras}
        allBudgets={allBudgets}
        onLinked={() => { setVincularTarget(null); onRefresh(); }}
      />
    </div>
  );
}

export function VincularPSRetroactivoModal({ target, onClose, listasClientes, listasObras, allBudgets, onLinked }) {
  const [clienteId, setClienteId] = useState("");
  const [obraId, setObraId] = useState("");
  const [presupuestoId, setPresupuestoId] = useState("");
  const [saving, setSaving] = useState(false);
  // Fix EK (2026-09-27): cada línea del pedido rápido elige su ítem del presupuesto.
  const [ptoData, setPtoData] = useState({ items: [], niveles: [] });
  const [lineaItem, setLineaItem] = useState({});

  useEffect(() => {
    setClienteId(""); setObraId(""); setPresupuestoId("");
  }, [target]);

  useEffect(() => {
    setLineaItem({});
    if (!presupuestoId) { setPtoData({ items: [], niveles: [] }); return; }
    axios.get(`${API}/budgets/${presupuestoId}/items-para-pedido`, { withCredentials: true })
      .then(({ data }) => setPtoData({ items: data.items || [], niveles: data.niveles || [] }))
      .catch(() => toast.error("No se pudieron cargar los ítems del presupuesto"));
  }, [presupuestoId]);

  const lineas = (Array.isArray(target?.lineas) ? target.lineas
    : (Array.isArray(target?.items) ? target.items.map((it, idx) => ({
        idx, nombre: it.description || "", unidad: it.unit || "", cantidad: it.quantity, item_uid: it.item_uid || "",
        categoria: it.categoria || "listado",   // Fix FH
      })) : []))
    .filter(l => !l.item_uid && (l.categoria || "listado") === "listado");
  const nivelMapV = {};
  ptoData.niveles.forEach(n => { nivelMapV[n.id] = n.label; });
  const faltanLineas = ptoData.items.length ? lineas.filter(l => !lineaItem[l.idx]).length : 0;

  const obrasFiltradas = clienteId ? listasObras.filter(o => o.cliente_id === clienteId) : listasObras;
  const selectedObra = listasObras.find(o => o._id === obraId);
  const presupuestos = filtrarPresupuestos(allBudgets, { clienteId, obraSel: selectedObra, listasClientes, listasObras });   // Fix FU

  const handleLink = async () => {
    if (!presupuestoId) { toast.error("Seleccioná un presupuesto aprobado"); return; }
    if (faltanLineas) { toast.error(`Falta vincular ${faltanLineas} línea(s) a un ítem del presupuesto`); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/purchases/service-requests/${target._id}/vincular-retroactivo`,
        { presupuesto_id: presupuestoId, lista_cliente_id: clienteId || null, lista_obra_id: obraId || null,
          lineas: lineas.filter(l => lineaItem[l.idx]).map(l => ({ idx: l.idx, item_uid: lineaItem[l.idx] })) },
        { withCredentials: true });
      const u = res.data?.updated || {};
      toast.success(`Vinculado a ${res.data?.presupuesto_codigo || "presupuesto"} · ${u.lineas || 0} línea(s) imputada(s) · ${(u.ots || 0)} OTS actualizada(s)`);
      onLinked();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al vincular"); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={!!target} onClose={() => !saving && onClose()} title="Vincular a Presupuesto" wide>
      {target && (
        <div className="space-y-4">
          <div className="flex items-start gap-2.5 bg-sky-50 border border-sky-200 rounded-xl p-3">
            <Cloud size={16} className="text-sky-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-sky-800">
              Vinculás el pedido de servicio de obra rápida <span className="font-mono font-bold">{target.number}</span>
              {target.obra_rapida_nombre ? <> ("{target.obra_rapida_nombre}")</> : null} a un presupuesto aprobado.
              Se propagará a todos sus documentos derivados (PPS, APS, PFS, OTS).
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Cliente (Catálogo)</label>
            {/* Fix FU (2026-10-12): "acá nos falta el buscador/filtro en el apartado Servicios/Alquileres" (Edson, 12/10). Con buscador, mismo valor y mismas cascadas. */}
            <SelectBuscable
              value={clienteId}
              onChange={v => { setClienteId(v); setObraId(""); setPresupuestoId(""); }}
              vacio="— Seleccionar cliente —"
              options={listasClientes.map(c => ({ value: c._id, label: `${c.codigo} · ${c.cliente}` }))}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white"
              testId="vincular-ps-cliente-select" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Obra (Catálogo)</label>
            <SelectBuscable
              value={obraId}
              onChange={v => { setObraId(v); setPresupuestoId(""); }}
              vacio="— Seleccionar obra —"
              options={obrasFiltradas.map(o => ({ value: o._id, label: `${o.codigo} · ${o.obra}` }))}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white"
              testId="vincular-ps-obra-select" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Presupuesto Aprobado <span className="text-red-600">*</span></label>
            <SelectBuscable
              value={presupuestoId}
              onChange={v => setPresupuestoId(v)}
              vacio="— Seleccionar presupuesto aprobado —"
              options={presupuestos.map(b => ({ value: b._id || b.id, label: `${b.number} · ${b.nombre || b.obra}` }))}
              className={`w-full border rounded-xl px-3 py-2.5 text-sm bg-white ${!presupuestoId ? "border-red-300" : "border-gray-300"}`}
              testId="vincular-ps-presupuesto-select" />
            {selectedObra && presupuestos.length === 0 && (
              <p className="text-[10px] text-red-600 mt-1">La obra seleccionada no tiene presupuestos aprobados.</p>
            )}
          </div>
          {presupuestoId && lineas.length > 0 && ptoData.items.length > 0 && (
            <div data-testid="vincular-ps-lineas">
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Ítem del presupuesto de cada línea <span className="text-red-600">*</span></label>
              <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-[40vh] overflow-y-auto">
                {lineas.map(l => (
                  <div key={l.idx} className="grid grid-cols-2 gap-2 items-start px-3 py-2" data-testid={`vincular-ps-linea-${l.idx}`}>
                    <p className="text-xs text-gray-800 font-medium">{l.nombre}<span className="block text-[10px] text-gray-400">{l.cantidad} {l.unidad}</span></p>
                    <SelectItemPto value={lineaItem[l.idx] || ""} itemsDeEsteMaterial={[]} itemsRestantes={ptoData.items} nivelMap={nivelMapV}
                      placeholder="— Imputar a —" testId={`vincular-ps-linea-item-${l.idx}`}
                      onChange={v => setLineaItem(prev => ({ ...prev, [l.idx]: v }))} />
                  </div>
                ))}
              </div>
              {faltanLineas > 0 && <p className="text-[10px] text-amber-700 mt-1">Faltan {faltanLineas} línea(s) por vincular.</p>}
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} disabled={saving}
              className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium disabled:opacity-60">Cancelar</button>
            <button type="button" onClick={handleLink} disabled={saving || !presupuestoId || faltanLineas > 0}
              className="flex-1 bg-sky-600 hover:bg-sky-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="confirm-vincular-ps-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />}
              {saving ? "Vinculando..." : "Vincular"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ==================== TAB: APROBACIONES ====================

function AprobacionesTab({ requests, loading, onRefresh, userRole, filtros }) {
  const [commentModal, setCommentModal] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [actionType, setActionType] = useState("");
  const [saving, setSaving] = useState(false);
  // Inline items modification
  const [modifyPSModal, setModifyPSModal] = useState(null);
  const [modifyItems, setModifyItems] = useState([]);
  const [modifyComment, setModifyComment] = useState("");
  const [savingModify, setSavingModify] = useState(false);
  const [listaFrentes, setListaFrentes] = useState([]);

  const canApprove = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra", "Compras"]);
  const aprobadorPerfil = useAprobadorPedidos(true);   // Fix EX (2026-10-05) · Fix HB: perfiles de Servicios
  const pendingAll = requests.filter(r => ["pendiente", "modificar"].includes(r.status));
  const fl = useFiltrosServicios(filtros);   // Fix FV (2026-10-13): los filtros de Materiales
  const pending = sortPendingFirst(fl.filtrar(pendingAll), () => true);

  useEffect(() => {
    axios.get(`${API}/listas/frentes`).then(r => setListaFrentes(r.data || [])).catch(() => {});
  }, []);

  // V3.3 — flatten catalog the same way as PedidosMaterialesTab to keep parity
  const frenteOptions = listaFrentes.flatMap(f => (f.frentes || []).map(fr => `${f.cod}. ${fr}`));

  const openComment = (ps, type) => { setCommentModal(ps); setActionType(type); setCommentText(""); };

  const openModifyItems = (ps) => {
    setModifyPSModal(ps);
    setModifyItems((ps.items || []).map(item => ({ ...item })));
    setModifyComment("");
  };

  const updateModifyItem = (idx, field, val) => {
    setModifyItems(prev => {
      const items = [...prev];
      items[idx] = { ...items[idx], [field]: field === "quantity" ? (Number(val) || 1) : val };
      return items;
    });
  };

  const addModifyItem = () => setModifyItems(prev => [...prev, { description: "", unit: "un", quantity: 1, sector: "", front: "" }]);
  const removeModifyItem = (idx) => setModifyItems(prev => prev.filter((_, i) => i !== idx));

  const handleModifySave = async (approve) => {
    if (!modifyPSModal) return;
    if (modifyItems.some(it => !it.description)) { toast.error("Complete la descripción de todos los ítems"); return; }
    setSavingModify(true);
    try {
      const { data } = await axios.put(`${API}/purchases/service-requests/${modifyPSModal._id}/modify-items`,
        { items: modifyItems, comment: modifyComment, approve },
        { withCredentials: true });
      toast.success(approve ? `Pedido modificado. ${mensajeAprobacion(data, modifyPSModal.number)}` : "Ítems actualizados");
      setModifyPSModal(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al modificar"); }
    finally { setSavingModify(false); }
  };

  const handleAction = async () => {
    if (!commentModal) return;
    if (actionType !== "approve" && !commentText.trim()) { toast.error("Ingrese un comentario"); return; }
    setSaving(true);
    const endpoint = actionType === "approve" ? "approve" : actionType === "reject" ? "reject" : "modify";
    try {
      const { data } = await axios.put(`${API}/purchases/service-requests/${commentModal._id}/${endpoint}`, { comment: commentText }, { withCredentials: true });
      toast.success(actionType === "approve" ? mensajeAprobacion(data, commentModal.number) : actionType === "reject" ? "Pedido rechazado" : "Pedido devuelto para modificación");
      setCommentModal(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <BarraFiltrosServicios fl={fl} filtros={filtros} testIdPrefix="svc-aprobaciones" searchPlaceholder="Buscar por N°, obra o servicio..."
        statusOptions={fl.opciones(pendingAll, { etiquetas: STATUS_LABELS, fijos: ["pendiente", "modificar"] })} />
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-gray-800">Aprobación de Pedidos de Servicio</h3>
          <p className="text-xs text-gray-500 mt-0.5">Rol: Coordinador de Obra</p>
        </div>
        {pending.length > 0 && <span className="bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1 rounded-full border border-amber-200">{pending.length} pendiente(s)</span>}
      </div>
      {loading ? <div className="space-y-3">{[1,2].map(i => <Skeleton key={i} className="h-24" />)}</div> : pending.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200/60 p-12 text-center shadow-sm">
          <CheckCircle2 size={44} className="mx-auto text-emerald-200 mb-3" />
          <p className="text-gray-500 font-medium">Sin pedidos pendientes</p>
          <p className="text-xs text-gray-400 mt-1">Todos los pedidos han sido procesados</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pending.map(ps => (
            <div key={ps._id} className="bg-white rounded-2xl border border-gray-200/60 p-4 shadow-sm" data-testid="aprob-ps-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-gray-900 text-sm font-mono">{ps.number}</span>
                    <StatusBadge status={ps.status} />
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${URGENCY_COLORS[ps.urgency] || "bg-gray-100"}`}>{(ps.urgency || "normal").toUpperCase()}</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-0.5">{ps.project_name} · por <strong>{ps.requested_by_name}</strong></p>
                  <p className="text-xs text-gray-400 mt-0.5">{ps.items?.length || 0} ítem(s) · {formatDate(ps.created_at)}</p>
                  <PSLinea ps={ps} />
                  <AprobacionParcial parciales={ps.aprobaciones_parciales} testId={`aprobacion-parcial-${ps.number}`} />
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(ps.items || []).slice(0, 4).map((item, i) => (
                      <span key={i} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{item.description || item.item_name} ×{item.quantity}</span>
                    ))}
                  </div>
                </div>
                {(canApprove || aprobadorPerfil) && (
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button onClick={() => openComment(ps, "approve")} className="flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 rounded-xl text-xs font-semibold transition-all" data-testid="approve-ps-btn"><Check size={12} /> Aprobar</button>
                    {canApprove && (<>
                    <button onClick={() => openModifyItems(ps)} className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-xl text-xs font-semibold transition-all" data-testid="modify-ps-btn"><Pencil size={12} /> Modificar Ítems</button>
                    <button onClick={() => openComment(ps, "reject")} className="flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-xl text-xs font-semibold transition-all" data-testid="reject-ps-btn"><X size={12} /> Rechazar</button>
                    </>)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal: Aprobar / Rechazar con comentario */}
      <Modal open={!!commentModal} onClose={() => setCommentModal(null)} title={`${actionType === "approve" ? "Aprobar" : "Rechazar"}: ${commentModal?.number}`}>
        <div className="space-y-4">
          <div className={`rounded-xl p-3 border text-xs ${actionType === "approve" ? "bg-emerald-50 border-emerald-100 text-emerald-700" : "bg-red-50 border-red-100 text-red-700"}`}>
            {actionType === "approve" ? "Al aprobar, Compras podrá generar el Pedido de Presupuesto de Servicio." : "Al rechazar, el pedido quedará anulado."}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Comentario {actionType !== "approve" ? "*" : "(opcional)"}</label>
            <textarea value={commentText} onChange={e => setCommentText(e.target.value)} rows={3} placeholder="Ingrese un comentario..." className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none" data-testid="approval-comment-input" />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setCommentModal(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
            <button onClick={handleAction} disabled={saving} className={`flex-1 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all ${actionType === "approve" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-red-500 hover:bg-red-600"}`} data-testid="confirm-action-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}{saving ? "Procesando..." : "Confirmar"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal: Modificar Ítems inline */}
      {modifyPSModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !savingModify && setModifyPSModal(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="bg-amber-500 px-6 py-4 rounded-t-2xl flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2.5 text-white">
                <Pencil size={16} />
                <div>
                  <p className="font-bold text-sm">Modificar Ítems del Pedido</p>
                  <p className="text-amber-100 text-xs">{modifyPSModal.number} — editar directamente como Coordinador</p>
                </div>
              </div>
              <button onClick={() => !savingModify && setModifyPSModal(null)} className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-all"><X size={16} /></button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              <div className="space-y-2">
                {modifyItems.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-1.5 items-center bg-gray-50 rounded-xl p-2 border border-gray-100">
                    <span className="col-span-1 text-[10px] text-gray-400 font-mono text-center">{idx + 1}</span>
                    <AutocompleteInput
                      value={item.description} onChange={v => updateModifyItem(idx, "description", v)}
                      suggestions={SEAV_DESCRIPTIONS}
                      placeholder="Descripción *" className="col-span-4"
                      multiline   /* Fix IU */
                      inputClassName="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                      data-testid={`ps-modify-item-desc-${idx}`} required
                    />
                    <input value={item.unit} onChange={e => updateModifyItem(idx, "unit", e.target.value)} placeholder="UM" className="col-span-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-center" />
                    <input type="number" min="1" value={item.quantity} onChange={e => updateModifyItem(idx, "quantity", e.target.value)} className="col-span-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-center" />
                    <input value={item.sector || ""} onChange={e => updateModifyItem(idx, "sector", e.target.value)} placeholder="Sector" className="col-span-2 border border-gray-200 rounded-lg px-2 py-1.5 text-xs" data-testid={`modify-sector-${idx}`} />
                    <div className="col-span-2 min-w-0">
                      <SelectBuscable value={item.front || ""} onChange={v => updateModifyItem(idx, "front", v)} vacio="— Frente —" placeholderBuscar="Buscar frente..."
                        options={(frenteOptions.length > 0 ? frenteOptions : SEAV_FRONTS).map(opt => ({ value: opt, label: opt }))}
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" envolver testId={`modify-frente-${idx}`} />
                    </div>
                    {modifyItems.length > 1 && <button type="button" onClick={() => removeModifyItem(idx)} className="col-span-1 flex justify-center text-red-400 hover:text-red-600"><X size={13} /></button>}
                  </div>
                ))}
              </div>
              <button type="button" onClick={addModifyItem} className="text-xs text-amber-600 hover:underline font-medium">+ Agregar ítem</button>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Comentario (opcional)</label>
                <textarea value={modifyComment} onChange={e => setModifyComment(e.target.value)} rows={2} placeholder="Motivo de la modificación..." className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm resize-none" />
              </div>
              <div className="flex gap-3 pt-2 border-t border-gray-100">
                <button onClick={() => setModifyPSModal(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
                <button onClick={() => handleModifySave(false)} disabled={savingModify} className="flex items-center gap-2 border border-amber-400 text-amber-700 hover:bg-amber-50 rounded-xl px-4 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="save-modify-btn">
                  {savingModify ? <Loader2 size={14} className="animate-spin" /> : <Pencil size={14} />}Solo Modificar
                </button>
                <button onClick={() => handleModifySave(true)} disabled={savingModify} className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl px-4 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="modify-approve-btn">
                  {savingModify ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}Modificar y Aprobar
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ==================== TAB: PEDIDOS A PROVEEDORES (PPS) ====================

function PedProvTab({ requests, supplierOrders: ppsTodos, loading, onRefresh, userRole, filtros }) {
  const [saving, setSaving] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [pdfSelectPPS, setPdfSelectPPS] = useState(null); // BLOQUE 18 PARTE A
  const canCreate = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Compras", "Coordinador de Obra"]);
  const canDelete = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Técnica", "Compras", "Jefe de Compras"]);
  const psPorId = Object.fromEntries(requests.map(r => [r._id, r])); // Fix CI (2026-09-11)
  // Fix FV (2026-10-13): los filtros de Materiales; los PPS pendientes arriba y lo más nuevo primero.
  const fl = useFiltrosServicios(filtros);
  const approvedPS = fl.filtrar(requests.filter(r => r.status === "aprobada"), { sinEstado: true });
  const supplierOrders = sortPendingFirst(fl.filtrar(ppsTodos), pps => pps.status !== "cerrado");

  const handleGenPPS = async (psId) => {
    setSaving(psId);
    try {
      await axios.post(`${API}/purchases/service-supplier-orders`, { ps_id: psId }, { withCredentials: true });
      toast.success("PPS generado correctamente");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al generar PPS"); }
    finally { setSaving(null); }
  };

  const handleDeletePPS = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/purchases/service-supplier-orders/${deleteTarget._id}`, { withCredentials: true });
      toast.success(`PPS ${deleteTarget.number} eliminado`);
      setDeleteTarget(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar PPS"); }
    finally { setDeleting(false); }
  };

  // BLOQUE 18 PARTE A — filtra ítems al exportar y registra trazabilidad si se excluyó algo.
  const handleConfirmPdfSelectionPPS = async (selectedIndexes, providerName) => {
    const pps = pdfSelectPPS;
    if (!pps) return;
    generatePPSPDF(pps, selectedIndexes);
    setPdfSelectPPS(null);
    const totalItems = (pps.items || []).length;
    const excludedCount = totalItems - selectedIndexes.length;
    if (excludedCount > 0) {
      try {
        await axios.post(`${API}/purchases/service-supplier-orders/${pps._id}/log-pdf-export`, {
          included_count: selectedIndexes.length,
          excluded_count: excludedCount,
          provider_name: providerName || "",
        }, { withCredentials: true });
      } catch { /* trazabilidad no bloqueante */ }
    }
  };

  const ppsByPS = Object.fromEntries(ppsTodos.map(o => [o.ps_id, o]));

  return (
    <div className="space-y-5">
      <BarraFiltrosServicios fl={fl} filtros={filtros} testIdPrefix="svc-ped-prov" searchPlaceholder="Buscar por N° PPS, PS, obra o servicio..."
        statusOptions={fl.opciones(ppsTodos, { etiquetas: STATUS_LABELS, fijos: ["pendiente", "en_analisis", "cerrado"] })} />
      <div className="bg-blue-50/70 rounded-xl border border-blue-100 p-4 text-xs text-blue-800">
        <p className="font-semibold mb-0.5">Pedido de Presupuesto de Servicio (PPS)</p>
        <p>Desde un PS aprobado, genera el documento que se envía a los proveedores/contratistas para solicitar cotizaciones.</p>
      </div>

      <div>
        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">PS Aprobados ({approvedPS.length})</h3>
        {loading ? <Skeleton className="h-24" /> : approvedPS.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200/60 p-8 text-center text-sm text-gray-400">No hay Pedidos de Servicio aprobados</div>
        ) : (
          <div className="space-y-3">
            {approvedPS.map(ps => {
              const pps = ppsByPS[ps._id];
              return (
                <div key={ps._id} className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm" data-testid="ped-prov-ps-card">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900 text-sm font-mono">{ps.number}</span>
                        <StatusBadge status={ps.status} />
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{ps.project_name} · {ps.items?.length || 0} ítem(s)</p>
                      <PSLinea ps={ps} />
                      {pps && <p className="text-xs text-blue-600 font-semibold mt-0.5">PPS generado: <span className="font-mono">{pps.number}</span></p>}
                    </div>
                    {canCreate && !pps && (
                      <button onClick={() => handleGenPPS(ps._id)} disabled={saving === ps._id} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-60" data-testid="gen-pps-btn">
                        {saving === ps._id ? <Loader2 size={13} className="animate-spin" /> : <Package size={13} />}
                        {saving === ps._id ? "Generando..." : "GEN PPS"}
                      </button>
                    )}
                    {pps && <span className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 px-2 py-1 rounded-lg font-mono">{pps.number}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {supplierOrders.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">PPS Generados ({supplierOrders.length})</h3>
          <div className="space-y-3">
            {supplierOrders.map(pps => (
              <div key={pps._id} className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm" data-testid="pps-card">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2"><span className="font-bold text-gray-900 text-sm font-mono">{pps.number}</span><StatusBadge status={pps.status} /></div>
                    <p className="text-xs text-gray-500 mt-0.5">{pps.project_name} · Origen: <span className="font-mono">{pps.ps_number}</span></p>
                    <p className="text-xs text-gray-400">{pps.items?.length || 0} ítem(s) · {formatDate(pps.created_at)}</p>
                    <PSLinea ps={psPorId[pps.ps_id]} />
                  </div>
                  <button onClick={() => setPdfSelectPPS(pps)} className="flex items-center gap-1.5 border border-[#2E6DA4]/30 text-[#2E6DA4] hover:bg-[#2E6DA4]/5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all" data-testid="download-pps-btn"><Download size={13} /> PDF</button>
                  {canDelete && (
                    <button onClick={() => setDeleteTarget(pps)} title="Eliminar PPS" className="flex items-center justify-center border border-red-200 text-red-500 hover:bg-red-50 px-2.5 py-1.5 rounded-xl transition-all ml-2" data-testid={`delete-pps-btn-${pps._id}`}><Trash2 size={13} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal confirmar eliminación de PPS (BLOQUE 6 PARTE C.2) */}
      <Modal open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} title="Eliminar Pedido a Proveedores de Servicio">
        {deleteTarget && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
              <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                ¿Confirmás eliminar el PPS <span className="font-mono font-bold">{deleteTarget.number}</span>? Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleting}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium disabled:opacity-60">Cancelar</button>
              <button type="button" onClick={handleDeletePPS} disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="confirm-delete-pps-btn">
                {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                {deleting ? "Eliminando..." : "Eliminar"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal BLOQUE 18 PARTE A — seleccionar ítems para el PDF del PPS */}
      <SelectItemsForPdfModal
        open={!!pdfSelectPPS}
        onClose={() => setPdfSelectPPS(null)}
        items={pdfSelectPPS?.items || []}
        itemLabel={(it) => `${it.description || it.item_name || "—"} (${it.quantity ?? "—"} ${it.unit || ""})`}
        onConfirm={handleConfirmPdfSelectionPPS}
      />
    </div>
  );
}

// ==================== TAB: ANÁLISIS PRESUPUESTO (APS) ====================

function AnalisisTab({ supplierOrders, budgetActs: actsTodos, loading, onRefresh, userRole, requests = [], filtros }) {
  const [activeAPS, setActiveAPS]     = useState(null);
  // Fix CI (2026-09-11): pedido de origen de cada PPS/APS y "Modificar Pedido" como en materiales.
  const psPorId = useMemo(() => Object.fromEntries(requests.map(r => [r._id, r])), [requests]);
  const [modifyItems, setModifyItems] = useState(null);
  const { user } = useAuth();
  const [saving, setSaving]           = useState(false);
  const [creatingAPS, setCreatingAPS] = useState(null);
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [newProviderName, setNewProviderName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Fix HB (2026-10-20): "en compras de servicios necesitamos también los rangos de aprobación" (CEN).
  const [rangosAprob, setRangosAprob] = useState(null);
  const [aprobandoAPS, setAprobandoAPS] = useState(false);
  useEffect(() => {
    axios.get(`${API}/settings/rangos-aprobacion-servicios`, { withCredentials: true })
      .then(({ data }) => setRangosAprob(data))
      .catch(() => setRangosAprob(null));
  }, []);
  // V3.4 — AI Analysis state
  const [aiModal, setAiModal] = useState({ open: false, providerIdx: 0, file: null, loading: false, results: null });
  const canEdit = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Compras"]);
  const canDelete = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Técnica", "Compras", "Jefe de Compras"]);
  // Fix EK (2026-09-27): "Ver" el análisis (abierto o cerrado) y su PDF, como en materiales.
  // El Residente de Obra no ve montos.
  const [soloVer, setSoloVer] = useState(false);
  const puedeEditar = canEdit && !soloVer;
  const canVerAPS = !roleAllowed(userRole, ["Residente de Obra"]);

  const handleDeleteAPS = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/purchases/service-budget-acts/${deleteTarget._id}`, { withCredentials: true });
      toast.success(`APS ${deleteTarget.number} eliminado`);
      setDeleteTarget(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar APS"); }
    finally { setDeleting(false); }
  };

  // Fix FV (2026-10-13): los filtros de Materiales; los APS abiertos arriba y lo más nuevo primero.
  const fl = useFiltrosServicios(filtros);
  const ppsWithoutAPS = fl.filtrar(supplierOrders.filter(pps => !actsTodos.find(a => a.pps_id === pps._id)), { sinEstado: true });
  const budgetActs = sortPendingFirst(fl.filtrar(actsTodos), act => act.status !== "cerrada");

  const handleCreateAPS = async (ppsId) => {
    setCreatingAPS(ppsId);
    try {
      const r = await axios.post(`${API}/purchases/service-budget-acts`, { pps_id: ppsId }, { withCredentials: true });
      toast.success("APS creado");
      setSoloVer(false);
      setActiveAPS(r.data);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setCreatingAPS(null); }
  };

  const openAPS = (act, ver = false) => {
    const copy = JSON.parse(JSON.stringify(act));
    setSoloVer(ver);
    setActiveAPS(copy);
  };

  // BLOQUE 58 PARTE A — columna "Días" solo para APS de categoría alquiler
  // (la mayoría de los alquileres se cotizan por tiempo, no por monto fijo).
  const isAlquiler = activeAPS?.categoria === "alquiler";

  // Fix EK (2026-09-27): "la línea editada debe seguir siendo imputada a los ítems asignados
  // al momento del Pedido Original ... al agregar un nuevo ítem, se agregue anexado a un ítem"
  // (CEN). El sub-ítem va debajo de su línea (1.1, 1.2…) y hereda su imputación.
  let nroP = 0, nroS = 0;
  const numerosAPS = (modifyItems || []).map(it => {
    if (it.sub_de_nombre !== undefined) { nroS += 1; return `${nroP}.${nroS}`; }
    nroP += 1; nroS = 0; return String(nroP);
  });
  const agregarSubAPS = (idx) => setModifyItems(prev => {
    const padre = prev[idx];
    let fin = idx + 1;
    while (fin < prev.length && prev[fin].sub_de_nombre !== undefined) fin++;
    const hereda = {};
    ["item_uid", "item_budget_label", "nivel_id", "nivel_label", "tarea_numero", "sector", "front", "lineas_ps"].forEach(k => { if (padre[k]) hereda[k] = padre[k]; });   // Fix FQ: lineas_ps
    const nuevo = {
      ...hereda, item_name: "", unit: padre.unit || (isAlquiler ? "día" : "un"), quantity: 1,
      prices: (activeAPS.provider_columns || []).map(() => ({ unit_price: 0, obs: "", dias: 1 })), selected_idx: -1,
      sub_de_nombre: padre.item_name || "",
    };
    return [...prev.slice(0, fin), nuevo, ...prev.slice(fin)];
  });

  const addProvider = () => {
    if (!activeAPS) return;
    const trimmed = newProviderName.trim();
    if (!trimmed) { toast.error("Ingrese un nombre de proveedor"); return; }
    if ((activeAPS.provider_columns || []).includes(trimmed)) { toast.error("Proveedor ya existe"); return; }
    const cols = [...(activeAPS.provider_columns || []), trimmed];
    const items = (activeAPS.analysis_items || []).map(item => ({
      ...item, prices: [...(item.prices || []), { unit_price: 0, obs: "", dias: 1 }]
    }));
    setActiveAPS({ ...activeAPS, provider_columns: cols, analysis_items: items });
    setNewProviderName("");
    setShowAddProvider(false);
  };

  const updatePrice = (itemIdx, colIdx, field, val) => {
    const items = JSON.parse(JSON.stringify(activeAPS.analysis_items));
    if (!items[itemIdx].prices[colIdx]) items[itemIdx].prices[colIdx] = { unit_price: 0, obs: "", dias: 1 };
    items[itemIdx].prices[colIdx][field] = (field === "unit_price" || field === "dias") ? (Number(val) || 0) : val;
    setActiveAPS({ ...activeAPS, analysis_items: items });
  };

  // Fix CI (2026-09-11): UM de tiempo del alquiler. "Por período" es precio cerrado: tiempo 1.
  const updateUnidadTiempo = (itemIdx, unit) => {
    const items = JSON.parse(JSON.stringify(activeAPS.analysis_items));
    items[itemIdx].unit = unit;
    if (esPorPeriodo(unit)) items[itemIdx].prices = (items[itemIdx].prices || []).map(p => ({ ...(p || {}), dias: 1 }));
    setActiveAPS({ ...activeAPS, analysis_items: items });
  };

  // Fix CI (2026-09-11): "que se pueda modificar también el pedido en esta instancia como en
  // análisis de compra de materiales" (Sergio, 11/09). Corrige, agrega o quita ítems del APS
  // (con sus precios) y deja registro de quién lo hizo.
  const abrirModificar = () => setModifyItems((activeAPS.analysis_items || []).map((it, k) => ({ ...it, _orig: k })));
  const guardarModificacion = async () => {
    if (modifyItems.some(it => !(it.item_name || "").trim())) { toast.error("Complete la descripción de todos los ítems"); return; }
    const antes = activeAPS.analysis_items || [];
    const quedan = modifyItems.filter(it => it._orig != null);
    const modificados = quedan.filter(it => {
      const o = antes[it._orig] || {};
      return o.item_name !== it.item_name || o.unit !== it.unit || Number(o.quantity) !== Number(it.quantity);
    });
    const entrada = {
      by: user?.name || "", at: new Date().toISOString(),
      items_added: modifyItems.length - quedan.length,
      items_removed: antes.length - quedan.length,
      items_modified: modificados.length,
      added_names: modifyItems.filter(it => it._orig == null).map(it => it.item_name.trim() + (it.sub_de_nombre ? ` (sub-ítem de ${it.sub_de_nombre})` : "")),
      removed_names: antes.filter((_, k) => !quedan.some(it => it._orig === k)).map(it => it.item_name),
      modified_names: modificados.map(it => it.item_name),
    };
    const analysis_items = modifyItems.map(({ _orig, ...it }) => ({ ...it, quantity: Number(it.quantity) || 0 }));
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/purchases/service-budget-acts/${activeAPS._id}`,
        { provider_columns: activeAPS.provider_columns, analysis_items, notes: activeAPS.notes || "", modification_log: [...(activeAPS.modification_log || []), entrada] },
        { withCredentials: true });
      setActiveAPS(JSON.parse(JSON.stringify(data)));
      setModifyItems(null);
      toast.success("Pedido modificado");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al modificar el pedido"); }
    finally { setSaving(false); }
  };

  const selectWinner = (itemIdx, colIdx) => {
    const items = JSON.parse(JSON.stringify(activeAPS.analysis_items));
    items[itemIdx].selected_idx = items[itemIdx].selected_idx === colIdx ? -1 : colIdx;
    setActiveAPS({ ...activeAPS, analysis_items: items });
  };

  const selectedTotal = (activeAPS?.analysis_items || []).reduce((sum, item) => {
    const idx = item.selected_idx;
    if (idx >= 0 && item.prices?.[idx]?.unit_price) {
      const dias = isAlquiler ? (item.prices[idx].dias || 1) : 1;
      return sum + item.quantity * item.prices[idx].unit_price * dias;
    }
    return sum;
  }, 0);

  // V3.4 — AI Analysis handlers
  const openAIModal = () => setAiModal({ open: true, providerIdx: 0, file: null, loading: false, results: null });
  const handleAIFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp", "application/pdf"];
    if (!allowed.includes(file.type)) { toast.error("Solo PDF, PNG, JPG o WEBP"); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("Máximo 20 MB"); return; }
    setAiModal(prev => ({ ...prev, file, results: null }));
  };
  const handleAIAnalyze = () => {
    if (!aiModal.file) { toast.error("Cargue un documento primero"); return; }
    setAiModal(prev => ({ ...prev, loading: true, results: null }));
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const res = await axios.post(
          `${API}/purchases/service-budget-acts/${activeAPS._id}/ai-analyze`,
          { file_data: ev.target.result, file_name: aiModal.file.name, file_type: aiModal.file.type },
          { withCredentials: true }
        );
        setAiModal(prev => ({ ...prev, loading: false, results: res.data }));
      } catch (err) {
        toast.error(err.response?.data?.detail || "Error al analizar");
        setAiModal(prev => ({ ...prev, loading: false }));
      }
    };
    reader.readAsDataURL(aiModal.file);
  };
  const handleApplyAIPrices = () => {
    const { providerIdx, results } = aiModal;
    if (!results?.precios_extraidos?.length) { toast.error("No hay precios para aplicar"); return; }
    let applied = 0;
    const items = JSON.parse(JSON.stringify(activeAPS.analysis_items));
    results.precios_extraidos.forEach(extracted => {
      const idx = _resolveAiTargetIdx(extracted, items);
      if (idx >= 0 && idx < items.length && extracted.precio_unitario != null) {
        const prices = [...(items[idx].prices || [])];
        prices[providerIdx] = { ...(prices[providerIdx] || { unit_price: 0, obs: "" }), unit_price: extracted.precio_unitario };
        items[idx] = { ...items[idx], prices };
        applied++;
      }
    });
    setActiveAPS({ ...activeAPS, analysis_items: items });
    toast.success(`${applied} precio(s) aplicados a "${activeAPS.provider_columns?.[providerIdx]}"`);
    setAiModal({ open: false, providerIdx: 0, file: null, loading: false, results: null });
  };

  const handleSave = async () => {
    if (!activeAPS) return;
    setSaving(true);
    try {
      await axios.put(`${API}/purchases/service-budget-acts/${activeAPS._id}`, { provider_columns: activeAPS.provider_columns, analysis_items: activeAPS.analysis_items, notes: activeAPS.notes || "" }, { withCredentials: true });
      toast.success("APS guardado");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  // Fix HB (2026-10-20): aprobar el análisis por su rango de monto. En el editor se guarda antes, porque se
  // aprueba lo guardado; desde «Ver» se aprueba tal como está.
  const aprobarAPS = async () => {
    if (!activeAPS) return;
    setAprobandoAPS(true);
    try {
      if (puedeEditar) {
        await axios.put(`${API}/purchases/service-budget-acts/${activeAPS._id}`,
          { provider_columns: activeAPS.provider_columns, analysis_items: activeAPS.analysis_items, notes: activeAPS.notes || "" }, { withCredentials: true });
      }
      const { data } = await axios.post(`${API}/purchases/service-budget-acts/${activeAPS._id}/aprobar`, {}, { withCredentials: true });
      setActiveAPS(a => ({ ...a, aprobaciones_ap: data.aprobaciones_ap }));
      toast.success("Aprobación registrada");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al aprobar"); }
    finally { setAprobandoAPS(false); }
  };

  const handleClose = async () => {
    if (!activeAPS) return;
    const anySelected = activeAPS.analysis_items?.some(i => i.selected_idx >= 0);
    if (!anySelected) { toast.error("Seleccione al menos un proveedor ganador por ítem"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/purchases/service-budget-acts/${activeAPS._id}/close`, { provider_columns: activeAPS.provider_columns, analysis_items: activeAPS.analysis_items, notes: activeAPS.notes || "" }, { withCredentials: true });
      toast.success("APS cerrado correctamente");
      setActiveAPS(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al cerrar APS"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <BarraFiltrosServicios fl={fl} filtros={filtros} testIdPrefix="svc-analisis" searchPlaceholder="Buscar por N° APS, PPS, obra o servicio..."
        statusOptions={fl.opciones(actsTodos, { etiquetas: STATUS_LABELS, fijos: ["abierta", "cerrada"] })} />
      {/* PPS disponibles para abrir APS */}
      {canEdit && ppsWithoutAPS.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">PPS Disponibles para Análisis ({ppsWithoutAPS.length})</h3>
          <div className="space-y-2">
            {ppsWithoutAPS.map(pps => (
              <div key={pps._id} className="bg-white rounded-xl border border-gray-200/60 p-3 shadow-sm flex items-center justify-between" data-testid="aps-pps-item">
                <div><span className="font-bold text-sm font-mono text-gray-800">{pps.number}</span><span className="text-xs text-gray-500 ml-2">{pps.project_name} · {pps.items?.length || 0} ítems</span><PSLinea ps={psPorId[pps.ps_id]} /></div>
                <button onClick={() => handleCreateAPS(pps._id)} disabled={creatingAPS === pps._id} className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded-xl text-xs font-semibold transition-all disabled:opacity-60" data-testid="create-aps-btn">
                  {creatingAPS === pps._id ? <Loader2 size={12} className="animate-spin" /> : <BarChart2 size={12} />}Abrir APS
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista de APS existentes */}
      {budgetActs.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">Actas de Presupuesto de Servicio ({budgetActs.length})</h3>
          <div className="space-y-3">
            {budgetActs.map(act => (
              <div key={act._id} className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm" data-testid="aps-card">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2"><span className="font-bold text-gray-900 text-sm font-mono">{act.number}</span><StatusBadge status={act.status} /></div>
                    <p className="text-xs text-gray-500 mt-0.5">{act.project_name} · PPS: <span className="font-mono">{act.pps_number}</span></p>
                    <PSLinea ps={psPorId[act.ps_id]} />
                    {act.status === "cerrada" && <p className="text-xs font-semibold text-emerald-600 mt-0.5">Total seleccionado: {formatPYG(act.selected_total || 0)}</p>}
                    {act.pf_generated && <p className="text-xs text-[#2E6DA4] mt-0.5">{act.pf_count || 0} PFS generado(s)</p>}
                    {act.status !== "cerrada" && <ChipAprobacionAP servicios act={act} cfg={rangosAprob} />}
                  </div>
                  {canVerAPS && (
                    <div className="flex items-center gap-2 ml-auto">
                      <button onClick={() => openAPS(act, true)} className="flex items-center gap-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 px-3 py-1.5 rounded-xl text-xs font-semibold" data-testid={`ver-aps-btn-${act._id}`}>
                        <Eye size={12} /> Ver
                      </button>
                      <button onClick={() => generateAPSPDF(act)} className="flex items-center gap-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 px-3 py-1.5 rounded-xl text-xs font-semibold" data-testid={`pdf-aps-btn-${act._id}`}>
                        <Download size={12} /> PDF
                      </button>
                    </div>
                  )}
                  {canEdit && act.status === "abierta" && (
                    <button onClick={() => openAPS(act)} className="flex items-center gap-2 border border-violet-300 text-violet-700 hover:bg-violet-50 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all" data-testid="edit-aps-btn">
                      <Pencil size={12} /> Editar
                    </button>
                  )}
                  {canDelete && act.status === "abierta" && (
                    <button onClick={() => setDeleteTarget(act)} title="Eliminar APS" className="flex items-center justify-center border border-red-200 text-red-500 hover:bg-red-50 px-2.5 py-1.5 rounded-xl transition-all" data-testid={`delete-aps-btn-${act._id}`}><Trash2 size={13} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Editor de APS */}
      {activeAPS && (
        <Modal open={!!activeAPS} onClose={() => setActiveAPS(null)} title={`Análisis de Presupuesto: ${activeAPS.number}`} extraWide>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-gray-500">{activeAPS.project_name} · PPS: {activeAPS.pps_number}</p>
                  {isAlquiler && <span className="text-[10px] font-bold bg-orange-50 text-orange-600 border border-orange-200 px-2 py-0.5 rounded-full uppercase" data-testid="aps-alquiler-badge">Alquiler</span>}
                </div>
                {/* Fix CI (2026-09-11): "que se sigan mostrando los datos del periodo de tiempo" (Sergio, 11/09). */}
                <PSLinea ps={psPorId[activeAPS.ps_id]} />
                {(activeAPS.modification_log || []).length > 0 && (() => {
                  const m = activeAPS.modification_log[activeAPS.modification_log.length - 1];
                  return <p className="text-[11px] text-amber-700 mt-0.5" data-testid="aps-modify-log">Pedido modificado por {m.by || "—"} el {formatDate(m.at)} · {m.items_added} agregado(s), {m.items_removed} quitado(s), {m.items_modified} corregido(s)</p>;
                })()}
                {selectedTotal > 0 && <p className="text-sm font-bold text-emerald-600 mt-0.5">Total Seleccionado: {formatPYG(selectedTotal)}</p>}
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <button onClick={() => generateAPSPDF(activeAPS)} className="flex items-center gap-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 px-3 py-1.5 rounded-xl text-xs font-semibold" data-testid="aps-pdf-btn">
                  <Download size={13} /> PDF
                </button>
                {puedeEditar && (<>
                  <button onClick={abrirModificar} className="flex items-center gap-2 border border-dashed border-amber-400 text-amber-600 hover:bg-amber-50 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all" data-testid="aps-modify-pedido-btn">
                    <Pencil size={13} /> Modificar Pedido
                  </button>
                  {showAddProvider ? (
                    <>
                      <input
                        autoFocus
                        value={newProviderName}
                        onChange={e => setNewProviderName(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && addProvider()}
                        placeholder="Nombre del proveedor..."
                        className="border border-violet-300 rounded-xl px-3 py-1.5 text-xs focus:ring-2 focus:ring-violet-200 focus:outline-none w-48"
                        data-testid="new-provider-name-input"
                      />
                      <button onClick={addProvider} className="bg-violet-600 text-white px-3 py-1.5 rounded-xl text-xs font-semibold" data-testid="confirm-add-provider-btn">+</button>
                      <button onClick={() => { setShowAddProvider(false); setNewProviderName(""); }} className="border border-gray-300 text-gray-500 px-3 py-1.5 rounded-xl text-xs">✕</button>
                    </>
                  ) : (
                    <button onClick={() => setShowAddProvider(true)} className="flex items-center gap-2 border border-dashed border-violet-400 text-violet-600 hover:bg-violet-50 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all" data-testid="add-provider-col-btn">
                      <Plus size={13} /> Agregar Proveedor
                    </button>
                  )}
                  {(activeAPS.provider_columns || []).length > 0 && (
                    <button onClick={openAIModal} className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded-xl text-xs font-semibold transition-all shadow-sm" data-testid="aps-ai-analyze-btn">
                      <Brain size={13} /> Analizar con IA
                    </button>
                  )}
                </>)}
              </div>
            </div>

            {/* Fix HB (2026-10-20): aprobación por rango de monto, como en el análisis de materiales. */}
            {activeAPS.status !== "cerrada" && (
              <AprobacionAP servicios act={activeAPS} cfg={rangosAprob} onAprobar={aprobarAPS} trabajando={aprobandoAPS} />
            )}

            {(activeAPS.provider_columns || []).length === 0 ? (
              <div className="bg-amber-50 rounded-xl p-4 text-xs text-amber-700 border border-amber-100">Agregue proveedores/contratistas para comenzar el análisis comparativo</div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                <table className="text-xs border-collapse" style={{ minWidth: "100%" }}>
                  <thead>
                    <tr className="bg-[#1E3A5F] text-white">
                      <th className="text-left px-3 py-2.5 font-semibold min-w-[160px] sticky left-0 bg-[#1E3A5F] z-10">Servicio</th>
                      <th className="px-2 py-2.5 font-semibold text-center w-12">UM</th>
                      <th className="px-2 py-2.5 font-semibold text-center w-14">Cant.</th>
                      {(activeAPS.provider_columns || []).map((col, ci) => (
                        <th key={ci} colSpan={isAlquiler ? 5 : 4} className="px-2 py-2.5 font-semibold text-center border-l border-white/20 min-w-[280px]">{col}</th>
                      ))}
                    </tr>
                    <tr className="bg-[#2E6DA4]/80 text-blue-100 text-[10px]">
                      <th className="sticky left-0 bg-[#2E6DA4]/80 z-10 px-3 py-1"></th>
                      <th></th>
                      <th></th>
                      {(activeAPS.provider_columns || []).map((_, ci) => (
                        <th key={ci} colSpan={isAlquiler ? 5 : 4} className="border-l border-white/10">
                          <div className={`grid text-center ${isAlquiler ? "grid-cols-5" : "grid-cols-4"}`}>
                            <span className="py-1 px-2 border-r border-white/10">P.U.</span>
                            {isAlquiler && <span className="py-1 px-2 border-r border-white/10">Tiempo</span>}
                            <span className="py-1 px-2 border-r border-white/10">Subtotal</span>
                            <span className="py-1 px-2 border-r border-white/10">Obs.</span>
                            <span className="py-1 px-1">Sel.</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(activeAPS.analysis_items || []).map((item, ii) => {
                      const selectedIdx = item.selected_idx ?? -1;
                      return (
                        <tr key={ii} className={ii % 2 === 0 ? "bg-white" : "bg-gray-50/60"}>
                          <td className="px-3 py-2 font-medium text-gray-800 sticky left-0 bg-inherit">
                            {item.item_name}
                            {item.sub_de_nombre && <span className="block text-[9px] font-normal text-sky-700" data-testid={`aps-sub-${ii}`}>Sub-ítem de «{item.sub_de_nombre}»</span>}
                            {imputacionTexto(item) && <span className="block text-[9px] font-normal text-gray-400" data-testid={`aps-imputacion-${ii}`}>{imputacionTexto(item)}</span>}
                          </td>
                          <td className="px-2 py-2 text-gray-500 text-center">
                            {isAlquiler && puedeEditar ? (
                              <select value={item.unit || ""} onChange={e => updateUnidadTiempo(ii, e.target.value)}
                                className="border border-orange-200 bg-orange-50/40 rounded-lg px-1 py-1 text-[11px]" data-testid={`aps-um-${ii}`}>
                                {!UM_TIEMPO.some(([v]) => v === item.unit) && <option value={item.unit || ""}>{item.unit || "—"}</option>}
                                {UM_TIEMPO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                              </select>
                            ) : item.unit}
                          </td>
                          <td className="px-2 py-2 text-center text-gray-600 font-semibold">{item.quantity}</td>
                          {(activeAPS.provider_columns || []).map((_, ci) => {
                            const priceObj = item.prices?.[ci] || {};
                            const price = priceObj.unit_price || 0;
                            const dias = isAlquiler ? (priceObj.dias || 1) : 1;
                            const subtotal = price * (item.quantity || 1) * dias;
                            const isWinner = selectedIdx === ci;
                            return (
                              <td key={ci} colSpan={isAlquiler ? 5 : 4} className={`border-l border-gray-100 p-0 ${isWinner ? "bg-emerald-50" : ""}`}>
                                <div className={`grid ${isAlquiler ? "grid-cols-5" : "grid-cols-4"}`}>
                                  <div className="px-1.5 py-1.5">
                                    <input
                                      type="number" min="0" value={price || ""} placeholder="0"
                                      onChange={e => puedeEditar && updatePrice(ii, ci, "unit_price", e.target.value)}
                                      readOnly={!puedeEditar}
                                      className={`w-full border rounded-lg px-2 py-1.5 text-right text-xs focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none ${isWinner ? "border-emerald-300 bg-emerald-50/80 font-semibold" : "border-gray-200 bg-white"}`}
                                      data-testid={`aps-price-${ii}-${ci}`}
                                    />
                                    {/* Fix O (2026-08-31): semáforo de precios para mano de obra,
                                        punto 7 de la carta. Mismo criterio que el de materiales:
                                        verde por debajo del costo presupuestado, ámbar si coincide
                                        dentro del 1%, rojo por encima, con el porcentaje al lado.
                                        Es informativo, no bloquea nada. */}
                                    {item.ref_precio_mo > 0 && price > 0 && (() => {
                                      const d = ((price - item.ref_precio_mo) / item.ref_precio_mo) * 100;
                                      const cls = d < -1 ? "text-emerald-600" : d > 1 ? "text-red-600" : "text-amber-600";
                                      return (
                                        <p className={`text-[9px] font-semibold mt-0.5 text-right ${cls}`}
                                           title={`Costo de mano de obra presupuestado: ${formatPYG(item.ref_precio_mo)}`}
                                           data-testid={`aps-semaforo-${ii}-${ci}`}>
                                          {d > 0 ? "+" : ""}{d.toFixed(1)}% vs ppto
                                        </p>
                                      );
                                    })()}
                                  </div>
                                  {isAlquiler && (
                                    <div className="px-1.5 py-1.5">
                                      {esPorPeriodo(item.unit) ? (
                                        <p className="text-[10px] text-orange-600 font-semibold text-center py-1.5" data-testid={`aps-dias-${ii}-${ci}`}>Período</p>
                                      ) : (<>
                                        <input
                                          type="number" min="1" value={priceObj.dias ?? 1} placeholder="1"
                                          onChange={e => puedeEditar && updatePrice(ii, ci, "dias", e.target.value)}
                                          readOnly={!puedeEditar}
                                          className={`w-full border rounded-lg px-2 py-1.5 text-right text-xs focus:ring-2 focus:ring-orange-200 focus:outline-none ${isWinner ? "border-emerald-300 bg-emerald-50/80 font-semibold" : "border-orange-200 bg-orange-50/40"}`}
                                          data-testid={`aps-dias-${ii}-${ci}`}
                                        />
                                        <p className="text-[9px] text-orange-500 text-center mt-0.5">{tiempoUnidad(item.unit)}</p>
                                      </>)}
                                    </div>
                                  )}
                                  <div className={`px-2 py-2 text-right font-medium text-xs flex items-center justify-end border-r border-gray-100 ${isWinner ? "text-emerald-700 font-bold" : "text-gray-600"}`}>
                                    {subtotal > 0 ? formatPYG(subtotal) : "—"}
                                  </div>
                                  <div className="px-1.5 py-1.5">
                                    <input
                                      type="text" value={priceObj.obs || ""} placeholder="Obs."
                                      onChange={e => puedeEditar && updatePrice(ii, ci, "obs", e.target.value)}
                                      readOnly={!puedeEditar}
                                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none bg-white"
                                      data-testid={`aps-obs-${ii}-${ci}`}
                                    />
                                  </div>
                                  <div className="px-1 py-1.5 flex items-center justify-center">
                                    <button
                                      onClick={() => puedeEditar && selectWinner(ii, ci)}
                                      title={`Seleccionar ${(activeAPS.provider_columns || [])[ci]}`}
                                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${isWinner ? "bg-emerald-500 border-emerald-500 shadow-sm" : "bg-white border-gray-300 hover:border-emerald-400"}`}
                                      data-testid={`sel-prov-${ii}-${ci}`}>
                                      {isWinner && <Check size={11} className="text-white" />}
                                    </button>
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 font-bold text-xs border-t-2 border-gray-300">
                      <td colSpan={3} className="px-3 py-2.5 text-gray-600 sticky left-0 bg-gray-100">TOTAL POR PROVEEDOR</td>
                      {(activeAPS.provider_columns || []).map((_, ci) => {
                        const total = (activeAPS.analysis_items || []).reduce((sum, item) => {
                          const dias = isAlquiler ? (item.prices?.[ci]?.dias || 1) : 1;
                          return sum + ((item.prices?.[ci]?.unit_price || 0) * (item.quantity || 1) * dias);
                        }, 0);
                        return (
                          <td key={ci} colSpan={isAlquiler ? 5 : 4} className="border-l border-gray-200">
                            <div className={`grid ${isAlquiler ? "grid-cols-5" : "grid-cols-4"}`}>
                              <div></div>
                              {isAlquiler && <div></div>}
                              <div className="px-2 py-2.5 text-right text-[#2E6DA4]">{formatPYG(total)}</div>
                              <div></div><div></div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {puedeEditar && (
              <div className="flex gap-3 pt-2 border-t border-gray-100">
                <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-xl text-sm font-medium disabled:opacity-60" data-testid="save-aps-btn">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Guardar
                </button>
                <button onClick={handleClose} disabled={saving || !activeAPS.analysis_items?.some(i => i.selected_idx >= 0)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all" data-testid="close-aps-btn">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <BadgeCheck size={14} />} Cerrar APS ({formatPYG(selectedTotal)})
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Fix CI (2026-09-11): Modificar Pedido desde el APS (como en el análisis de materiales). */}
      {modifyItems && activeAPS && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" data-testid="aps-modify-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !saving && setModifyItems(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="bg-amber-600 px-6 py-4 rounded-t-2xl flex items-center justify-between flex-shrink-0">
              <div className="text-white"><p className="font-bold text-sm">Modificar Ítems del Pedido</p><p className="text-amber-100 text-xs">{activeAPS.number} — agregar, quitar o corregir ítems</p></div>
              <button onClick={() => !saving && setModifyItems(null)} className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10"><X size={16} /></button>
            </div>
            <div className="p-5 overflow-y-auto space-y-3">
              <table className="w-full text-xs">
                <thead><tr className="bg-gray-100 text-gray-600 font-semibold"><th className="text-left px-2 py-2">#</th><th className="text-left px-2 py-2">Descripción</th><th className="px-2 py-2">UM</th><th className="px-2 py-2">Cant.</th><th className="w-8"></th></tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {modifyItems.map((it, idx) => (
                    <tr key={idx} className={it._orig == null ? "bg-amber-50/40" : ""}>
                      <td className="px-2 py-1.5 text-gray-400" data-testid={`aps-modify-nro-${idx}`}>{it._orig == null && it.sub_de_nombre === undefined ? <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold text-[9px]">NUEVO</span> : numerosAPS[idx]}</td>
                      <td className="px-1 py-1.5"><input value={it.item_name || ""} onChange={e => setModifyItems(prev => prev.map((x, k) => (k === idx ? { ...x, item_name: e.target.value } : x)))} placeholder="Descripción del ítem" className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs" data-testid={`aps-modify-nombre-${idx}`} />
                        {it.sub_de_nombre !== undefined && <p className="text-[10px] text-sky-700 mt-0.5 pl-1" data-testid={`aps-modify-sub-${idx}`}>Sub-ítem de «{it.sub_de_nombre}» · misma imputación</p>}
                      </td>
                      <td className="px-1 py-1.5">
                        {isAlquiler ? (
                          <select value={it.unit || ""} onChange={e => setModifyItems(prev => prev.map((x, k) => (k === idx ? { ...x, unit: e.target.value, prices: esPorPeriodo(e.target.value) ? (x.prices || []).map(p => ({ ...(p || {}), dias: 1 })) : x.prices } : x)))} className="border border-gray-200 rounded-lg px-1 py-1.5 text-xs" data-testid={`aps-modify-um-${idx}`}>
                            {!UM_TIEMPO.some(([v]) => v === it.unit) && <option value={it.unit || ""}>{it.unit || "—"}</option>}
                            {UM_TIEMPO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        ) : (
                          <input value={it.unit || ""} onChange={e => setModifyItems(prev => prev.map((x, k) => (k === idx ? { ...x, unit: e.target.value } : x)))} className="w-20 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-center" data-testid={`aps-modify-um-${idx}`} />
                        )}
                      </td>
                      <td className="px-1 py-1.5"><input type="number" min="0" value={it.quantity} onChange={e => setModifyItems(prev => prev.map((x, k) => (k === idx ? { ...x, quantity: e.target.value } : x)))} className="w-20 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-center" data-testid={`aps-modify-cant-${idx}`} /></td>
                      <td className="px-1 text-center whitespace-nowrap">
                        {it._orig != null && it.sub_de_nombre === undefined && (
                          <button onClick={() => agregarSubAPS(idx)} title="Agregar un sub-ítem con la misma imputación"
                            className="px-1.5 py-0.5 mr-1 text-[10px] font-bold text-sky-700 border border-sky-300 rounded hover:bg-sky-50" data-testid={`aps-modify-sub-btn-${idx}`}>+ Sub</button>
                        )}
                        <button onClick={() => setModifyItems(prev => prev.filter((_, k) => k !== idx))} className="p-1 text-red-400 hover:text-red-600" data-testid={`aps-modify-quitar-${idx}`}><X size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {modifyItems.some(it => it.item_uid || it.lineas_ps) ? (   /* Fix FQ */
                <p className="text-[11px] text-sky-700" data-testid="aps-modify-sub-hint">Para agregar un servicio, usá «+ Sub» en la línea a la que corresponde: queda como sub-ítem con su misma imputación.</p>
              ) : (
              <button onClick={() => setModifyItems(prev => [...prev, { item_name: "", unit: isAlquiler ? "día" : "un", quantity: 1, sector: "", front: "", prices: (activeAPS.provider_columns || []).map(() => ({ unit_price: 0, obs: "", dias: 1 })), selected_idx: -1 }])}
                className="flex items-center gap-1.5 text-xs text-amber-600 font-semibold border border-dashed border-amber-300 px-3 py-2 rounded-lg hover:bg-amber-50" data-testid="aps-modify-agregar">
                <Plus size={12} /> Agregar nuevo ítem
              </button>
              )}
            </div>
            <div className="flex gap-3 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setModifyItems(null)} disabled={saving} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
              <button onClick={guardarModificacion} disabled={saving} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="aps-modify-guardar">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Guardar modificaciones
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* V3.4 — Modal Analizar con IA */}
      {aiModal.open && activeAPS && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !aiModal.loading && setAiModal({ open: false, providerIdx: 0, file: null, loading: false, results: null })} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col">
            <div className="bg-violet-600 px-6 py-4 rounded-t-2xl flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2.5 text-white">
                <Brain size={18} />
                <div>
                  <p className="font-bold text-sm">Analizar Cotización de Servicio con IA</p>
                  <p className="text-violet-100 text-xs">Suba el PDF/imagen del contratista y la IA extraerá los precios</p>
                </div>
              </div>
              <button onClick={() => !aiModal.loading && setAiModal({ open: false, providerIdx: 0, file: null, loading: false, results: null })} className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-all"><X size={16} /></button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Proveedor / Contratista (columna destino)</label>
                <select value={aiModal.providerIdx} onChange={e => setAiModal(p => ({ ...p, providerIdx: Number(e.target.value) }))} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white" data-testid="aps-ai-provider-select">
                  {(activeAPS.provider_columns || []).map((col, idx) => <option key={idx} value={idx}>{col}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Archivo de cotización</label>
                <input type="file" accept="application/pdf,image/png,image/jpeg,image/jpg,image/webp" onChange={handleAIFileChange} className="w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-violet-50 file:text-violet-700 file:font-semibold hover:file:bg-violet-100" data-testid="aps-ai-file-input" />
                {aiModal.file && <p className="text-[10px] text-gray-500 mt-1">{aiModal.file.name} ({(aiModal.file.size / 1024).toFixed(0)} KB)</p>}
              </div>
              {!aiModal.results && (
                <button onClick={handleAIAnalyze} disabled={!aiModal.file || aiModal.loading} className="w-full bg-violet-600 hover:bg-violet-700 text-white py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="aps-ai-run-btn">
                  {aiModal.loading ? <Loader2 size={14} className="animate-spin" /> : <Brain size={14} />}
                  {aiModal.loading ? "Analizando..." : "Analizar Documento"}
                </button>
              )}
              {aiModal.results && (
                <div className="space-y-3">
                  <div className="bg-violet-50 rounded-xl p-3 border border-violet-100 text-xs space-y-1">
                    <p><strong>Proveedor detectado:</strong> {aiModal.results.proveedor_sugerido || "—"}</p>
                    <p><strong>Moneda:</strong> {aiModal.results.moneda_original || "PYG"}</p>
                    {aiModal.results.obs_general && <p className="text-gray-600 mt-1">{aiModal.results.obs_general}</p>}
                  </div>
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr><th className="px-2 py-2 text-left text-gray-600 font-semibold">Ítem (IA)</th><th className="px-2 py-2 text-left text-gray-600 font-semibold">Se aplicará a</th><th className="px-2 py-2 text-right text-gray-600 font-semibold">P.U.</th><th className="px-2 py-2 text-center text-gray-600 font-semibold">Confianza</th></tr>
                      </thead>
                      <tbody>
                        {(aiModal.results.precios_extraidos || []).map((p, i) => {
                          const targetIdx = _resolveAiTargetIdx(p, activeAPS?.analysis_items || []);
                          const targetItem = targetIdx >= 0 ? (activeAPS?.analysis_items || [])[targetIdx] : null;
                          const wasCorrected = targetIdx >= 0 && targetIdx !== p.item_index;
                          return (
                            <tr key={i} className="border-t border-gray-100" data-testid={`aps-ai-price-row-${i}`}>
                              <td className="px-2 py-1.5 text-gray-700">{p.item_name}</td>
                              <td className="px-2 py-1.5">
                                {targetItem ? (
                                  <span className={wasCorrected ? "text-amber-700 font-medium" : "text-gray-700"}>
                                    {targetItem.item_name}
                                    {wasCorrected && <span className="ml-1 text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold uppercase">corregido</span>}
                                  </span>
                                ) : <span className="text-gray-400">—</span>}
                              </td>
                              <td className="px-2 py-1.5 text-right text-gray-900 font-semibold">{p.precio_unitario ? formatPYG(p.precio_unitario) : "—"}</td>
                              <td className="px-2 py-1.5 text-center"><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${p.confianza === "alta" ? "bg-emerald-100 text-emerald-700" : p.confianza === "media" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{(p.confianza || "—").toUpperCase()}</span></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setAiModal(p => ({ ...p, results: null, file: null }))} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2 text-sm font-medium">Reintentar</button>
                    <button onClick={handleApplyAIPrices} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2 text-sm font-semibold flex items-center justify-center gap-2" data-testid="aps-ai-apply-btn"><Check size={13} /> Aplicar precios</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal confirmar eliminación de APS (BLOQUE 6 PARTE C.2) */}
      <Modal open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} title="Eliminar Análisis de Presupuesto de Servicio">
        {deleteTarget && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
              <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                ¿Confirmás eliminar el APS <span className="font-mono font-bold">{deleteTarget.number}</span>? Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleting}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium disabled:opacity-60">Cancelar</button>
              <button type="button" onClick={handleDeleteAPS} disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="confirm-delete-aps-btn">
                {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                {deleting ? "Eliminando..." : "Eliminar"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ==================== TAB: PRESUPUESTOS FINALES (PFS) ====================

function PtosFinalTab({ budgetActs, finalBudgets, loading, onRefresh, userRole, requests = [], filtros }) {
  const psPorId = Object.fromEntries(requests.map(r => [r._id, r])); // Fix CI (2026-09-11)
  const [editPFS, setEditPFS] = useState(null);
  const [editForm, setEditForm] = useState({ condicion_pago: "contado", notes: "" });
  const [saving, setSaving] = useState(false);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [expandedAnalysis, setExpandedAnalysis] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const canEdit = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Compras"]);
  const canDelete = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Técnica", "Compras", "Jefe de Compras"]);

  // Fix CI (2026-09-11): "En Ptos Finales debe ordenarse de manera que lo más actual siempre se
  // vea primero" (Sergio, 11/09): por fecha de cierre del APS, sin agrupar por urgencia.
  const closedAPSTodos = budgetActs.filter(a => a.status === "cerrada")
    .sort((a, b) => String(b.closed_at || b.created_at || "").localeCompare(String(a.closed_at || a.created_at || "")));
  // Fix FV (2026-10-13): los filtros de Materiales. El estado es el de sus PFS: sin generar, en borrador o con OTS.
  const fl = useFiltrosServicios(filtros);
  const estadoPFS = (act) => {
    const pfs = finalBudgets.filter(p => p.aps_id === act._id);
    return !pfs.length ? "sin_pfs" : pfs.some(p => p.status === "borrador") ? "borrador" : "ots_generada";
  };
  const closedAPS = fl.filtrar(closedAPSTodos, { estado: estadoPFS });

  const handleDeletePFS = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/purchases/service-final-budgets/${deleteTarget._id}`, { withCredentials: true });
      toast.success(`PFS ${deleteTarget.number} eliminado`);
      setDeleteTarget(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar PFS"); }
    finally { setDeleting(false); }
  };

  const handleGenPFS = async (apsId) => {
    setGeneratingFor(apsId);
    try {
      const r = await axios.post(`${API}/purchases/service-final-budgets/from-aps/${apsId}`, {}, { withCredentials: true });
      toast.success(`${r.data.length} PFS generado(s)`);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al generar PFS"); }
    finally { setGeneratingFor(null); }
  };

  const handleEditSave = async () => {
    if (!editPFS) return;
    setSaving(true);
    try {
      await axios.put(`${API}/purchases/service-final-budgets/${editPFS._id}`, editForm, { withCredentials: true });
      toast.success("PFS actualizado");
      setEditPFS(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const pfsGrouped = Object.fromEntries(closedAPS.map(a => [a._id, finalBudgets.filter(p => p.aps_id === a._id)]));

  return (
    <div className="space-y-5">
      <BarraFiltrosServicios fl={fl} filtros={filtros} testIdPrefix="svc-ptos-finales" searchPlaceholder="Buscar por N° APS, obra o servicio..."
        statusOptions={fl.opciones(closedAPSTodos, { estado: estadoPFS, etiquetas: { sin_pfs: "Sin PFS generados", borrador: "PFS en borrador", ots_generada: "Con OTS" }, fijos: ["sin_pfs", "borrador", "ots_generada"] })} />
      {loading ? <Skeleton className="h-32" /> : closedAPS.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200/60 p-12 text-center shadow-sm">
          <FileCheck size={44} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-500 font-medium">Sin APS cerrados</p>
          <p className="text-xs text-gray-400 mt-1">Cierra un Análisis de Presupuesto para generar los Presupuestos Finales</p>
        </div>
      ) : (
        <div className="space-y-4">
          {closedAPS.map(act => {
            const pfsList = pfsGrouped[act._id] || [];
            // V3.9 — compute provider rankings from the APS analysis
            const cols = act.provider_columns || [];
            const aitems = act.analysis_items || [];
            const providerTotals = cols.map((name, ci) => ({
              name,
              total: aitems.reduce((s, it) => s + ((it.prices?.[ci]?.unit_price || 0) * (it.quantity || 1) * (act.categoria === "alquiler" ? (it.prices?.[ci]?.dias || 1) : 1)), 0),
            }));
            const ranked = [...providerTotals].filter(p => p.total > 0).sort((a, b) => a.total - b.total);
            const winner = ranked[0];
            const isExpanded = !!expandedAnalysis[act._id];
            return (
              <div key={act._id} className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden" data-testid="pfs-aps-group">
                <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50/50">
                  <div>
                    <div className="flex items-center gap-2"><span className="font-bold text-gray-900 text-sm font-mono">{act.number}</span><StatusBadge status={act.status} /></div>
                    <p className="text-xs text-gray-500 mt-0.5">{act.project_name} · Total: {formatPYG(act.selected_total || 0)}</p>
                    <PSLinea ps={psPorId[act.ps_id]} />
                  </div>
                  <div className="flex items-center gap-2">
                    {ranked.length > 0 && (
                      <button onClick={() => setExpandedAnalysis(e => ({ ...e, [act._id]: !e[act._id] }))} className="flex items-center gap-1.5 border border-violet-200 text-violet-700 hover:bg-violet-50 px-3 py-1.5 rounded-xl text-xs font-medium transition-all" data-testid="toggle-aps-analysis-btn">
                        <BarChart2 size={12} /> {isExpanded ? "Ocultar" : "Ver"} análisis APS
                      </button>
                    )}
                    {canEdit && pfsList.length === 0 && (
                      <button onClick={() => handleGenPFS(act._id)} disabled={generatingFor === act._id} className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-60" data-testid="gen-pfs-btn">
                        {generatingFor === act._id ? <Loader2 size={13} className="animate-spin" /> : <FileCheck size={13} />} GEN PFS
                      </button>
                    )}
                  </div>
                </div>
                {/* V3.9 — Panel de Análisis del APS de origen (ranking de proveedores) */}
                {isExpanded && ranked.length > 0 && (
                  <div className="border-b border-gray-100 bg-violet-50/40 p-4" data-testid="aps-analysis-panel">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold text-violet-800 uppercase tracking-wide">Ranking de Proveedores (APS de origen)</p>
                      <span className="text-[10px] text-gray-500">{ranked.length} cotizante(s)</span>
                    </div>
                    <div className="space-y-1.5">
                      {ranked.map((p, idx) => {
                        const diff = winner && p.total > winner.total ? ((p.total - winner.total) / winner.total) * 100 : 0;
                        return (
                          <div key={idx} className={`flex items-center justify-between px-3 py-1.5 rounded-lg text-xs ${idx === 0 ? "bg-emerald-100 border border-emerald-200" : "bg-white border border-gray-200"}`}>
                            <div className="flex items-center gap-2">
                              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${idx === 0 ? "bg-emerald-600 text-white" : "bg-gray-200 text-gray-600"}`}>{idx + 1}</span>
                              <span className="font-semibold text-gray-800">{p.name}</span>
                              {idx === 0 && <span className="text-[9px] bg-emerald-600 text-white px-1.5 py-0.5 rounded font-bold">GANADOR</span>}
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-mono font-bold text-gray-900">{formatPYG(p.total)}</span>
                              {diff > 0 && <span className="text-[10px] text-red-600 font-semibold">+{diff.toFixed(1)}%</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-gray-500 mt-2">Total seleccionado en APS (suma de ítems ganadores): <strong>{formatPYG(act.selected_total || 0)}</strong></p>
                  </div>
                )}
                {pfsList.length > 0 && (
                  <div className="divide-y divide-gray-100">
                    {pfsList.map(pfs => (
                      <div key={pfs._id} className="p-4 flex items-center justify-between" data-testid="pfs-card">
                        <div>
                          <div className="flex items-center gap-2"><span className="font-bold text-gray-800 text-sm font-mono">{pfs.number}</span><StatusBadge status={pfs.status} /></div>
                          <p className="text-xs text-gray-500 mt-0.5">{pfs.provider_name} · Total: <strong>{formatPYG(pfs.total || 0)}</strong></p>
                          {pfs.condicion_pago && <span className="text-[10px] bg-cyan-50 text-cyan-700 border border-cyan-200 px-1.5 py-0.5 rounded font-semibold">{pfs.condicion_pago}</span>}
                          {pfs.ots_number && <span className="ml-2 text-[10px] bg-[#2E6DA4]/10 text-[#2E6DA4] border border-[#2E6DA4]/20 px-1.5 py-0.5 rounded font-mono">{pfs.ots_number}</span>}
                        </div>
                        {canEdit && pfs.status === "borrador" && (
                          <button onClick={() => { setEditPFS(pfs); setEditForm({ condicion_pago: pfs.condicion_pago || "contado", notes: pfs.notes || "" }); }} className="flex items-center gap-1.5 border border-[#2E6DA4]/30 text-[#2E6DA4] hover:bg-[#2E6DA4]/5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all" data-testid="edit-pfs-btn"><Pencil size={12} /> Cerrar Cond. Pago</button>
                        )}
                        {canDelete && pfs.status === "borrador" && (
                          <button onClick={() => setDeleteTarget(pfs)} title="Eliminar PFS" className="flex items-center justify-center border border-red-200 text-red-500 hover:bg-red-50 px-2.5 py-1.5 rounded-xl transition-all ml-2" data-testid={`delete-pfs-btn-${pfs._id}`}><Trash2 size={13} /></button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal open={!!editPFS} onClose={() => setEditPFS(null)} title={`Cerrar Condiciones de Pago — ${editPFS?.number}`}>
        <div className="space-y-4">
          <div><label className="block text-xs font-semibold text-gray-700 mb-1.5">Condición de Pago</label>
            <select value={editForm.condicion_pago} onChange={e => setEditForm(f => ({...f, condicion_pago: e.target.value}))} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white">
              {CONDICION_PAGO_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div><label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label><textarea value={editForm.notes} onChange={e => setEditForm(f => ({...f, notes: e.target.value}))} rows={2} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none" /></div>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setEditPFS(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
            <button onClick={handleEditSave} disabled={saving} className="flex-1 bg-[#2E6DA4] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="save-pfs-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}{saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal confirmar eliminación de PFS (BLOQUE 6 PARTE C.2) */}
      <Modal open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} title="Eliminar Presupuesto Final de Servicio">
        {deleteTarget && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
              <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                ¿Confirmás eliminar el PFS <span className="font-mono font-bold">{deleteTarget.number}</span>? Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleting}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium disabled:opacity-60">Cancelar</button>
              <button type="button" onClick={handleDeletePFS} disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="confirm-delete-pfs-btn">
                {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                {deleting ? "Eliminando..." : "Eliminar"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ==================== TAB: ÓRDENES DE SERVICIO (OTS) ====================

function OrdenesTab({ finalBudgets, svcOrders: otsTodas, loading, onRefresh, userRole, requests = [], filtros }) {
  const psPorId = Object.fromEntries(requests.map(r => [r._id, r])); // Fix CI (2026-09-11)
  const [selectedPFs, setSelectedPFs] = useState([]);
  const [genForm, setGenForm] = useState({ condicion_pago: "contado", notes: "" });
  const [viewOTS, setViewOTS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const canCreate = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Compras"]);
  const canApprove = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico"]);

  // Fix FV (2026-10-13): los filtros de Materiales; las OTS por aprobar arriba y lo más nuevo primero.
  const fl = useFiltrosServicios(filtros);
  const availablePFs = fl.filtrar(finalBudgets.filter(pf => pf.status === "borrador"), { sinEstado: true });
  const svcOrders = sortPendingFirst(fl.filtrar(otsTodas), o => o.status === "pendiente_aprobacion");
  const togglePF = (id) => {
    setSelectedPFs(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      if (!prev.includes(id)) {
        const pf = finalBudgets.find(p => p._id === id);
        if (pf?.condicion_pago) setGenForm(f => ({...f, condicion_pago: pf.condicion_pago}));
      }
      return next;
    });
  };

  const handleGenOTS = async () => {
    if (selectedPFs.length === 0) { toast.error("Seleccione al menos un PFS"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/purchases/service-orders/generate`, { pf_ids: selectedPFs, ...genForm }, { withCredentials: true });
      toast.success(`${r.data.length} OTS generada(s)`);
      setSelectedPFs([]);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al generar OTS"); }
    finally { setSaving(false); }
  };

  const handleApprove = async (id) => {
    setApprovingId(id);
    try {
      await axios.put(`${API}/purchases/service-orders/${id}/approve`, {}, { withCredentials: true });
      toast.success("OTS aprobada");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setApprovingId(null); }
  };

  return (
    <div className="space-y-5">
      <BarraFiltrosServicios fl={fl} filtros={filtros} testIdPrefix="svc-ordenes" searchPlaceholder="Buscar por N° OTS, obra, proveedor o servicio..."
        statusOptions={fl.opciones(otsTodas, { etiquetas: STATUS_LABELS, fijos: ["pendiente_aprobacion", "aprobada", "certificada", "facturada"] })} />
      {canCreate && availablePFs.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200/60 p-4 shadow-sm space-y-4">
          <h3 className="text-sm font-bold text-gray-800">Generar Orden de Trabajo de Servicio (OTS)</h3>
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Seleccionar PFS ({selectedPFs.length} seleccionados)</p>
            <div className="space-y-2">
              {availablePFs.map(pf => (
                <label key={pf._id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${selectedPFs.includes(pf._id) ? "border-[#2E6DA4] bg-[#2E6DA4]/5" : "border-gray-200 hover:border-gray-300"}`} data-testid="pf-checkbox-label">
                  <input type="checkbox" checked={selectedPFs.includes(pf._id)} onChange={() => togglePF(pf._id)} className="w-4 h-4 accent-[#2E6DA4]" data-testid={`pf-check-${pf._id}`} />
                  <div><p className="text-xs font-bold text-gray-800 font-mono">{pf.number}</p><p className="text-xs text-gray-500">{pf.provider_name} · {formatPYG(pf.total)}</p></div>
                </label>
              ))}
            </div>
          </div>
          {selectedPFs.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-semibold text-gray-700 mb-1">Condición de Pago</label>
                <select value={genForm.condicion_pago} onChange={e => setGenForm(f => ({...f, condicion_pago: e.target.value}))} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white">
                  {CONDICION_PAGO_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div><label className="block text-xs font-semibold text-gray-700 mb-1">Observaciones</label><input value={genForm.notes} onChange={e => setGenForm(f => ({...f, notes: e.target.value}))} placeholder="Notas de la OTS..." className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" /></div>
            </div>
          )}
          {selectedPFs.length > 0 && (
            <button onClick={handleGenOTS} disabled={saving} className="w-full bg-[#2E6DA4] hover:bg-[#245A8A] text-white py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all" data-testid="gen-ots-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <ShoppingCart size={15} />}{saving ? "Generando..." : "Generar OTS"}
            </button>
          )}
        </div>
      )}

      {loading ? <Skeleton className="h-32" /> : svcOrders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200/60 p-12 text-center shadow-sm">
          <ShoppingCart size={44} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-500 font-medium">No hay Órdenes de Servicio</p>
          <p className="text-xs text-gray-400 mt-1">Seleccione PFS aprobados para generar una OTS</p>
        </div>
      ) : (
        <div className="space-y-3">
          {svcOrders.map(ots => (
            <div key={ots._id} className="bg-white rounded-2xl border border-gray-200/60 p-4 shadow-sm hover:shadow-md transition-all" data-testid="ots-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-gray-900 text-sm font-mono">{ots.number}</span>
                    <StatusBadge status={ots.status} />
                  </div>
                  <p className="text-xs text-gray-600 mt-0.5">{ots.project_name} · <strong>{ots.provider_name}</strong></p>
                  <PSLinea ps={psPorId[ots.ps_id]} />
                  <p className="text-sm font-bold text-[#2E6DA4] mt-0.5" data-testid="ots-total-civa">{formatPYG(ots.total || 0)} <span className="text-[10px] text-gray-400 font-normal">c/IVA</span></p>
                  <div className="flex gap-2 mt-1 text-[10px]">
                    {ots.condicion_pago && <span className="bg-cyan-50 text-cyan-700 px-1.5 py-0.5 rounded border border-cyan-200">{ots.condicion_pago}</span>}
                  </div>
                  {ots.certified_amount && <p className="text-xs text-teal-600 font-semibold mt-0.5">Certificado: {formatPYG(montoCertificadoOTS(ots))} <span className="font-normal">c/IVA</span></p>}
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  <button onClick={() => setViewOTS(ots)} className="flex items-center gap-1.5 border border-gray-200 text-gray-600 hover:bg-gray-50 px-3 py-1.5 rounded-xl text-xs font-medium transition-all" data-testid="view-ots-btn"><Eye size={13} /> VER</button>
                  <button onClick={() => generateOTSPDF(ots)} className="flex items-center gap-1.5 border border-[#2E6DA4]/30 text-[#2E6DA4] hover:bg-[#2E6DA4]/5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all"><Download size={13} /> PDF</button>
                  {canApprove && ots.status === "pendiente_aprobacion" && (
                    <button onClick={() => handleApprove(ots._id)} disabled={approvingId === ots._id} className="flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 rounded-xl text-xs font-semibold transition-all disabled:opacity-60" data-testid="approve-ots-btn">
                      {approvingId === ots._id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}Aprobar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal: Ver OTS */}
      {viewOTS && (
        <Modal open={!!viewOTS} onClose={() => setViewOTS(null)} title={`Detalle OTS: ${viewOTS.number}`} wide>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {[["N° OTS", viewOTS.number], ["Estado", <StatusBadge key="s" status={viewOTS.status} />], ["Proyecto", viewOTS.project_name], ["Proveedor", viewOTS.provider_name], ["Condición Pago", viewOTS.condicion_pago || "contado"], ["Aprobado por", viewOTS.approved_by_name || "Auto"]].map(([k, v]) => (
                <div key={k} className="bg-gray-50 rounded-xl p-3"><p className="text-[10px] text-gray-400 uppercase font-semibold tracking-wide">{k}</p><div className="text-sm font-medium text-gray-900 mt-0.5">{v}</div></div>
              ))}
            </div>
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-xs"><thead className="bg-gray-50 border-b border-gray-200"><tr>{["#","Descripción","UM","Cant.","P.U.","Subtotal"].map(h => <th key={h} className={`text-left px-3 py-2.5 text-gray-600 font-semibold ${h === "P.U." || h === "Subtotal" ? "text-right" : ""}`}>{h}{h === "P.U." || h === "Subtotal" ? " c/IVA" : ""}</th>)}</tr></thead>
                <tbody>{agruparServicios(viewOTS.items).map((item, i) => (<tr key={i} className="border-b border-gray-100 last:border-0"><td className="px-3 py-2.5 text-gray-500">{i+1}</td><td className="px-3 py-2.5 text-gray-700">{item.item_name}{item.__lineas > 1 && <span className="block text-[10px] text-gray-400" data-testid={`ots-item-lineas-${i}`}>Total de {item.__lineas} ítems del presupuesto</span>}</td><td className="px-3 py-2.5 text-gray-500">{item.unit}</td><td className="px-3 py-2.5 text-gray-700">{item.quantity}</td><td className="px-3 py-2.5 text-right font-mono text-gray-600">{formatPYG(item.unit_price || 0)}</td><td className="px-3 py-2.5 text-right font-mono font-bold text-[#2E6DA4]">{formatPYG(item.subtotal || 0)}</td></tr>))}</tbody>
              </table>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 space-y-1 text-sm">
              <div className="flex justify-between font-bold text-base" data-testid="ots-detail-total"><span>TOTAL (IVA incluido)</span><span className="text-[#2E6DA4]">{formatPYG(viewOTS.total || 0)}</span></div>
            </div>
            <div className="flex gap-3 pt-2 border-t border-gray-100">
              <button onClick={() => generateOTSPDF(viewOTS)} className="flex items-center gap-2 bg-[#2E6DA4] text-white px-4 py-2.5 rounded-xl text-xs font-semibold"><Download size={13} /> PDF OTS</button>
              <button onClick={() => setViewOTS(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-xs font-medium">Cerrar</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ==================== TAB: CERTIFICACIÓN ====================

function CertificacionTab({ svcOrders, certifications, alquileres, loading, onRefresh, userRole, requests = [], filtros }) {
  const psPorId = Object.fromEntries(requests.map(r => [r._id, r])); // Fix CI (2026-09-11)
  const [certModal, setCertModal] = useState(null);
  const [certForm, setCertForm] = useState({ certified_amount: "", invoice_number: "", invoice_total: "", notes: "", certified_items: [] });
  const [manualOverride, setManualOverride] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewCRT, setViewCRT] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const canCreate = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra", "Ingeniero Residente", "Compras"]);

  // Fix FQ (2026-10-12): "se debe poder certificar un anticipo sin la necesidad de tener que cerrar
  // el alquiler con los días hasta ese momento, y con ello generar la OP (…) Posteriormente el sistema
  // debe tener en cuenta lo ya pagado" (Sergio, 12/10). El anticipo es una certificación por monto;
  // las certificaciones por días descuentan lo anticipado que todavía no se descontó.
  const [anticipoOTS, setAnticipoOTS] = useState(null);
  const [anticipoForm, setAnticipoForm] = useState({ monto: "", notes: "" });
  const anticipoDe = (ots) => {
    const crts = (certifications || []).filter(c => c.ots_id === ots?._id && !c.cancelled_at);
    const total = crts.filter(c => c.anticipo).reduce((a, c) => a + (Number(c.total) || 0), 0);
    const aplicado = crts.filter(c => !c.anticipo).reduce((a, c) => a + (Number(c.anticipo_descontado) || 0), 0);
    return { total, pendiente: Math.max(total - aplicado, 0) };
  };
  const handleAnticipo = async () => {
    const monto = Number(anticipoForm.monto) || 0;
    if (monto <= 0) { toast.error("Ingresá el monto del anticipo"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/purchases/service-certifications`, { ots_id: anticipoOTS._id, certified_amount: monto, anticipo: true, notes: anticipoForm.notes }, { withCredentials: true });
      toast.success("Anticipo certificado. Ya está en Tesorería para generar la Orden de Pago");
      setAnticipoOTS(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al certificar el anticipo"); }
    finally { setSaving(false); }
  };

  // Fix FV (2026-10-13): los filtros de Materiales. La certificación toma la obra de su OTS;
  // su estado: sin factura, con factura o anulada.
  const fl = useFiltrosServicios(filtros);
  const otsPorId = Object.fromEntries(svcOrders.map(o => [o._id, o]));
  const refOTS = (crt) => otsPorId[crt.ots_id];
  const estadoCRT = (crt) => crt.cancelled_at ? "anulada" : crtInvoices(crt).length ? "con_factura" : "sin_factura";
  const approvedOTS = fl.filtrar(svcOrders.filter(o => o.status === "aprobada"), { sinEstado: true });
  const crtsFiltradas = fl.filtrar(certifications || [], { ref: refOTS, estado: estadoCRT });
  const findAlq = (ots) => (alquileres || []).find(a => a.ots_id === ots._id);

  const handleCancelCRT = async (crt) => {
    if (!window.confirm(`¿Anular la certificación ${crt.number}? Esto libera el período certificado para poder rehacerla.`)) return;
    setCancelling(true);
    try {
      await axios.put(`${API}/purchases/service-certifications/${crt._id}/cancel`, {}, { withCredentials: true });
      toast.success("Certificación anulada correctamente");
      setViewCRT(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al anular"); }
    finally { setCancelling(false); }
  };

  const openCert = (ots) => {
    setManualOverride(false);
    if (ots.categoria === "alquiler") {
      const alq = findAlq(ots);
      const periodo = proponerPeriodo(alq);
      const items = agruparServicios(ots.items).map((item) => {   // Fix FQ (2026-10-12): una línea por servicio
        const dias = tiempoCertificable(item.unit, periodo.from, periodo.to, true, true); // Fix CI (2026-09-11)
        return { item_name: item.item_name, unit: item.unit, quantity: item.quantity, unit_price: item.unit_price, sabado_incluido: true, domingo_incluido: true, dias, subtotal: (item.unit_price || 0) * (item.quantity || 1) * dias };
      });
      setCertForm({ certified_amount: "", invoice_number: "", invoice_total: "", notes: "", certified_items: items, period_from: periodo.from, period_to: periodo.to });
    } else {
      setCertForm({ certified_amount: ots.total || "", invoice_number: "", invoice_total: "", notes: "", certified_items: (ots.items || []).map(item => ({ ...item, certified_qty: item.quantity, certified: true })) });   // Fix FK: el total de la OTS (IVA incluido)
    }
    setCertModal(ots);
  };

  const isAlquilerCert = certModal?.categoria === "alquiler";
  const certAlq = certModal ? findAlq(certModal) : null;
  const alqIniciado = certAlq?.estado_base && certAlq.estado_base !== "no_iniciado";
  const alqFinalizado = certAlq?.estado_base === "finalizado";
  const sinDiasPendientes = alqFinalizado && (certAlq?.dias_pendientes_certificar ?? 1) <= 0;
  const diasPeriodoSeleccionado = (isAlquilerCert && certForm.period_from && certForm.period_to) ? diasEfectivosClient(certForm.period_from, certForm.period_to, true, true) : null;
  const anticipoCert = anticipoDe(certModal);   // Fix FQ · Fix FV (2026-10-13): también en servicios

  // BLOQUE 60 — aviso NO bloqueante si el período elegido deja un hueco respecto
  // del último certificado (el bloqueo real por superposición lo hace el backend).
  const ultimoCertificadoHasta = (certAlq?.certified_periods || []).length
    ? certAlq.certified_periods.slice().sort((a, b) => (a.to > b.to ? 1 : -1)).slice(-1)[0].to
    : null;
  let gapHint = null;
  if (isAlquilerCert && ultimoCertificadoHasta && certForm.period_from) {
    const siguiente = parseDateOnly(ultimoCertificadoHasta);
    siguiente.setDate(siguiente.getDate() + 1);
    const desde = parseDateOnly(certForm.period_from);
    if (desde && desde > siguiente) {
      gapHint = `Hay días sin certificar entre el ${formatDate(ultimoCertificadoHasta)} y el ${formatDate(certForm.period_from)} — no quedarán incluidos en esta certificación.`;
    }
  }

  // Fix FX (2026-10-14): aviso NO bloqueante cuando el período llega más allá de la fecha real de fin.
  const extraHint = (isAlquilerCert && alqFinalizado && certForm.period_to && certAlq?.fecha_fin_real && certForm.period_to > certAlq.fecha_fin_real)
    ? `El período llega hasta el ${formatDate(certForm.period_to)}, después de la fecha real de fin del alquiler (${formatDate(certAlq.fecha_fin_real)}): se certifican como días adicionales.`
    : null;

  const updatePeriodo = (field, value) => {
    setCertForm(f => {
      const next = { ...f, [field]: value };
      const items = f.certified_items.map(it => {
        const dias = tiempoCertificable(it.unit, next.period_from, next.period_to, it.sabado_incluido, it.domingo_incluido); // Fix CI
        return { ...it, dias, subtotal: (it.unit_price || 0) * (it.quantity || 1) * dias };
      });
      return { ...next, certified_items: items };
    });
  };

  const toggleDiaFlag = (idx, flag) => {
    const items = certForm.certified_items.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, [flag]: !it[flag] };
      const dias = tiempoCertificable(next.unit, certForm.period_from, certForm.period_to, next.sabado_incluido, next.domingo_incluido); // Fix CI
      return { ...next, dias, subtotal: (next.unit_price || 0) * (next.quantity || 1) * dias };
    });
    setCertForm(f => ({ ...f, certified_items: items }));
  };

  const updateItemDias = (idx, val) => {
    const dias = Number(val) || 0;
    const items = certForm.certified_items.map((it, i) => i === idx ? { ...it, dias, subtotal: (it.unit_price || 0) * (it.quantity || 1) * dias } : it);
    setCertForm(f => ({ ...f, certified_items: items }));
  };

  const alquilerCalcTotal = (certForm.certified_items || []).reduce((s, it) => s + (it.subtotal || 0), 0);

  const handleCertify = async () => {
    if (!certModal) return;
    if (isAlquilerCert) {
      if (!alqIniciado) { toast.error("Iniciá el conteo del alquiler en Inventario antes de certificar"); return; }
      if (!certForm.period_from || !certForm.period_to) { toast.error("Indicá el período (Desde–Hasta) a certificar"); return; }
      if (manualOverride && !certForm.certified_amount) { toast.error("Ingresá el monto ajustado manualmente"); return; }
      if (manualOverride && !certForm.notes.trim()) { toast.error("Indicá el motivo del ajuste manual en Observaciones"); return; }
    } else if (!certForm.certified_amount) { toast.error("Ingrese el monto certificado"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/purchases/service-certifications`, {
        ots_id: certModal._id,
        certified_amount: isAlquilerCert ? Number(certForm.certified_amount || alquilerCalcTotal) : Number(certForm.certified_amount),
        invoice_number: certForm.invoice_number,
        invoice_total: certForm.invoice_total ? Number(certForm.invoice_total) : 0,
        notes: certForm.notes,
        certified_items: certForm.certified_items,
        manual_override: isAlquilerCert ? manualOverride : false,
        ...(isAlquilerCert ? { period_from: certForm.period_from, period_to: certForm.period_to } : {}),
      }, { withCredentials: true });
      toast.success("Certificación registrada correctamente");
      if (res.data?.gap_warning) toast.warning(res.data.gap_warning, { duration: 8000 });
      setCertModal(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al certificar"); }
    finally { setSaving(false); }
  };

  // Fix FK (2026-10-09): el monto certificado ya es el total con IVA incluido (no se le suma el 10 %).
  const certAmt = isAlquilerCert ? (manualOverride ? (Number(certForm.certified_amount) || 0) : alquilerCalcTotal) : (Number(certForm.certified_amount) || 0);
  const descuentoAnticipo = Math.min(anticipoCert.pendiente, certAmt);   // Fix FQ

  return (
    <div className="space-y-5">
      <BarraFiltrosServicios fl={fl} filtros={filtros} testIdPrefix="svc-certificacion" searchPlaceholder="Buscar por N° CRT, OTS, obra o proveedor..."
        statusOptions={fl.opciones(certifications || [], { ref: refOTS, estado: estadoCRT, etiquetas: { sin_factura: "Sin factura", con_factura: "Con factura", anulada: "Anulada" }, fijos: ["sin_factura", "con_factura", "anulada"] })} />
      <div className="bg-teal-50/70 rounded-xl border border-teal-100 p-4 text-xs text-teal-800">
        <p className="font-semibold mb-0.5">Certificación de Servicio (CRT)</p>
        <p>El monto certificado puede ser parcial (diferente al monto total de la OTS) y va con IVA incluido. El pago al contratista se realiza sobre el monto certificado. En alquileres, se certifica por PERÍODOS mientras el alquiler sigue en curso (mes a mes) — cada período genera su propia certificación y Orden de Pago; para anular una con error, abrí "Ver" en <strong>Certificaciones Registradas</strong> más abajo.</p>
      </div>

      {/* OTS aprobadas pendientes de certificar */}
      {approvedOTS.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">OTS Pendientes de Certificación ({approvedOTS.length})</h3>
          <div className="space-y-3">
            {approvedOTS.map(ots => {
              const alq = ots.categoria === "alquiler" ? findAlq(ots) : null;
              const noIniciado = ots.categoria === "alquiler" && (!alq || alq.estado_base === "no_iniciado");
              const sinPendientes = ots.categoria === "alquiler" && alq?.estado_base === "finalizado" && (alq?.dias_pendientes_certificar ?? 1) <= 0;
              const gated = noIniciado || sinPendientes;
              return (
                <div key={ots._id} className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm" data-testid="cert-ots-card">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900 text-sm font-mono">{ots.number}</span><StatusBadge status={ots.status} />
                        {ots.categoria === "alquiler" && <span className="text-[10px] font-bold bg-orange-50 text-orange-600 border border-orange-200 px-2 py-0.5 rounded-full uppercase flex items-center gap-1"><Home size={10} /> Alquiler</span>}
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">{ots.project_name} · <strong>{ots.provider_name}</strong></p>
                      <p className="text-sm font-bold text-[#2E6DA4] mt-0.5">{formatPYG(ots.total || 0)} <span className="text-[10px] text-gray-400 font-normal">c/IVA</span></p>
                      <p className="text-xs text-gray-400">{ots.condicion_pago || "contado"}</p>
                      <PSLinea ps={psPorId[ots.ps_id]} />
                      {anticipoDe(ots).total > 0 && (   /* Fix FQ · Fix FV: también en servicios */
                        <p className="text-[11px] text-violet-600 mt-1 font-semibold" data-testid={`cert-anticipo-resumen-${ots._id}`}>Anticipo certificado: {formatPYG(anticipoDe(ots).total)} · Pendiente de descontar: {formatPYG(anticipoDe(ots).pendiente)}</p>
                      )}
                      {canCreate && !sinPendientes && (   /* Fix FV (2026-10-13): "sumemos eso también a servicios" (Sergio, 13/10) */
                        <button onClick={() => { setAnticipoForm({ monto: "", notes: "" }); setAnticipoOTS(ots); }} className="mt-2 flex items-center gap-1.5 border border-violet-300 text-violet-700 hover:bg-violet-50 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all" data-testid="anticipo-btn">
                          <Banknote size={12} /> Certificar anticipo
                        </button>
                      )}
                      {ots.categoria === "alquiler" && !gated && (
                        <p className="text-[11px] text-violet-600 mt-1 font-semibold">Certificado: {alq?.dias_certificados_acumulados || 0}d · Pendiente: {alq?.dias_pendientes_certificar ?? "—"}d</p>
                      )}
                      {noIniciado && (
                        <p className="text-xs font-semibold text-amber-700 mt-1 flex items-center gap-1" data-testid={`cert-gated-${ots._id}`}>
                          <AlertTriangle size={12} /> Iniciá el conteo en{" "}
                          <Link to={`/inventario/alquileres?open=${alq?.number || ""}`} className="underline">Inventario &gt; Alquileres</Link> para poder certificar
                        </p>
                      )}
                      {sinPendientes && (
                        <p className="text-xs font-semibold text-emerald-700 mt-1 flex items-center gap-1" data-testid={`cert-completo-${ots._id}`}>
                          <CheckCircle2 size={12} /> Totalmente certificado ({alq?.dias_certificados_acumulados || 0} días)
                        </p>
                      )}
                    </div>
                    {canCreate && (   /* Fix FX (2026-10-14): "dado el caso que se haya necesitado más días del alquiler" (Sergio, 14/10): también con todo certificado */
                      <button onClick={() => !noIniciado && openCert(ots)} disabled={noIniciado} title={noIniciado ? "Iniciá el conteo del alquiler en Inventario primero" : ""} className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed" data-testid="certify-btn">
                        <Award size={13} /> {sinPendientes ? "Certificar días adicionales" : "Certificar"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* BLOQUE 58 PARTE C — Certificaciones Registradas, con detalle por ítem (alquiler) y
          botón Anular. Se mantiene AQUÍ (Compras > Servicios > Certificación) porque es el
          único punto de la app que un usuario real puede alcanzar — el módulo "Obra >
          Certificaciones" (ObraDetail.jsx) es para certificación de avance % al cliente,
          un flujo distinto sin relación con las certificaciones CRT de proveedores. */}
      {crtsFiltradas.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3 mt-6">Certificaciones Registradas ({crtsFiltradas.filter(c => !c.cancelled_at).length})</h3>
          <div className="space-y-2">
            {crtsFiltradas.slice(0, fl.activo ? 200 : 30).map(crt => (
              <div key={crt._id} className={`bg-white rounded-xl border p-3.5 shadow-sm flex items-center justify-between gap-3 ${crt.cancelled_at ? "border-red-200 opacity-60" : "border-gray-200/60"}`} data-testid="cert-card">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-900 text-sm font-mono">{crt.number}</span>
                    {crt.categoria === "alquiler" && <span className="text-[10px] font-bold bg-orange-50 text-orange-600 border border-orange-200 px-2 py-0.5 rounded-full uppercase flex items-center gap-1"><Home size={9} /> Alquiler</span>}
                    {crt.cancelled_at && <span className="text-[10px] font-bold bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 rounded-full">ANULADA</span>}
                  </div>
                  <p className="text-xs text-gray-600 mt-0.5">{crt.provider_name} · OTS: <span className="font-mono">{crt.ots_number}</span></p>
                  {crt.alquiler_period_from && <p className="text-[11px] text-orange-600 mt-0.5">Período: {formatDate(crt.alquiler_period_from)} – {formatDate(crt.alquiler_period_to)}</p>}
                  {crt.anticipo && <p className="text-[11px] text-violet-600 mt-0.5 font-semibold">{crt.categoria === "alquiler" ? "Anticipo de alquiler" : "Anticipo"}</p>}   {/* Fix FQ · Fix FV */}
                  {crt.anticipo_descontado > 0 && <p className="text-[11px] text-violet-600 mt-0.5">Descuenta anticipo: {formatPYG(crt.anticipo_descontado)} ({crt.categoria === "alquiler" ? "período" : "total"}: {formatPYG(crt.monto_periodo || 0)})</p>}
                  <p className="text-sm font-bold text-teal-700 mt-0.5">{formatPYG(crt.total || 0)}</p>
                  {!crt.cancelled_at && (   /* Fix FL */
                    <p className={`text-[11px] mt-0.5 ${crtInvoices(crt).length ? "text-emerald-600" : "text-amber-600"}`} data-testid={`cert-factura-${crt._id}`}>
                      {crtInvoices(crt).length ? `Factura: ${crtInvoices(crt).map(i => i.invoice_number).join(", ")}` : "Sin factura vinculada"}
                    </p>
                  )}
                </div>
                <button onClick={() => setViewCRT(crt)} className="flex items-center gap-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 px-3 py-1.5 rounded-lg text-xs font-semibold" data-testid={`cert-view-btn-${crt._id}`}>
                  <Eye size={12} /> Ver
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {approvedOTS.length === 0 && !loading && (
        <div className="bg-white rounded-2xl border border-gray-200/60 p-12 text-center shadow-sm">
          <Award size={44} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-500 font-medium">Sin OTS pendientes de certificación</p>
        </div>
      )}

      {/* Modal: Ver certificación / Anular */}
      <Modal open={!!viewCRT} onClose={() => setViewCRT(null)} title={`Certificación: ${viewCRT?.number}`} wide={viewCRT?.categoria === "alquiler"}>
        {viewCRT && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {[["OTS Ref.", viewCRT.ots_number], ["Proveedor", viewCRT.provider_name], ["Monto certificado (IVA incluido)", formatPYG(viewCRT.total || 0)], ["Factura", viewCRT.invoice_number || "—"]].map(([k, v]) => (   /* Fix FK */
                <div key={k} className="bg-gray-50 rounded-xl p-2.5">
                  <p className="text-[10px] text-gray-400 uppercase font-semibold">{k}</p>
                  <div className="text-xs font-medium text-gray-900 mt-0.5">{v}</div>
                </div>
              ))}
            </div>
            {viewCRT.cancelled_at && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700 font-semibold">
                Certificación ANULADA el {formatDate(viewCRT.cancelled_at)} por {viewCRT.cancelled_by || "—"}
              </div>
            )}
            {viewCRT.categoria === "alquiler" && (viewCRT.certified_items || []).length > 0 && (
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="text-xs w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 text-gray-600">
                      <th className="px-2 py-1.5 text-left">Ítem</th>
                      <th className="px-2 py-1.5 text-center">Cant.</th>
                      <th className="px-2 py-1.5 text-center">Días Ef.</th>
                      <th className="px-2 py-1.5 text-center">Sábado</th>
                      <th className="px-2 py-1.5 text-center">Domingo</th>
                      <th className="px-2 py-1.5 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {viewCRT.certified_items.map((it, i) => (
                      <tr key={i}>
                        <td className="px-2 py-1.5">{it.item_name}</td>
                        <td className="px-2 py-1.5 text-center">{it.quantity} {it.unit}</td>
                        <td className="px-2 py-1.5 text-center">{it.dias_efectivos}</td>
                        <td className="px-2 py-1.5 text-center">{it.sabado_incluido ? "Sí" : "No"}</td>
                        <td className="px-2 py-1.5 text-center">{it.domingo_incluido ? "Sí" : "No"}</td>
                        <td className="px-2 py-1.5 text-right font-semibold">{formatPYG(it.subtotal || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {viewCRT.alquiler_period_from && <p className="text-[11px] text-gray-400 mt-1.5 px-1">Período certificado: {formatDate(viewCRT.alquiler_period_from)} – {formatDate(viewCRT.alquiler_period_to)}</p>}
              </div>
            )}
            {viewCRT.anticipo && <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 text-xs text-violet-800 font-semibold">{viewCRT.categoria === "alquiler" ? "Anticipo de alquiler: se descuenta de las próximas certificaciones por días." : "Anticipo: se descuenta de la certificación del servicio."}</div>}   {/* Fix FQ */}
            {viewCRT.anticipo_descontado > 0 && <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 text-xs text-violet-800">{viewCRT.categoria === "alquiler" ? "Total del período" : "Total certificado"}: <strong>{formatPYG(viewCRT.monto_periodo || 0)}</strong> · Anticipo descontado: <strong>- {formatPYG(viewCRT.anticipo_descontado)}</strong> · A pagar: <strong>{formatPYG(viewCRT.total || 0)}</strong></div>}
            {viewCRT.notes && <div className="bg-gray-50 rounded-xl p-3 text-xs"><p className="text-[10px] text-gray-400 uppercase font-semibold mb-1">Observaciones</p>{viewCRT.notes}</div>}
            <div className="flex gap-2 pt-2">
              {canCreate && !viewCRT.cancelled_at && (
                <button onClick={() => handleCancelCRT(viewCRT)} disabled={cancelling} className="flex items-center gap-2 border border-red-300 text-red-600 hover:bg-red-50 px-4 py-2 rounded-xl text-xs font-semibold disabled:opacity-60" data-testid="cert-cancel-btn">
                  {cancelling ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Anular
                </button>
              )}
              <button onClick={() => setViewCRT(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2 text-xs font-medium">Cerrar</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Fix FQ (2026-10-12): Modal: Anticipo de alquiler */}
      <Modal open={!!anticipoOTS} onClose={() => setAnticipoOTS(null)} title={`${anticipoOTS?.categoria === "alquiler" ? "Anticipo de alquiler" : "Anticipo"}: ${anticipoOTS?.number || ""}`}>
        {anticipoOTS && (
          <div className="space-y-4" data-testid="anticipo-modal">
            <div className="bg-violet-50 rounded-xl p-3 border border-violet-100 text-xs text-violet-800 space-y-1">
              <p className="font-semibold">OTS: {anticipoOTS.number} · {anticipoOTS.provider_name}</p>
              <p>Total OTS: <strong>{formatPYG(anticipoOTS.total || 0)}</strong> · Anticipos ya certificados: <strong>{formatPYG(anticipoDe(anticipoOTS).total)}</strong></p>
              {anticipoOTS.categoria === "alquiler" ? (
                <p>No hace falta cerrar el alquiler ni indicar días: queda listo en Tesorería para generar la Orden de Pago, y cuando se certifiquen los días el sistema descuenta lo ya anticipado.</p>
              ) : (   /* Fix FV (2026-10-13) */
                <p>Queda listo en Tesorería para generar la Orden de Pago, y cuando se certifique el servicio el sistema descuenta lo ya anticipado.</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Monto del anticipo (Gs., IVA incluido) *</label>
              <input type="number" min="0" value={anticipoForm.monto} onChange={e => setAnticipoForm(f => ({ ...f, monto: e.target.value }))} placeholder="0" className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="anticipo-monto-input" />
            </div>
            <div><label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label><textarea value={anticipoForm.notes} onChange={e => setAnticipoForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none" /></div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setAnticipoOTS(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
              <button onClick={handleAnticipo} disabled={saving || !anticipoForm.monto} className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all" data-testid="confirm-anticipo-btn">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Banknote size={15} />}{saving ? "Registrando..." : "Registrar anticipo"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal: Certificar */}
      <Modal open={!!certModal} onClose={() => setCertModal(null)} title={`Certificar OTS: ${certModal?.number}`} wide={!isAlquilerCert} extraWide={isAlquilerCert}>
        {!isAlquilerCert ? (
          <div className="space-y-4">
            <div className="bg-teal-50 rounded-xl p-3 border border-teal-100 text-xs text-teal-800">
              <p className="font-semibold">OTS: {certModal?.number} · {certModal?.provider_name}</p>
              <p className="mt-0.5">Total OTS: <strong>{formatPYG(certModal?.total || 0)}</strong> · El monto certificado puede ser diferente al total.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Monto Certificado (Gs., IVA incluido) *</label>
              <input type="number" min="0" value={certForm.certified_amount} onChange={e => setCertForm(f => ({...f, certified_amount: e.target.value}))} placeholder="0" className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="cert-amount-input" />
              {certAmt > 0 && (
                <div className="mt-2 bg-teal-50 rounded-xl p-3 text-xs">
                  <div className="flex justify-between font-bold" data-testid="cert-total-civa"><span>Total certificado (IVA incluido):</span><span className="text-teal-700">{formatPYG(certAmt)}</span></div>
                </div>
              )}
              {descuentoAnticipo > 0 && (   /* Fix FV (2026-10-13): anticipo del servicio */
                <div className="mt-2 bg-violet-50 border border-violet-200 rounded-xl p-3 text-xs text-violet-800 space-y-1" data-testid="cert-anticipo-descuento">
                  <div className="flex justify-between"><span>Anticipo ya certificado, a descontar:</span><span className="font-semibold">- {formatPYG(descuentoAnticipo)}</span></div>
                  <div className="flex justify-between font-bold"><span>A pagar con esta certificación:</span><span>{formatPYG(certAmt - descuentoAnticipo)}</span></div>
                </div>
              )}
            </div>
            <p className="text-[11px] text-gray-500">La factura del proveedor se vincula después, en la pestaña <strong>Facturas</strong>.</p>   {/* Fix FL */}
            <div><label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label><textarea value={certForm.notes} onChange={e => setCertForm(f => ({...f, notes: e.target.value}))} rows={2} placeholder="Descripción del trabajo certificado..." className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none" /></div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setCertModal(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
              <button onClick={handleCertify} disabled={saving || !certForm.certified_amount} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all" data-testid="confirm-cert-btn">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Award size={15} />}{saving ? "Registrando..." : "Registrar Certificación"}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="cert-alquiler-modal">
            <div className="bg-orange-50 rounded-xl p-3 border border-orange-100 text-xs text-orange-800 space-y-1">
              <p className="font-semibold flex items-center gap-1.5"><Home size={13} /> Alquiler {certAlq?.number} · {certModal?.provider_name}</p>
              <p>Alquiler {alqFinalizado ? "finalizado" : "en curso"} desde <strong>{formatDate(certAlq?.fecha_inicio)}</strong>{alqFinalizado ? <> hasta <strong>{formatDate(certAlq?.fecha_fin_real)}</strong></> : null} · Certificado: <strong>{certAlq?.dias_certificados_acumulados || 0}d</strong> · Pendiente: <strong>{certAlq?.dias_pendientes_certificar ?? "—"}d</strong></p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Período a certificar — Desde *</label>
                <input type="date" value={certForm.period_from || ""} onChange={e => updatePeriodo("period_from", e.target.value)} className="w-full border border-orange-300 rounded-xl px-3 py-2.5 text-sm" data-testid="cert-period-from-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Período a certificar — Hasta *</label>
                <input type="date" value={certForm.period_to || ""} onChange={e => updatePeriodo("period_to", e.target.value)} className="w-full border border-orange-300 rounded-xl px-3 py-2.5 text-sm" data-testid="cert-period-to-input" />
              </div>
            </div>
            <p className="text-xs text-gray-500">Días del período seleccionado (calendario, ambos extremos inclusive): <strong>{diasPeriodoSeleccionado ?? "—"} días</strong> — los "días efectivos" de cada ítem pueden diferir si se desmarca "Sábado" y/o "Domingo".</p>
            {gapHint && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 text-xs text-amber-800 flex items-start gap-2" data-testid="cert-gap-hint">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {gapHint}
              </div>
            )}
            {extraHint && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 text-xs text-amber-800 flex items-start gap-2" data-testid="cert-extra-hint">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {extraHint}
              </div>
            )}

            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="text-xs w-full border-collapse">
                <thead>
                  <tr className="bg-[#1E3A5F] text-white">
                    <th className="px-2 py-2 text-center w-10">N°</th>
                    <th className="px-2 py-2 text-left">Descripción</th>
                    <th className="px-2 py-2 text-center w-16">Cant.</th>
                    <th className="px-2 py-2 text-center w-20">Tiempo</th>
                    <th className="px-2 py-2 text-center w-16">Sábado</th>
                    <th className="px-2 py-2 text-center w-16">Domingo</th>
                    <th className="px-2 py-2 text-right w-28">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(certForm.certified_items || []).map((it, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50/60"} data-testid={`cert-item-row-${idx}`}>
                      <td className="px-2 py-2 text-center text-gray-500">{idx + 1}</td>
                      <td className="px-2 py-2 font-medium text-gray-800">{it.item_name}</td>
                      <td className="px-2 py-2 text-center">{it.quantity} {it.unit}</td>
                      <td className="px-1 py-1">
                        <input type="number" min="0" value={it.dias} onChange={e => updateItemDias(idx, e.target.value)} className="w-full border border-orange-200 bg-orange-50/40 rounded-lg px-2 py-1.5 text-right text-xs focus:ring-2 focus:ring-orange-200 focus:outline-none" data-testid={`cert-item-dias-${idx}`} />
                        <p className="text-[9px] text-orange-500 text-center mt-0.5">{tiempoUnidad(it.unit)}</p>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <input type="checkbox" checked={!!it.sabado_incluido} onChange={() => toggleDiaFlag(idx, "sabado_incluido")} className="w-4 h-4 accent-orange-600 cursor-pointer" data-testid={`cert-item-sabado-${idx}`} />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <input type="checkbox" checked={!!it.domingo_incluido} onChange={() => toggleDiaFlag(idx, "domingo_incluido")} className="w-4 h-4 accent-orange-600 cursor-pointer" data-testid={`cert-item-domingo-${idx}`} />
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-gray-800">{formatPYG(it.subtotal || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center gap-2">
              <input type="checkbox" checked={manualOverride} onChange={e => setManualOverride(e.target.checked)} className="w-4 h-4 accent-orange-600" data-testid="cert-manual-override-checkbox" />
              <label className="text-xs font-semibold text-gray-700">Ajustar el monto certificado manualmente (excepcional, quedará registrado en Observaciones)</label>
            </div>
            {manualOverride && (
              <input type="number" min="0" value={certForm.certified_amount} onChange={e => setCertForm(f => ({...f, certified_amount: e.target.value}))} placeholder={`Sugerido: ${Math.round(alquilerCalcTotal)}`} className="w-full border border-amber-300 bg-amber-50 rounded-xl px-3 py-2.5 text-sm" data-testid="cert-manual-amount-input" />
            )}

            <div className="bg-teal-50 rounded-xl p-3 text-xs">
              <div className="flex justify-between font-bold" data-testid="cert-total-civa"><span>Total certificado{manualOverride ? " (manual)" : " (calculado del detalle)"}, IVA incluido:</span><span className="text-teal-700">{formatPYG(certAmt)}</span></div>
            </div>
            {descuentoAnticipo > 0 && (   /* Fix FQ */
              <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 text-xs text-violet-800 space-y-1" data-testid="cert-anticipo-descuento">
                <div className="flex justify-between"><span>Anticipo ya certificado, a descontar:</span><span className="font-semibold">- {formatPYG(descuentoAnticipo)}</span></div>
                <div className="flex justify-between font-bold"><span>A pagar con esta certificación:</span><span>{formatPYG(certAmt - descuentoAnticipo)}</span></div>
              </div>
            )}

            <p className="text-[11px] text-gray-500">La factura del proveedor se vincula después, en la pestaña <strong>Facturas</strong>.</p>   {/* Fix FL */}
            <div><label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones {manualOverride && <span className="text-amber-600">(indicar motivo del ajuste manual)</span>}</label><textarea value={certForm.notes} onChange={e => setCertForm(f => ({...f, notes: e.target.value}))} rows={2} placeholder="Descripción del trabajo certificado..." className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none" /></div>

            <div className="flex gap-3 pt-2">
              <button onClick={() => setCertModal(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
              <button onClick={handleCertify} disabled={saving} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all" data-testid="confirm-cert-btn">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Award size={15} />}{saving ? "Registrando..." : "Registrar Certificación"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ==================== TAB: PROVEEDORES ====================

function ProveedoresTab({ providers, loading, onRefresh, userRole }) {
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", ruc: "", contact: "", email: "", phone: "", address: "", category: "Servicios", payment_terms: "" });
  const [saving, setSaving] = useState(false);
  const canCreate = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Compras"]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.ruc) { toast.error("Nombre y RUC son obligatorios"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/purchases/providers`, form, { withCredentials: true });
      toast.success("Proveedor registrado");
      setShowNew(false);
      setForm({ name: "", ruc: "", contact: "", email: "", phone: "", address: "", category: "Servicios", payment_terms: "" });
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{providers.length} proveedor(es) / contratista(s)</p>
        {canCreate && (
          <button onClick={() => setShowNew(true)} className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all" data-testid="new-provider-svc-btn">
            <Plus size={15} /> Nuevo Proveedor
          </button>
        )}
      </div>
      {loading ? <Skeleton className="h-32" /> : providers.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200/60 p-12 text-center shadow-sm"><Building2 size={44} className="mx-auto text-gray-200 mb-3" /><p className="text-gray-500 font-medium">No hay proveedores registrados</p></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {providers.map(p => (
            <div key={p._id} className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm" data-testid="provider-svc-card">
              <p className="font-bold text-gray-900 text-sm">{p.name}</p>
              <p className="text-xs text-gray-500 mt-0.5">RUC: {p.ruc}</p>
              {p.contact && <p className="text-xs text-gray-500">{p.contact}</p>}
              {p.email && <p className="text-xs text-[#2E6DA4]">{p.email}</p>}
              {p.category && <span className="text-[10px] bg-orange-50 text-orange-700 border border-orange-200 px-1.5 py-0.5 rounded font-semibold mt-1 inline-block">{p.category}</span>}
            </div>
          ))}
        </div>
      )}
      <Modal open={showNew} onClose={() => setShowNew(false)} title="Nuevo Proveedor / Contratista">
        <form onSubmit={handleSave} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="block text-xs font-semibold text-gray-700 mb-1.5">Nombre / Empresa *</label><input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" required data-testid="provider-name-input" /></div>
            <div><label className="block text-xs font-semibold text-gray-700 mb-1.5">RUC *</label><input value={form.ruc} onChange={e => setForm(f => ({...f, ruc: e.target.value}))} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" required /></div>
            <div><label className="block text-xs font-semibold text-gray-700 mb-1.5">Contacto</label><input value={form.contact} onChange={e => setForm(f => ({...f, contact: e.target.value}))} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" /></div>
            <div><label className="block text-xs font-semibold text-gray-700 mb-1.5">Email</label><input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" /></div>
            <div><label className="block text-xs font-semibold text-gray-700 mb-1.5">Teléfono</label><input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" /></div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowNew(false)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="save-provider-svc-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}{saving ? "Guardando..." : "Registrar"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

// ==================== MAIN COMPONENT ====================

export default function ComprasServicios() {
  const { user } = useAuth();
  const userRole = user?.role || "";
  // BLOQUE 34 — deep-link desde el Dashboard (cuadro "Alquileres"): ?open=<numero PS>
  // abre directamente la pestaña Pedidos de Serv. con el detalle abierto.
  const [searchParams] = useSearchParams();
  const openNumber = searchParams.get("open") || "";
  const [activeTab, setActiveTab] = useState("pedidos");
  const [loading, setLoading] = useState(true);

  // Data state
  const [projects, setProjects] = useState([]);
  const [providers, setProviders] = useState([]);
  const [requests, setRequests] = useState([]);
  const [supplierOrders, setSupplierOrders] = useState([]);
  const [budgetActs, setBudgetActs] = useState([]);
  const [finalBudgets, setFinalBudgets] = useState([]);
  const [svcOrders, setSvcOrders] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [alquileres, setAlquileres] = useState([]);

  // BLOQUE 35 PARTE A — catálogos + estado del filtro Cliente->Obra->Presupuesto
  // (mismo patrón compartido del Bloque 27 en Materiales, ahora también en Servicios).
  const [listasClientes, setListasClientes] = useState([]);
  const [listasObras, setListasObras] = useState([]);
  const [allBudgets, setAllBudgets] = useState([]);
  const [filterCliente, setFilterCliente] = useState("");
  const [filterObra, setFilterObra] = useState("");
  const [filterPresupuesto, setFilterPresupuesto] = useState("");
  const filterProps = { filterCliente, setFilterCliente, filterObra, setFilterObra, filterPresupuesto, setFilterPresupuesto };
  const refreshBudgets = useCallback(() => {
    axios.get(`${API}/budgets`, { withCredentials: true }).then(r => setAllBudgets(r.data || [])).catch(() => {});
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const [pRes, provRes, reqRes, soRes, baRes, fbRes, oRes, cRes, clientesRes, obrasRes, budgetsRes, alqRes] = await Promise.all([
        axios.get(`${API}/projects`, { withCredentials: true }),
        axios.get(`${API}/purchases/providers`, { withCredentials: true }),
        axios.get(`${API}/purchases/service-requests`, { withCredentials: true }),
        axios.get(`${API}/purchases/service-supplier-orders`, { withCredentials: true }),
        axios.get(`${API}/purchases/service-budget-acts`, { withCredentials: true }),
        axios.get(`${API}/purchases/service-final-budgets`, { withCredentials: true }),
        axios.get(`${API}/purchases/service-orders`, { withCredentials: true }),
        axios.get(`${API}/purchases/service-certifications`, { withCredentials: true }),
        axios.get(`${API}/listas/clientes`, { withCredentials: true }),
        axios.get(`${API}/listas/obras`, { withCredentials: true }),
        axios.get(`${API}/budgets`, { withCredentials: true }),
        axios.get(`${API}/alquileres`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setProjects(pRes.data);
      setProviders(provRes.data);
      setRequests(reqRes.data);
      setSupplierOrders(soRes.data);
      setBudgetActs(baRes.data);
      setFinalBudgets(fbRes.data);
      setSvcOrders(oRes.data);
      setCertifications(cRes.data);
      setListasClientes(clientesRes.data || []);
      setListasObras(obrasRes.data || []);
      setAllBudgets(budgetsRes.data || []);
      setAlquileres(alqRes.data || []);
    } catch (err) {
      toast.error("Error al cargar datos de Compras Servicios");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Fix FV (2026-10-13): "más parecido en cómo se visualiza a la parte de compras de materiales"
  // (Sergio, 13/10). Como en Materiales: cada pestaña muestra lo que tiene pendiente, su urgencia
  // (C crítica · B alta · A normal) y los nuevos de hoy; sin tarjetas de totales ni recuadro.
  const pendientesPorPestana = {
    pedidos: requests.filter(r => ["pendiente", "modificar"].includes(r.status)),
    aprobaciones: requests.filter(r => ["pendiente", "modificar"].includes(r.status)),
    ped_prov: requests.filter(r => r.status === "aprobada" && !supplierOrders.some(o => o.ps_id === r._id)),
    analisis: [...supplierOrders.filter(p => !budgetActs.some(a => a.pps_id === p._id)), ...budgetActs.filter(a => a.status === "abierta")],
    ptos_finales: budgetActs.filter(a => a.status === "cerrada" && !finalBudgets.some(p => p.aps_id === a._id)),
    ordenes: [...finalBudgets.filter(p => p.status === "borrador"), ...svcOrders.filter(o => o.status === "pendiente_aprobacion")],
    certificacion: svcOrders.filter(o => o.status === "aprobada"),
    facturas: certifications.filter(c => !c.cancelled_at && !crtCoverage(c).complete),
  };
  const urgencias = (lista) => {
    const out = { crit: 0, alta: 0, normal: 0 };
    lista.forEach(d => { const u = (d?.urgency || "normal").toLowerCase(); if (u === "critica" || u === "crítica") out.crit++; else if (u === "alta") out.alta++; else out.normal++; });
    return out;
  };
  const esHoy = (f) => { if (!f) return false; const d = new Date(f), t = new Date(); return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate(); };
  const filtros = { listasClientes, listasObras, allBudgets, ...filterProps };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="compras-servicios-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Compras de Servicios/Alquileres</h1>
        <p className="text-sm text-gray-500 mt-0.5">Flujo completo: pedido → aprobación → PPS → análisis → PFS → OTS → certificación → factura</p>
      </div>

      {/* Tabs (como en Compras de Materiales) */}
      <div className="flex flex-wrap gap-0.5 pb-0 pt-1.5 lg:pt-0 border-b border-gray-200" data-testid="servicios-tabs">
        {TABS.map(({ id, label, icon: Icon }) => {
          const pend = pendientesPorPestana[id];
          const badge = pend ? pend.length : 0;
          const bd = pend ? urgencias(pend) : null;
          const newToday = pend ? pend.filter(d => esHoy(d?.created_at)).length : 0;
          const tooltipParts = [];
          if (bd) tooltipParts.push(`Pendientes — Crítica: ${bd.crit} · Alta: ${bd.alta} · Normal: ${bd.normal}`);
          if (newToday > 0) tooltipParts.push(`+ ${newToday} nuevos hoy`);
          return (
            <button key={id} onClick={() => setActiveTab(id)} data-testid={`tab-${id}`}
              title={tooltipParts.length ? tooltipParts.join(" · ") : undefined}
              className={`flex items-center gap-1 px-2 py-2.5 text-xs font-semibold rounded-t-lg transition-all whitespace-nowrap relative ${
                activeTab === id
                  ? "text-[#2E6DA4] border-b-2 border-[#2E6DA4] bg-blue-50/60"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50/60"
              }`}>
              <Icon size={13} />
              <span>{label}</span>
              {badge > 0 && (
                <span data-testid={`badge-${id}`} className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-amber-400 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {badge}
                </span>
              )}
              {bd && (bd.crit > 0 || bd.alta > 0 || bd.normal > 0) && (
                <span data-testid={`urg-breakdown-${id}`} className="hidden md:flex items-center gap-px ml-0.5 text-[9px] font-bold">
                  {bd.crit > 0 && <span className="bg-red-100 text-red-700 rounded px-0.5 leading-tight" title="Crítica">{bd.crit}C</span>}
                  {bd.alta > 0 && <span className="bg-amber-100 text-amber-700 rounded px-0.5 leading-tight" title="Alta">{bd.alta}B</span>}
                  {bd.normal > 0 && <span className="bg-blue-100 text-blue-700 rounded px-0.5 leading-tight" title="Normal">{bd.normal}A</span>}
                </span>
              )}
              {newToday > 0 && (
                <span data-testid={`new-today-${id}`} className="hidden md:flex items-center ml-px text-[9px] font-bold bg-emerald-100 text-emerald-700 rounded px-0.5 leading-tight" title={`+ ${newToday} nuevos hoy`}>
                  +{newToday}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="min-h-64">
        {activeTab === "listas"        && <ListasTab />}
        {activeTab === "pedidos"       && <PedidosTab requests={requests} projects={projects} loading={loading} onRefresh={fetchAll} userRole={userRole} openNumber={openNumber} listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets} refreshBudgets={refreshBudgets} filtros={filtros} />}
        {activeTab === "aprobaciones"  && <AprobacionesTab requests={requests} loading={loading} onRefresh={fetchAll} userRole={userRole} filtros={filtros} />}
        {activeTab === "ped_prov"      && <PedProvTab requests={requests} supplierOrders={supplierOrders} loading={loading} onRefresh={fetchAll} userRole={userRole} filtros={filtros} />}
        {activeTab === "analisis"      && <AnalisisTab supplierOrders={supplierOrders} budgetActs={budgetActs} loading={loading} onRefresh={fetchAll} userRole={userRole} requests={requests} filtros={filtros} />}
        {activeTab === "ptos_finales"  && <PtosFinalTab budgetActs={budgetActs} finalBudgets={finalBudgets} loading={loading} onRefresh={fetchAll} userRole={userRole} requests={requests} filtros={filtros} />}
        {activeTab === "ordenes"       && <OrdenesTab finalBudgets={finalBudgets} svcOrders={svcOrders} loading={loading} onRefresh={fetchAll} userRole={userRole} requests={requests} filtros={filtros} />}
        {activeTab === "certificacion" && <CertificacionTab svcOrders={svcOrders} certifications={certifications} alquileres={alquileres} loading={loading} onRefresh={fetchAll} userRole={userRole} requests={requests} filtros={filtros} />}
        {activeTab === "facturas"      && <ServiciosFacturasTab certifications={certifications} loading={loading} onRefresh={fetchAll} userRole={userRole} svcOrders={svcOrders} filtros={filtros} />}
        {activeTab === "proveedores"   && <ProveedoresTab providers={providers} loading={loading} onRefresh={fetchAll} userRole={userRole} />}
        {activeTab === "rangos"        && <RangosAprobacionTab servicios />}   {/* Fix HB (2026-10-20) */}
      </div>
    </div>
  );
}
